// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Tabs,
    Statistic,
    Space,
    Select,
    DatePicker,
    Typography,
    Table,
    Tag,
    Tooltip,
    Button,
    Radio,
    Alert,
    Badge,
    Grid,
    Divider,
    theme,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Upload,
    Popconfirm,
    Empty,
    Progress,
    Switch,
    AutoComplete,
    Dropdown,
} from 'antd';
import {
    WalletOutlined,
    RiseOutlined,
    FallOutlined,
    LineChartOutlined,
    DollarOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    WarningOutlined,
    InfoCircleOutlined,
    ShoppingOutlined,
    FileTextOutlined,
    FilterOutlined,
    RightOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    UploadOutlined,
    FolderOpenOutlined,
    InboxOutlined,
    EyeOutlined,
    DownloadOutlined,
    PaperClipOutlined,
    FilePdfOutlined,
    FileImageOutlined,
    FileExcelOutlined,
    FileZipOutlined,
    SearchOutlined,
    SettingOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as ChartTooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    financialEntriesApi,
    crmApi,
    productsApi,
    channelsApi,
    filesApi,
    ordersApi,
    adminApi,
    financeApi,
    integrationsApi,
} from '../../api/client';
import { exportToCSV, exportToXLSX } from '../../utils/exportUtils';

const { RangePicker } = DatePicker;
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const renderCategory = (category: any) => {
    if (!category) return '';
    if (Array.isArray(category)) return category[0];
    if (typeof category === 'string') {
        if (category.startsWith('[') || category.startsWith('{')) {
            try {
                const parsed = JSON.parse(category);
                if (Array.isArray(parsed)) return parsed[0];
                return parsed;
            } catch (e) {}
        }
        return category;
    }
    return String(category);
};

const getMonday = (date: any) => {
    const d = dayjs(date);
    const day = d.day();
    const diff = day === 0 ? -6 : 1 - day;
    return d.add(diff, 'day').startOf('day');
};

export default function FinancialHubPage() {
    const { token } = theme.useToken();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const queryClient = useQueryClient();

    // State parameters
    const [reportMode, setReportMode] = useState<'accrual' | 'cash'>('accrual');
    const [period, setPeriod] = useState('thisMonth');
    const [customRange, setCustomRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedForecastWeek, setSelectedForecastWeek] = useState<number | null>(1);

    // Filter states for Transactions tab
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<string | undefined>(undefined);
    const [filterClass, setFilterClass] = useState<string | undefined>(undefined);
    const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
    const [filterCategory, setFilterCategory] = useState<string | undefined>(undefined);

    // Filter states for Zobowiązania (Payables) tab
    const [pSearch, setPSearch] = useState('');
    const [pContractor, setPContractor] = useState<string | undefined>(undefined);
    const [pProduct, setPProduct] = useState<string | undefined>(undefined);
    const [pChannel, setPChannel] = useState<string | undefined>(undefined);
    const [pStatus, setPStatus] = useState<string | undefined>(undefined);
    const [pDateRange, setPDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

    // Filter states for Należności (Receivables) tab
    const [rSearch, setRSearch] = useState('');
    const [rContractor, setRContractor] = useState<string | undefined>(undefined);
    const [rProduct, setRProduct] = useState<string | undefined>(undefined);
    const [rChannel, setRChannel] = useState<string | undefined>(undefined);
    const [rStatus, setRStatus] = useState<string | undefined>(undefined);
    const [rDateRange, setRDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

    // Filter states for Documents tab
    const [docSearchQuery, setDocSearchQuery] = useState('');
    const [docFileTypeFilter, setDocFileTypeFilter] = useState<string | undefined>(undefined);
    const [docEntryTypeFilter, setDocEntryTypeFilter] = useState<string | undefined>(undefined);
    const [docDateRangeFilter, setDocDateRangeFilter] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

    // Form/Modal state (Central Modals)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

    // Payables sub-tab selection (active obligations, upcoming planned recurring costs, templates)
    const [payablesSubTab, setPayablesSubTab] = useState<'active' | 'upcoming' | 'templates'>('active');
    
    // Recurring Templates state
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
    const [templateForm] = Form.useForm();
    const [form] = Form.useForm();
    const [paymentForm] = Form.useForm();
    const [liquidityForm] = Form.useForm();

    // Details Modal state
    const [selectedEntry, setSelectedEntry] = useState<any | null>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    const [selectedMonthDetails, setSelectedMonthDetails] = useState<any | null>(null);
    const [isMonthModalOpen, setIsMonthModalOpen] = useState(false);

    // States for Plan vs Execution monthly analysis
    const [monthsViewMode, setMonthsViewMode] = useState<'overview' | 'planVsExecution'>('overview');
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string | null>(null);
    const [selectedMonthForDrillDown, setSelectedMonthForDrillDown] = useState<any | null>(null);
    const [isDrillDownModalOpen, setIsDrillDownModalOpen] = useState(false);
    const [modalCategoryFilter, setModalCategoryFilter] = useState<string | null>(null);

    // Liquidity operations modal (Saldo otwarcia / Korekta salda)
    const [isLiquidityModalOpen, setIsLiquidityModalOpen] = useState(false);
    const [liquidityModalType, setLiquidityModalType] = useState<'OPENING_BALANCE' | 'BALANCE_CORRECTION'>('OPENING_BALANCE');

    // Auto-calculate logic variables
    const [amountNet, setAmountNet] = useState<number | null>(null);
    const [amountGross, setAmountGross] = useState<number | null>(null);
    const [amountVat, setAmountVat] = useState<number | null>(null);
    const [lastChanged, setLastChanged] = useState<('net' | 'gross' | 'vat')[]>([]);
    const [vatRate, setVatRate] = useState<number | null>(23);
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

    // Map time preset to actual date strings
    const getDateRange = () => {
        if (period === 'custom' && customRange) {
            return {
                startDate: customRange[0].format('YYYY-MM-DD'),
                endDate: customRange[1].format('YYYY-MM-DD'),
            };
        }
        const now = dayjs();
        let start = now.startOf('month');
        let end = now.endOf('month');

        if (period === '7d') {
            start = now.subtract(7, 'day');
            end = now;
        } else if (period === '30d') {
            start = now.subtract(30, 'day');
            end = now;
        } else if (period === 'lastMonth') {
            start = now.subtract(1, 'month').startOf('month');
            end = now.subtract(1, 'month').endOf('month');
        } else if (period === 'thisQuarter') {
            start = now.startOf('quarter');
            end = now.endOf('quarter');
        }
        return {
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD'),
        };
    };

    const { startDate, endDate } = getDateRange();

    // --- Queries ---
    const { data: summary, refetch: refetchSummary } = useQuery({
        queryKey: ['financial-summary', startDate, endDate],
        queryFn: async () => {
            const res = await financialEntriesApi.getSummary({ startDate, endDate });
            return res.data;
        },
    });

    const { data: entriesData, refetch: refetchEntries } = useQuery({
        queryKey: ['financial-entries', startDate, endDate, filterType, filterClass, filterStatus, filterCategory, searchQuery],
        queryFn: async () => {
            const res = await financialEntriesApi.getAll({
                startDate,
                endDate,
                type: filterType,
                classification: filterClass,
                status: filterStatus,
                category: filterCategory,
                search: searchQuery || undefined,
                limit: 200,
            });
            return res.data;
        },
    });

    // Payables Query
    const { data: payablesData, refetch: refetchPayables } = useQuery({
        queryKey: ['financial-entries-payables', pSearch, pContractor, pProduct, pChannel, pStatus, pDateRange],
        queryFn: async () => {
            const res = await financialEntriesApi.getAll({
                type: 'EXPENSE',
                search: pSearch || undefined,
                contractorId: pContractor,
                productId: pProduct,
                channelId: pChannel,
                status: pStatus === 'ALL' ? undefined : pStatus,
                startDate: pDateRange ? pDateRange[0].format('YYYY-MM-DD') : undefined,
                endDate: pDateRange ? pDateRange[1].format('YYYY-MM-DD') : undefined,
                limit: 200,
            });
            let items = res.data?.items || [];
            if (!pStatus) {
                // Default to non-completed
                items = items.filter((e: any) => e.status !== 'COMPLETED');
            }
            return items;
        }
    });

    // Receivables Query
    const { data: receivablesData, refetch: refetchReceivables } = useQuery({
        queryKey: ['financial-entries-receivables', rSearch, rContractor, rProduct, rChannel, rStatus, rDateRange],
        queryFn: async () => {
            const res = await financialEntriesApi.getAll({
                type: 'REVENUE',
                search: rSearch || undefined,
                contractorId: rContractor,
                productId: rProduct,
                channelId: rChannel,
                status: rStatus === 'ALL' ? undefined : rStatus,
                startDate: rDateRange ? rDateRange[0].format('YYYY-MM-DD') : undefined,
                endDate: rDateRange ? rDateRange[1].format('YYYY-MM-DD') : undefined,
                limit: 200,
            });
            let items = res.data?.items || [];
            if (!rStatus) {
                // Default to non-completed
                items = items.filter((e: any) => e.status !== 'COMPLETED');
            }
            return items;
        }
    });

    const { data: financialCategoriesDict } = useQuery({
        queryKey: ['financial-categories'],
        queryFn: async () => {
            try {
                const res = await adminApi.getDictionary('FINANCIAL_CATEGORY');
                return res.data;
            } catch (e) {
                return null;
            }
        },
    });

    const { data: financialFrequencyDict } = useQuery({
        queryKey: ['financial-frequency'],
        queryFn: async () => {
            try {
                const res = await adminApi.getDictionary('FINANCIAL_FREQUENCY');
                return res.data;
            } catch (e) {
                return null;
            }
        },
    });

    const { data: allegroStatus } = useQuery({
        queryKey: ['allegro-status'],
        queryFn: async () => {
            try {
                const res = await integrationsApi.getAllegroStatus();
                return res.data;
            } catch (e) {
                return { connected: false };
            }
        },
    });

    const hasAllegroProfile = !!allegroStatus?.connected;

    const { data: allegroSummary } = useQuery({
        queryKey: ['allegro-summary', startDate, endDate],
        queryFn: () => financialEntriesApi.getAllegroSummary({ startDate, endDate }).then(res => res.data),
    });

    // Payouts from API
    const { data: allegroPayouts } = useQuery({
        queryKey: ['allegro-payouts', startDate, endDate],
        queryFn: () => integrationsApi.getAllegroPayouts({ startDate, endDate }).then(res => res.data),
    });

    // Fetch channels for grouping
    const { data: channels } = useQuery({
        queryKey: ['channels-list'],
        queryFn: () => channelsApi.getAll().then(res => res.data),
    });

    const { data: contractors } = useQuery({
        queryKey: ['crm-contractors-list'],
        queryFn: async () => {
            const res = await crmApi.getContractors({ limit: 100 });
            return res.data?.items || [];
        },
    });

    const { data: products } = useQuery({
        queryKey: ['products-list'],
        queryFn: async () => {
            const res = await productsApi.getAll({ limit: 100 });
            return res.data?.items || [];
        },
    });

    // Historical data for Months tab (13 months for YoY calculation)
    const startOf13MonthsAgo = dayjs().subtract(12, 'month').startOf('month').format('YYYY-MM-DD');
    const endOfCurrentMonth = dayjs().endOf('month').format('YYYY-MM-DD');

    const { data: historicalOrders } = useQuery({
        queryKey: ['historical-orders', startOf13MonthsAgo, endOfCurrentMonth],
        queryFn: async () => {
            const res = await ordersApi.getAll({
                startDate: startOf13MonthsAgo,
                endDate: endOfCurrentMonth,
                limit: 1000,
            });
            return res.data?.data || [];
        },
    });

    const { data: historicalEntries } = useQuery({
        queryKey: ['historical-entries', startOf13MonthsAgo, endOfCurrentMonth],
        queryFn: async () => {
            const res = await financialEntriesApi.getAll({
                startDate: startOf13MonthsAgo,
                endDate: endOfCurrentMonth,
                limit: 2000,
            });
            return res.data?.items || [];
        },
    });

    const { data: allegroCompanyCosts } = useQuery({
        queryKey: ['allegro-company-costs', startOf13MonthsAgo, endOfCurrentMonth],
        queryFn: async () => {
            try {
                const res = await financeApi.getCosts({
                    vendorName: 'Allegro',
                    startDate: startOf13MonthsAgo,
                    endDate: endOfCurrentMonth,
                });
                return res.data || [];
            } catch (e) {
                return [];
            }
        },
    });

    const { data: allEntriesForMapping } = useQuery({
        queryKey: ['all-entries-for-mapping'],
        queryFn: async () => {
            const res = await financialEntriesApi.getAll({
                limit: 10000,
            });
            return res.data?.items || [];
        },
    });

    const { data: financialDocuments, refetch: refetchFinancialDocuments, isLoading: isLoadingDocs } = useQuery({
        queryKey: ['financial-documents'],
        queryFn: async () => {
            const res = await filesApi.getFinancial();
            return res.data || [];
        },
    });

    const { data: templatesData, refetch: refetchTemplates } = useQuery({
        queryKey: ['financial-templates'],
        queryFn: async () => {
            const res = await financialEntriesApi.getAllTemplates();
            return res.data || [];
        },
    });

    const { data: recurringOccurrencesData, refetch: refetchRecurringOccurrences } = useQuery({
        queryKey: ['financial-entries-recurring-occurrences'],
        queryFn: async () => {
            const res = await financialEntriesApi.getAll({
                type: 'EXPENSE',
                limit: 1000,
            });
            return (res.data?.items || []).filter((e: any) => e.isPlannedOccurrence || e.recurringTemplateId);
        },
    });

    const usedContractors = React.useMemo(() => {
        const names = new Set<string>();
        (allEntriesForMapping || []).forEach((e: any) => {
            if (e.contractorName) names.add(e.contractorName.trim());
        });
        (templatesData || []).forEach((t: any) => {
            if (t.contractorName) names.add(t.contractorName.trim());
        });
        return Array.from(names).filter(Boolean).sort((a, b) => a.localeCompare(b, 'pl'));
    }, [allEntriesForMapping, templatesData]);

    const expenseCategories = React.useMemo(() => {
        const map = new Map<string, string>();
        // Add dictionary items first
        (financialCategoriesDict?.items || []).forEach((item: any) => {
            if (item.isActive && item.metadata?.financialType === 'EXPENSE') {
                map.set(item.code, item.name || item.code);
            }
        });
        (allEntriesForMapping || []).forEach((entry: any) => {
            if (entry.type === 'EXPENSE' && entry.category) {
                const catStr = renderCategory(entry.category).trim();
                if (catStr && !catStr.toLowerCase().includes('transfer')) {
                    const dictItem = (financialCategoriesDict?.items || []).find(
                        (i: any) => i.code === catStr || i.name === catStr
                    );
                    if (dictItem) {
                        map.set(dictItem.code, dictItem.name || dictItem.code);
                    } else {
                        map.set(catStr, catStr);
                    }
                }
            }
        });
        (templatesData || []).forEach((template: any) => {
            if (template.category) {
                const catStr = renderCategory(template.category).trim();
                if (catStr && !catStr.toLowerCase().includes('transfer')) {
                    const dictItem = (financialCategoriesDict?.items || []).find(
                        (i: any) => i.code === catStr || i.name === catStr
                    );
                    if (dictItem) {
                        map.set(dictItem.code, dictItem.name || dictItem.code);
                    } else {
                        map.set(catStr, catStr);
                    }
                }
            }
        });
        
        // Ensure Allegro categories are included in expenses list
        if (!map.has('LOGISTICS')) map.set('LOGISTICS', 'Logistyka');
        if (!map.has('OPERATIONS')) map.set('OPERATIONS', 'Operacje');
        if (!map.has('OTHER')) map.set('OTHER', 'Inne');
        
        return Array.from(map.entries())
            .map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    }, [allEntriesForMapping, templatesData, financialCategoriesDict]);

    const transferCategories = React.useMemo(() => {
        const map = new Map<string, string>();
        (financialCategoriesDict?.items || []).forEach((item: any) => {
            if (item.isActive && item.metadata?.financialType === 'TRANSFER') {
                map.set(item.code, item.name || item.code);
            }
        });
        (allEntriesForMapping || []).forEach((entry: any) => {
            if (entry.type === 'TRANSFER' && entry.category) {
                const catStr = renderCategory(entry.category).trim();
                if (catStr) {
                    const dictItem = (financialCategoriesDict?.items || []).find(
                        (i: any) => i.code === catStr || i.name === catStr
                    );
                    if (dictItem) {
                        map.set(dictItem.code, dictItem.name || dictItem.code);
                    } else {
                        map.set(catStr, catStr);
                    }
                }
            }
        });
        return Array.from(map.entries())
            .map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    }, [allEntriesForMapping, financialCategoriesDict]);

    const revenueCategories = React.useMemo(() => {
        const map = new Map<string, string>();
        (financialCategoriesDict?.items || []).forEach((item: any) => {
            if (item.isActive && item.metadata?.financialType === 'REVENUE') {
                map.set(item.code, item.name || item.code);
            }
        });
        (allEntriesForMapping || []).forEach((entry: any) => {
            if (entry.type === 'REVENUE' && entry.category) {
                const catStr = renderCategory(entry.category).trim();
                if (catStr) {
                    const dictItem = (financialCategoriesDict?.items || []).find(
                        (i: any) => i.code === catStr || i.name === catStr
                    );
                    if (dictItem) {
                        map.set(dictItem.code, dictItem.name || dictItem.code);
                    } else {
                        map.set(catStr, catStr);
                    }
                }
            }
        });
        return Array.from(map.entries())
            .map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    }, [allEntriesForMapping, financialCategoriesDict]);

    const displayedCategories = React.useMemo(() => {
        if (filterType === 'EXPENSE') return expenseCategories;
        if (filterType === 'TRANSFER') return transferCategories;
        if (filterType === 'REVENUE') return revenueCategories;
        const map = new Map<string, string>();
        [...expenseCategories, ...revenueCategories, ...transferCategories].forEach(cat => {
            map.set(cat.code, cat.name);
        });
        return Array.from(map.entries())
            .map(([code, name]) => ({ code, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
    }, [filterType, expenseCategories, revenueCategories, transferCategories]);

    const getCategoryName = React.useCallback((categoryStr: any) => {
        const catVal = renderCategory(categoryStr).trim();
        if (!catVal) return '';
        if (catVal === 'MARKETING' || catVal === 'EXP_MARKETING') return 'Marketing';
        if (catVal === 'LOGISTICS') return 'Logistyka';
        if (catVal === 'OPERATIONS') return 'Operacje';
        if (catVal === 'OTHER') return 'Inne';
        const dictItem = (financialCategoriesDict?.items || []).find(
            (item: any) => item.code === catVal || item.name === catVal
        );
        return dictItem ? (dictItem.name || dictItem.code) : catVal;
    }, [financialCategoriesDict]);

    const getCategoryCode = React.useCallback((categoryStr: any) => {
        const catVal = renderCategory(categoryStr).trim();
        if (!catVal) return '';
        if (catVal === 'MARKETING' || catVal === 'Marketing' || catVal === 'EXP_MARKETING') return 'EXP_MARKETING';
        if (catVal === 'LOGISTICS' || catVal === 'Logistyka') return 'LOGISTICS';
        if (catVal === 'OPERATIONS' || catVal === 'Operacje') return 'OPERATIONS';
        if (catVal === 'OTHER' || catVal === 'Inne') return 'OTHER';
        const dictItem = (financialCategoriesDict?.items || []).find(
            (item: any) => item.code === catVal || item.name === catVal
        );
        return dictItem ? dictItem.code : catVal;
    }, [financialCategoriesDict]);

    const formType = Form.useWatch('type', form);
    const formCategories = React.useMemo(() => {
        if (formType === 'EXPENSE') return expenseCategories;
        if (formType === 'TRANSFER') return transferCategories;
        if (formType === 'REVENUE') return revenueCategories;
        return [];
    }, [formType, expenseCategories, transferCategories, revenueCategories]);

    const getFileIcon = (mimeType: string) => {
        const mime = (mimeType || '').toLowerCase();
        if (mime.includes('pdf')) {
            return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
        }
        if (mime.includes('image')) {
            return <FileImageOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
        }
        if (mime.includes('excel') || mime.includes('csv') || mime.includes('sheet')) {
            return <FileExcelOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
        }
        if (mime.includes('zip') || mime.includes('rar') || mime.includes('tar') || mime.includes('compressed')) {
            return <FileZipOutlined style={{ color: '#fa8c16', fontSize: 16 }} />;
        }
        return <PaperClipOutlined style={{ color: token.colorPrimary, fontSize: 16 }} />;
    };

    const filteredDocuments = React.useMemo(() => {
        if (!financialDocuments) return [];

        return financialDocuments.filter((doc: any) => {
            // Find parent financial entry
            const entry = (allEntriesForMapping || []).find((e: any) => e.id === doc.entityId);

            // 1. Search by filename
            if (docSearchQuery) {
                const searchLower = docSearchQuery.toLowerCase();
                const filenameMatch = (doc.originalName || '').toLowerCase().includes(searchLower);
                const descMatch = (doc.description || '').toLowerCase().includes(searchLower);
                const entryDescMatch = (entry?.description || '').toLowerCase().includes(searchLower);
                const contractorMatch = (entry?.contractorName || '').toLowerCase().includes(searchLower);
                if (!filenameMatch && !descMatch && !entryDescMatch && !contractorMatch) {
                    return false;
                }
            }

            // 2. File type filter
            if (docFileTypeFilter) {
                const mime = (doc.mimeType || '').toLowerCase();
                const ext = (doc.originalName || '').split('.').pop()?.toLowerCase() || '';

                if (docFileTypeFilter === 'pdf') {
                    if (!mime.includes('pdf')) return false;
                } else if (docFileTypeFilter === 'image') {
                    if (!mime.includes('image')) return false;
                } else if (docFileTypeFilter === 'spreadsheet') {
                    const isSpreadsheet = mime.includes('excel') || mime.includes('csv') || mime.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext);
                    if (!isSpreadsheet) return false;
                } else if (docFileTypeFilter === 'other') {
                    const isPdf = mime.includes('pdf');
                    const isImg = mime.includes('image');
                    const isSpreadsheet = mime.includes('excel') || mime.includes('csv') || mime.includes('spreadsheet') || ['xls', 'xlsx', 'csv'].includes(ext);
                    if (isPdf || isImg || isSpreadsheet) return false;
                }
            }

            // 3. Entry type filter
            if (docEntryTypeFilter) {
                if (!entry || entry.type !== docEntryTypeFilter) {
                    return false;
                }
            }

            // 4. Date range filter
            if (docDateRangeFilter) {
                const docDate = dayjs(doc.createdAt);
                const start = docDateRangeFilter[0].startOf('day');
                const end = docDateRangeFilter[1].endOf('day');
                if (docDate.isBefore(start) || docDate.isAfter(end)) {
                    return false;
                }
            }

            return true;
        });
    }, [financialDocuments, allEntriesForMapping, docSearchQuery, docFileTypeFilter, docEntryTypeFilter, docDateRangeFilter]);



    const monthlyData = React.useMemo(() => {
        const monthMap: Record<string, any> = {};
        const monthsList: string[] = [];
        
        // Generate last 13 months keys (YYYY-MM)
        for (let i = 12; i >= 0; i--) {
            const m = dayjs().subtract(i, 'month');
            const key = m.format('YYYY-MM');
            monthsList.push(key);
            monthMap[key] = {
                monthKey: key,
                monthName: m.format('MMMM YYYY'),
                przychody: 0,
                koszty: 0,
                wynik: 0,
                cashPosition: 0,
                overdueCount: 0,
                openPayables: 0,
                openReceivables: 0,
                salesByChannel: {} as Record<string, number>
            };
        }

        // Process Orders
        (historicalOrders || []).forEach((order: any) => {
            // Exclude fake/test/dummy orders (without externalId)
            if (!order.externalId) return;

            const date = dayjs(order.orderDate || order.createdAt);
            const key = date.format('YYYY-MM');
            if (monthMap[key]) {
                const isExcludedStatus = ['PENDING', 'CANCELLED', 'REFUNDED'].includes(order.status);
                const isPaid = order.paymentStatus === 'PAID';
                if (reportMode === 'accrual') {
                    // Match backend ordersService.getStats filtering and amount logic:
                    // Only count PAID orders, exclude status: PENDING, CANCELLED, REFUNDED.
                    if (!isExcludedStatus && isPaid) {
                        const amount = Number(order.total || 0) - Number(order.refundAmount || 0);
                        monthMap[key].przychody += amount;
                        
                        // Channel breakdown
                        const channelName = order.channel?.name || 'Sprzedaż bezpośrednia';
                        monthMap[key].salesByChannel[channelName] = (monthMap[key].salesByChannel[channelName] || 0) + amount;
                    }
                } else {
                    // Cash mode: original logic
                    if (order.status !== 'CANCELLED') {
                        const amount = Number(order.paidAmount || 0);
                        monthMap[key].przychody += amount;
                        
                        // Channel breakdown
                        const channelName = order.channel?.name || 'Sprzedaż bezpośrednia';
                        monthMap[key].salesByChannel[channelName] = (monthMap[key].salesByChannel[channelName] || 0) + amount;
                    }
                }

                // Add Allegro costs to koszty in both modes
                if (!isExcludedStatus && isPaid) {
                    const allegroCost = Number(order.allegroCommission || 0) +
                                        Number(order.allegroPromotion || 0) +
                                        Number(order.allegroFulfillment || 0) +
                                        Number(order.allegroSmart || 0) +
                                        Number(order.allegroPayment || 0) +
                                        Number(order.allegroOther || 0);
                    if (allegroCost > 0) {
                        monthMap[key].koszty += allegroCost;
                    }
                }
                
                // Counts: Open Receivables and Overdue (keep original non-cancelled logic)
                if (order.status !== 'CANCELLED') {
                    if (order.paymentStatus !== 'PAID') {
                        monthMap[key].openReceivables += 1;
                    }
                    if (order.paymentStatus === 'FAILED' || order.status === 'OVERDUE') {
                        monthMap[key].overdueCount += 1;
                    }
                }
            }
        });

        // Process Financial Entries (Revenues & Expenses)
        (historicalEntries || []).forEach((entry: any) => {
            const amtNet = Number(entry.amountNet) || 0;
            const amtGross = Number(entry.amountGross) || 0;

            if (entry.type === 'REVENUE') {
                if (reportMode === 'accrual') {
                    const date = dayjs(entry.date);
                    const key = date.format('YYYY-MM');
                    if (monthMap[key]) {
                        monthMap[key].przychody += amtNet;
                        if (entry.status !== 'COMPLETED') monthMap[key].openReceivables += 1;
                        if (entry.status === 'OVERDUE') monthMap[key].openReceivables += 1;
                    }
                } else {
                    // Cash mode
                    if (entry.paymentHistory && entry.paymentHistory.length > 0) {
                        entry.paymentHistory.forEach((p: any) => {
                            const pDate = dayjs(p.date);
                            const key = pDate.format('YYYY-MM');
                            if (monthMap[key]) {
                                monthMap[key].przychody += Number(p.amount) || 0;
                            }
                        });
                    } else if (entry.status === 'COMPLETED') {
                        const date = dayjs(entry.date);
                        const key = date.format('YYYY-MM');
                        if (monthMap[key]) {
                            monthMap[key].przychody += amtGross;
                        }
                    }
                    
                    // Counts
                    const date = dayjs(entry.date);
                    const key = date.format('YYYY-MM');
                    if (monthMap[key]) {
                        if (entry.status !== 'COMPLETED') monthMap[key].openReceivables += 1;
                        if (entry.status === 'OVERDUE') monthMap[key].openReceivables += 1;
                    }
                }
            } else if (entry.type === 'EXPENSE') {
                if (reportMode === 'accrual') {
                    const date = dayjs(entry.date);
                    const key = date.format('YYYY-MM');
                    if (monthMap[key]) {
                        monthMap[key].koszty += amtNet;
                        if (entry.status !== 'COMPLETED') monthMap[key].openPayables += 1;
                        if (entry.status === 'OVERDUE') monthMap[key].overdueCount += 1;
                    }
                } else {
                    // Cash mode
                    if (entry.paymentHistory && entry.paymentHistory.length > 0) {
                        entry.paymentHistory.forEach((p: any) => {
                            const pDate = dayjs(p.date);
                            const key = pDate.format('YYYY-MM');
                            if (monthMap[key]) {
                                monthMap[key].koszty += Number(p.amount) || 0;
                            }
                        });
                    } else if (entry.status === 'COMPLETED') {
                        const date = dayjs(entry.date);
                        const key = date.format('YYYY-MM');
                        if (monthMap[key]) {
                            monthMap[key].koszty += amtGross;
                        }
                    }
                    
                    // Counts
                    const date = dayjs(entry.date);
                    const key = date.format('YYYY-MM');
                    if (monthMap[key]) {
                        if (entry.status !== 'COMPLETED') monthMap[key].openPayables += 1;
                        if (entry.status === 'OVERDUE') monthMap[key].overdueCount += 1;
                    }
                }
            }
        });

        // Process Allegro campaign-level costs (excluding aggregated monthly records to avoid duplication)
        (allegroCompanyCosts || []).forEach((cost: any) => {
            if (cost.customFields?.aggregated === true) return;
            const date = dayjs(cost.date);
            const key = date.format('YYYY-MM');
            if (monthMap[key]) {
                const amount = Number(cost.amount) || 0;
                monthMap[key].koszty += amount;
            }
        });

        // Calculate Result and Cumulative Cash Position
        let cumulativePosition = 0;
        const resultList = monthsList.map((key) => {
            const m = monthMap[key];
            m.wynik = m.przychody - m.koszty;
            cumulativePosition += m.wynik;
            m.cashPosition = cumulativePosition;
            return m;
        });

        // Add MoM and YoY
        resultList.forEach((m, idx) => {
            if (idx > 0) {
                const prev = resultList[idx - 1];
                if (prev.przychody > 0) m.momRevenue = ((m.przychody - prev.przychody) / prev.przychody) * 100;
                if (prev.koszty > 0) m.momExpense = ((m.koszty - prev.koszty) / prev.koszty) * 100;
                if (prev.wynik !== 0) m.momResult = ((m.wynik - prev.wynik) / Math.abs(prev.wynik)) * 100;
            }
            if (idx >= 12) {
                const yoy = resultList[idx - 12];
                if (yoy.przychody > 0) m.yoyRevenue = ((m.przychody - yoy.przychody) / yoy.przychody) * 100;
                if (yoy.koszty > 0) m.yoyExpense = ((m.koszty - yoy.koszty) / yoy.koszty) * 100;
                if (yoy.wynik !== 0) m.yoyResult = ((m.wynik - yoy.wynik) / Math.abs(yoy.wynik)) * 100;
            }
        });

        // Return the last 12 months (reversed so newest is first)
        return resultList.reverse().slice(0, 12);

    }, [historicalOrders, historicalEntries, reportMode, allegroCompanyCosts]);

    const { data: entryFiles, refetch: refetchEntryFiles } = useQuery({
        queryKey: ['financial-entry-files', selectedEntry?.id],
        queryFn: async () => {
            if (!selectedEntry?.id) return [];
            const isExpense = selectedEntry.type === 'EXPENSE';
            const res = await filesApi.getByEntity(isExpense ? 'COST' : 'OTHER', selectedEntry.id);
            return res.data || [];
        },
        enabled: !!selectedEntry?.id,
    });

    // --- Mutations ---
    const createMutation = useMutation({
        mutationFn: async (data: any) => {
            const res = await financialEntriesApi.create(data);
            const entryId = res.data?.id;
            if (entryId && uploadedFiles.length > 0) {
                for (const f of uploadedFiles) {
                    if (f.originFileObj) {
                        await filesApi.upload(f.originFileObj, data.type === 'EXPENSE' ? 'COST' : 'OTHER', entryId);
                    }
                }
            }
            return res.data;
        },
        onSuccess: () => {
            message.success('Wpis finansowy został dodany.');
            invalidateAllQueries();
            setIsFormOpen(false);
            form.resetFields();
            resetAmountCalculator();
            setUploadedFiles([]);
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: any }) => {
            const res = await financialEntriesApi.update(id, data);
            const entryId = res.data?.id || id;
            if (entryId && uploadedFiles.length > 0) {
                for (const f of uploadedFiles) {
                    if (f.originFileObj && !f.existingId) {
                        await filesApi.upload(f.originFileObj, data.type === 'EXPENSE' ? 'COST' : 'OTHER', entryId);
                    }
                }
            }
            return res.data;
        },
        onSuccess: (data) => {
            message.success('Zapisano zmiany we wpisie finansowym.');
            invalidateAllQueries();
            setIsFormOpen(false);
            setEditingEntryId(null);
            form.resetFields();
            resetAmountCalculator();
            setUploadedFiles([]);
            if (isDetailModalOpen && selectedEntry?.id === data?.id) {
                setSelectedEntry(data);
            }
        },
    });

    const registerPaymentMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: { amount: number; date: string; description?: string } }) =>
            financialEntriesApi.registerPayment(id, data),
        onSuccess: (res) => {
            message.success('Zarejestrowano płatność.');
            invalidateAllQueries();
            setSelectedEntry(res.data);
            paymentForm.resetFields();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => financialEntriesApi.delete(id),
        onSuccess: () => {
            message.success('Wpis został usunięty.');
            invalidateAllQueries();
            setIsDetailModalOpen(false);
        },
    });

    const deleteDocumentMutation = useMutation({
        mutationFn: (id: string) => filesApi.delete(id),
        onSuccess: () => {
            message.success('Dokument został usunięty.');
            queryClient.invalidateQueries({ queryKey: ['financial-documents'] });
            queryClient.invalidateQueries({ queryKey: ['all-entries-for-mapping'] });
            if (selectedEntry?.id) {
                queryClient.invalidateQueries({ queryKey: ['financial-entry-files', selectedEntry.id] });
            }
        },
        onError: () => {
            message.error('Nie udało się usunąć dokumentu.');
        },
    });

    const createLiquidityEntryMutation = useMutation({
        mutationFn: async (data: any) => {
            const res = await financialEntriesApi.create(data);
            return res.data;
        },
        onSuccess: () => {
            message.success('Operacja płynnościowa została zapisana.');
            invalidateAllQueries();
            setIsLiquidityModalOpen(false);
            liquidityForm.resetFields();
        },
        onError: () => {
            message.error('Nie udało się zapisać operacji płynnościowej.');
        },
    });

    const createTemplateMutation = useMutation({
        mutationFn: async (data: any) => {
            const res = await financialEntriesApi.createTemplate(data);
            return res.data;
        },
        onSuccess: () => {
            message.success('Szablon kosztu cyklicznego został dodany.');
            invalidateAllQueries();
            setIsTemplateModalOpen(false);
            templateForm.resetFields();
        },
    });

    const updateTemplateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: any }) => {
            const res = await financialEntriesApi.updateTemplate(id, data);
            return res.data;
        },
        onSuccess: () => {
            message.success('Zapisano zmiany w szablonie kosztu cyklicznego.');
            invalidateAllQueries();
            setIsTemplateModalOpen(false);
            setEditingTemplate(null);
            templateForm.resetFields();
        },
    });

    const deleteTemplateMutation = useMutation({
        mutationFn: async (id: string) => {
            const res = await financialEntriesApi.deleteTemplate(id);
            return res.data;
        },
        onSuccess: () => {
            message.success('Szablon został usunięty.');
            invalidateAllQueries();
        },
    });

    const invalidateAllQueries = () => {
        queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
        queryClient.invalidateQueries({ queryKey: ['financial-entries'] });
        queryClient.invalidateQueries({ queryKey: ['financial-entries-payables'] });
        queryClient.invalidateQueries({ queryKey: ['financial-entries-receivables'] });
        queryClient.invalidateQueries({ queryKey: ['financial-categories'] });
        queryClient.invalidateQueries({ queryKey: ['financial-hub-menu-status'] });
        queryClient.invalidateQueries({ queryKey: ['all-entries-for-mapping'] });
        queryClient.invalidateQueries({ queryKey: ['financial-documents'] });
        queryClient.invalidateQueries({ queryKey: ['financial-templates'] });
        queryClient.invalidateQueries({ queryKey: ['financial-entries-recurring-occurrences'] });
        if (selectedEntry?.id) {
            queryClient.invalidateQueries({ queryKey: ['financial-entry-files', selectedEntry.id] });
        }
    };

    // --- Helper calculations & state logic ---

    const guessVatRate = (net: number, vat: number) => {
        if (!net || !vat) return 23; // default
        const computed = (vat / net) * 100;
        if (Math.abs(computed - 23) < 0.5) return 23;
        if (Math.abs(computed - 8) < 0.5) return 8;
        if (Math.abs(computed - 5) < 0.5) return 5;
        if (Math.abs(computed - 0) < 0.5) return 0;
        return null;
    };

    const resetAmountCalculator = () => {
        setAmountNet(null);
        setAmountGross(null);
        setAmountVat(null);
        setVatRate(23); // Default to 23%
        setLastChanged([]);
    };

    const handleAmountChange = (field: 'net' | 'gross' | 'vat', value: number | null, formToUse = form) => {
        let n = field === 'net' ? value : amountNet;
        let g = field === 'gross' ? value : amountGross;
        let v = field === 'vat' ? value : amountVat;

        if (vatRate !== null && vatRate !== undefined) {
            if (field === 'net' && n !== null) {
                v = Number((n * (vatRate / 100)).toFixed(2));
                g = Number((n + v).toFixed(2));
            } else if (field === 'gross' && g !== null) {
                n = Number((g / (1 + vatRate / 100)).toFixed(2));
                v = Number((g - n).toFixed(2));
            } else if (field === 'vat') {
                g = Number((Number(n || 0) + Number(v)).toFixed(2));
            }
        } else {
            const nextHistory = [field, ...lastChanged.filter(f => f !== field)].slice(0, 2);
            setLastChanged(nextHistory);

            if (nextHistory.length === 2) {
                const hasNet = n !== null && n !== undefined;
                const hasGross = g !== null && g !== undefined;
                const hasVat = v !== null && v !== undefined;

                if (nextHistory.includes('net') && nextHistory.includes('vat') && hasNet && hasVat) {
                    g = Number((Number(n) + Number(v)).toFixed(2));
                } else if (nextHistory.includes('net') && nextHistory.includes('gross') && hasNet && hasGross) {
                    v = Number((Number(g) - Number(n)).toFixed(2));
                } else if (nextHistory.includes('gross') && nextHistory.includes('vat') && hasGross && hasVat) {
                    n = Number((Number(g) - Number(v)).toFixed(2));
                }
            }
        }

        setAmountNet(n);
        setAmountGross(g);
        setAmountVat(v);

        formToUse.setFieldsValue({
            amountNet: n,
            amountGross: g,
            amountVat: v,
        });
    };

    const handleVatRateChange = (rate: number | null, formToUse = form) => {
        setVatRate(rate);
        if (rate !== null && rate !== undefined) {
            if (amountNet !== null && amountNet !== undefined) {
                const n = amountNet;
                const v = Number((n * (rate / 100)).toFixed(2));
                const g = Number((n + v).toFixed(2));
                setAmountVat(v);
                setAmountGross(g);
                formToUse.setFieldsValue({ amountVat: v, amountGross: g });
            } else if (amountGross !== null && amountGross !== undefined) {
                const g = amountGross;
                const n = Number((g / (1 + rate / 100)).toFixed(2));
                const v = Number((g - n).toFixed(2));
                setAmountNet(n);
                setAmountVat(v);
                formToUse.setFieldsValue({ amountNet: n, amountVat: v });
            }
        }
    };

    // Auto-calculate on initial edit load
    const startEdit = async (entry: any) => {
        setEditingEntryId(entry.id);
        const net = Number(entry.amountNet);
        const vat = Number(entry.amountVat);
        const gross = Number(entry.amountGross);
        setAmountNet(net);
        setAmountGross(gross);
        setAmountVat(vat);

        const guessed = guessVatRate(net, vat);
        setVatRate(guessed);
        setLastChanged(['net', 'gross']);

        // Load files
        try {
            const isExpense = entry.type === 'EXPENSE';
            const res = await filesApi.getByEntity(isExpense ? 'COST' : 'OTHER', entry.id);
            const authStore = localStorage.getItem('curecraft-auth');
            let token = '';
            if (authStore) {
                try { token = JSON.parse(authStore).state?.token || ''; } catch { }
            }
            setUploadedFiles((res.data || []).map((f: any) => ({
                uid: f.id,
                name: f.originalName,
                status: 'done',
                existingId: f.id,
                url: `${filesApi.getDownloadUrl(f.id)}?token=${token}`,
            })));
        } catch (e) {
            setUploadedFiles([]);
        }

        form.setFieldsValue({
            type: entry.type,
            status: entry.status,
            date: dayjs(entry.date),
            dueDate: entry.dueDate ? dayjs(entry.dueDate) : null,
            category: entry.category ? [getCategoryCode(entry.category)] : [],
            contractorName: entry.contractorName,
            description: entry.description,
            amountNet: net,
            amountGross: gross,
            amountVat: vat,
            amountPaid: Number(entry.amountPaid) || 0,
        });
        setIsFormOpen(true);
    };

    const openNewEntryModal = (defaultType: 'EXPENSE' | 'REVENUE' | 'TRANSFER' = 'EXPENSE') => {
        resetAmountCalculator();
        setUploadedFiles([]);
        setVatRate(23); // Default to 23%
        form.resetFields();
        form.setFieldsValue({
            type: defaultType,
            status: 'PENDING',
            date: dayjs(),
            amountPaid: 0,
        });
        setEditingEntryId(null);
        setIsFormOpen(true);
    };

    const openLiquidityModal = (type: 'OPENING_BALANCE' | 'BALANCE_CORRECTION') => {
        setLiquidityModalType(type);
        liquidityForm.resetFields();
        liquidityForm.setFieldsValue({
            date: dayjs(),
        });
        setIsLiquidityModalOpen(true);
    };

    const handleLiquidityFormSubmit = async () => {
        try {
            const values = await liquidityForm.validateFields();
            const amount = Number(values.amount) || 0;
            const payload = {
                type: liquidityModalType,
                status: 'COMPLETED',
                date: values.date.format('YYYY-MM-DD'),
                amountNet: amount,
                amountGross: amount,
                amountVat: 0,
                amountPaid: amount,
                description: values.description || (liquidityModalType === 'OPENING_BALANCE' ? 'Saldo otwarcia' : 'Korekta salda'),
                category: liquidityModalType === 'OPENING_BALANCE' ? 'OPENING_BALANCE' : 'BALANCE_CORRECTION',
            };
            createLiquidityEntryMutation.mutate(payload);
        } catch (e) {
            // validation error
        }
    };

    const startEditTemplate = (template: any) => {
        setEditingTemplate(template);
        const net = Number(template.amountNet);
        const vat = Number(template.amountVat);
        const gross = Number(template.amountGross);
        setAmountNet(net);
        setAmountGross(gross);
        setAmountVat(vat);
        
        const guessed = guessVatRate(net, vat);
        setVatRate(guessed);
        setLastChanged(['net', 'gross']);

        templateForm.setFieldsValue({
            name: template.name,
            amountNet: net,
            amountGross: gross,
            amountVat: vat,
            category: template.category ? [getCategoryCode(template.category)] : [],
            contractorId: template.contractorId,
            contractorName: template.contractorName,
            frequency: template.frequency,
            startDate: dayjs(template.startDate),
            endDate: template.endDate ? dayjs(template.endDate) : null,
            billingDay: template.billingDay,
            isActive: template.isActive,
            note: template.note,
        });
        setIsTemplateModalOpen(true);
    };

    const openNewTemplateModal = () => {
        setEditingTemplate(null);
        resetAmountCalculator();
        templateForm.resetFields();
        templateForm.setFieldsValue({
            frequency: 'MONTHLY',
            startDate: dayjs(),
            billingDay: 1,
            isActive: true,
            amountNet: null,
            amountGross: null,
            amountVat: null,
        });
        setIsTemplateModalOpen(true);
    };

    const handleTemplateFormSubmit = async () => {
        try {
            const values = await templateForm.validateFields();
            
            const categoryStr = values.category ? renderCategory(values.category) : '';
            
            const payload = {
                ...values,
                category: categoryStr,
                contractorId: null,
                amountNet: amountNet !== null ? Number(amountNet) : 0,
                amountGross: amountGross !== null ? Number(amountGross) : 0,
                amountVat: amountVat !== null ? Number(amountVat) : 0,
                startDate: values.startDate.format('YYYY-MM-DD'),
                endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
            };

            if (editingTemplate) {
                updateTemplateMutation.mutate({ id: editingTemplate.id, data: payload });
            } else {
                createTemplateMutation.mutate(payload);
            }
        } catch (e) {
            console.error('Błąd walidacji szablonu:', e);
        }
    };

    const handleFormSubmit = () => {
        form.validateFields().then(values => {
            const catRaw = values.category;
            const categoryStr = Array.isArray(catRaw) ? (catRaw[0] || '').trim() : (catRaw || '').trim();

            const payload = {
                ...values,
                category: categoryStr,
                date: values.date.format('YYYY-MM-DD'),
                dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,
                amountNet: amountNet !== null ? amountNet : (values.amountNet || 0),
                amountGross: amountGross !== null ? amountGross : (values.amountGross || 0),
                amountVat: amountVat !== null ? amountVat : (values.amountVat || 0),
                amountPaid: values.amountPaid || 0,
                contractorId: null,
                channelId: null,
                productIds: null,
            };

            if (editingEntryId) {
                updateMutation.mutate({ id: editingEntryId, data: payload });
            } else {
                createMutation.mutate(payload);
            }
        });
    };

    const showDetails = (record: any) => {
        setSelectedEntry(record);
        setIsDetailModalOpen(true);
    };

    // Calculate delay in days
    const getDelayDays = (dueDateStr?: string) => {
        if (!dueDateStr) return 0;
        const due = dayjs(dueDateStr).startOf('day');
        const today = dayjs().startOf('day');
        return today.diff(due, 'day');
    };

    // Fill payment registration form when selected entry changes
    useEffect(() => {
        if (selectedEntry) {
            const remaining = Math.max(0, Number(selectedEntry.amountGross) - (Number(selectedEntry.amountPaid) || 0));
            paymentForm.setFieldsValue({
                amount: Number(remaining.toFixed(2)),
                date: dayjs(),
                description: 'Przelew bankowy',
            });
        }
    }, [selectedEntry, paymentForm]);

    const handleRegisterPayment = (values: any) => {
        if (!selectedEntry) return;
        registerPaymentMutation.mutate({
            id: selectedEntry.id,
            data: {
                amount: values.amount,
                date: values.date.format('YYYY-MM-DD'),
                description: values.description,
            }
        });
    };

    // Export Handlers
    const handleExportTransactions = async (format: 'CSV' | 'XLSX') => {
        const items = entriesData?.items || [];
        if (items.length === 0) {
            message.warning('Brak danych do wyeksportowania.');
            return;
        }

        const mappedData = items.map((entry: any) => {
            let statusLabel = 'Oczekujące';
            if (entry.status === 'COMPLETED') statusLabel = 'Zrealizowane';
            else if (entry.status === 'OVERDUE' || (entry.status === 'PENDING' && entry.dueDate && new Date(entry.dueDate) < new Date())) {
                statusLabel = 'Po terminie';
            } else if (entry.status === 'PLANNED') statusLabel = 'Planowane';

            let typeLabel = 'Transfer';
            if (entry.type === 'REVENUE') typeLabel = 'Przychód';
            else if (entry.type === 'EXPENSE') typeLabel = 'Koszt';

            let classLabel = 'Transfer';
            if (entry.classification === 'ACCRUAL') classLabel = 'Wynikowe';
            else if (entry.classification === 'CASH') classLabel = 'Gotówkowe';

            return {
                ...entry,
                typeLabel,
                classLabel,
                dateStr: entry.date ? dayjs(entry.date).format('YYYY-MM-DD') : '',
                dueDateStr: entry.dueDate ? dayjs(entry.dueDate).format('YYYY-MM-DD') : '',
                categoryName: getCategoryName(entry.category),
                contractorName: entry.contractorName || '',
                amountNet: entry.amountNet ? Number(entry.amountNet) : 0,
                amountGross: entry.amountGross ? Number(entry.amountGross) : 0,
                amountVat: entry.amountVat ? Number(entry.amountVat) : 0,
                statusLabel
            };
        });

        const columns = [
            { key: 'typeLabel', title: 'Typ' },
            { key: 'dateStr', title: 'Data' },
            { key: 'dueDateStr', title: 'Termin płatności' },
            { key: 'classLabel', title: 'Klasyfikacja' },
            { key: 'categoryName', title: 'Kategoria' },
            { key: 'description', title: 'Opis' },
            { key: 'contractorName', title: 'Kontrahent' },
            { key: 'amountNet', title: 'Kwota Netto (PLN)' },
            { key: 'amountGross', title: 'Kwota Brutto (PLN)' },
            { key: 'amountVat', title: 'Kwota VAT (PLN)' },
            { key: 'statusLabel', title: 'Status' }
        ];

        if (format === 'XLSX') {
            await exportToXLSX(mappedData, 'transakcje', columns);
        } else {
            exportToCSV(mappedData, 'transakcje', columns);
        }
    };

    const handleExportPayables = async (format: 'CSV' | 'XLSX') => {
        if (payablesSubTab === 'active') {
            const items = (payablesData || []).filter((e: any) => e.status !== 'PLANNED');
            if (items.length === 0) {
                message.warning('Brak danych do wyeksportowania.');
                return;
            }

            const mappedData = items.map((record: any) => {
                const remaining = Number(record.amountGross) - (Number(record.amountPaid) || 0);
                let statusLabel = 'Oczekuje';
                if (record.status === 'COMPLETED') {
                    statusLabel = 'Opłacone';
                } else {
                    const delay = getDelayDays(record.dueDate);
                    if (delay > 0) {
                        statusLabel = `Po terminie o ${delay} dni`;
                    }
                }
                return {
                    ...record,
                    dueDateStr: record.dueDate ? dayjs(record.dueDate).format('YYYY-MM-DD') : '',
                    categoryName: getCategoryName(record.category),
                    contractorName: record.contractorName || '',
                    amountNet: record.amountNet ? Number(record.amountNet) : 0,
                    amountGross: record.amountGross ? Number(record.amountGross) : 0,
                    amountVat: record.amountVat ? Number(record.amountVat) : 0,
                    amountPaid: record.amountPaid ? Number(record.amountPaid) : 0,
                    amountRemaining: remaining,
                    statusLabel
                };
            });

            const columns = [
                { key: 'description', title: 'Numer faktury / Opis' },
                { key: 'contractorName', title: 'Dostawca' },
                { key: 'categoryName', title: 'Kategoria' },
                { key: 'dueDateStr', title: 'Termin płatności' },
                { key: 'amountNet', title: 'Kwota Netto (PLN)' },
                { key: 'amountGross', title: 'Kwota Brutto (PLN)' },
                { key: 'amountVat', title: 'Kwota VAT (PLN)' },
                { key: 'amountPaid', title: 'Kwota Opłacona (PLN)' },
                { key: 'amountRemaining', title: 'Kwota Pozostała (PLN)' },
                { key: 'statusLabel', title: 'Status' }
            ];

            if (format === 'XLSX') {
                await exportToXLSX(mappedData, 'zobowiazania_biezace', columns);
            } else {
                exportToCSV(mappedData, 'zobowiazania_biezace', columns);
            }
        } else if (payablesSubTab === 'upcoming') {
            const items = recurringOccurrencesData || [];
            if (items.length === 0) {
                message.warning('Brak danych do wyeksportowania.');
                return;
            }

            const mappedData = items.map((record: any) => {
                let statusLabel = 'Nadchodzące';
                if (record.status === 'COMPLETED') {
                    statusLabel = 'Zrealizowano (Opłacone)';
                } else if (record.status === 'PENDING') {
                    statusLabel = 'Oczekujące (Zaksięgowane)';
                } else {
                    const isPast = dayjs(record.dueDate).isBefore(dayjs(), 'day');
                    if (isPast) {
                        statusLabel = 'Przeterminowane';
                    }
                }
                return {
                    ...record,
                    dueDateStr: record.dueDate ? dayjs(record.dueDate).format('YYYY-MM-DD') : '',
                    categoryName: getCategoryName(record.category),
                    contractorName: record.contractorName || '',
                    amountNet: record.amountNet ? Number(record.amountNet) : 0,
                    amountGross: record.amountGross ? Number(record.amountGross) : 0,
                    amountVat: record.amountVat ? Number(record.amountVat) : 0,
                    statusLabel
                };
            });

            const columns = [
                { key: 'description', title: 'Opis / Szablon' },
                { key: 'contractorName', title: 'Kontrahent' },
                { key: 'categoryName', title: 'Kategoria' },
                { key: 'dueDateStr', title: 'Planowany termin' },
                { key: 'amountNet', title: 'Kwota Netto (PLN)' },
                { key: 'amountGross', title: 'Kwota Brutto (PLN)' },
                { key: 'amountVat', title: 'Kwota VAT (PLN)' },
                { key: 'statusLabel', title: 'Status' }
            ];

            if (format === 'XLSX') {
                await exportToXLSX(mappedData, 'planowane_koszty', columns);
            } else {
                exportToCSV(mappedData, 'planowane_koszty', columns);
            }
        } else if (payablesSubTab === 'templates') {
            const items = templatesData || [];
            if (items.length === 0) {
                message.warning('Brak danych do wyeksportowania.');
                return;
            }

            const mappedData = items.map((record: any) => {
                let frequencyLabel = record.frequency;
                if (record.frequency === 'MONTHLY') frequencyLabel = 'Miesięcznie';
                else if (record.frequency === 'QUARTERLY') frequencyLabel = 'Kwartalnie';
                else if (record.frequency === 'YEARLY') frequencyLabel = 'Rocznie';

                return {
                    ...record,
                    categoryName: getCategoryName(record.category),
                    contractorName: record.contractorName || '',
                    frequencyLabel,
                    billingLabel: `Dzień ${record.billingDay}`,
                    amountNet: record.amountNet ? Number(record.amountNet) : 0,
                    amountGross: record.amountGross ? Number(record.amountGross) : 0,
                    amountVat: record.amountVat ? Number(record.amountVat) : 0,
                    isActiveLabel: record.isActive ? 'Aktywny' : 'Nieaktywny'
                };
            });

            const columns = [
                { key: 'name', title: 'Nazwa szablonu' },
                { key: 'contractorName', title: 'Kontrahent' },
                { key: 'categoryName', title: 'Kategoria' },
                { key: 'frequencyLabel', title: 'Częstotliwość' },
                { key: 'billingLabel', title: 'Rozliczenie' },
                { key: 'amountNet', title: 'Kwota Netto (PLN)' },
                { key: 'amountGross', title: 'Kwota Brutto (PLN)' },
                { key: 'amountVat', title: 'Kwota VAT (PLN)' },
                { key: 'isActiveLabel', title: 'Status szablonu' }
            ];

            if (format === 'XLSX') {
                await exportToXLSX(mappedData, 'szablony_kosztow', columns);
            } else {
                exportToCSV(mappedData, 'szablony_kosztow', columns);
            }
        }
    };

    const handleExportReceivables = async (format: 'CSV' | 'XLSX') => {
        const items = receivablesData || [];
        if (items.length === 0) {
            message.warning('Brak danych do wyeksportowania.');
            return;
        }

        const mappedData = items.map((record: any) => {
            const remaining = Number(record.amountGross) - (Number(record.amountPaid) || 0);
            let statusLabel = 'Oczekuje na wpływ';
            if (record.status === 'COMPLETED') {
                statusLabel = 'Odebrane';
            } else {
                const delay = getDelayDays(record.dueDate);
                if (delay > 0) {
                    statusLabel = `Po terminie o ${delay} dni`;
                } else if (record.status === 'PLANNED') {
                    statusLabel = 'Planowane wpływy';
                }
            }
            return {
                ...record,
                dueDateStr: record.dueDate ? dayjs(record.dueDate).format('YYYY-MM-DD') : '',
                categoryName: getCategoryName(record.category),
                contractorName: record.contractorName || '',
                amountNet: record.amountNet ? Number(record.amountNet) : 0,
                amountGross: record.amountGross ? Number(record.amountGross) : 0,
                amountVat: record.amountVat ? Number(record.amountVat) : 0,
                amountPaid: record.amountPaid ? Number(record.amountPaid) : 0,
                amountRemaining: remaining,
                statusLabel
            };
        });

        const columns = [
            { key: 'description', title: 'Sygnatura / Opis' },
            { key: 'contractorName', title: 'Klient B2B' },
            { key: 'categoryName', title: 'Kategoria' },
            { key: 'dueDateStr', title: 'Termin płatności' },
            { key: 'amountNet', title: 'Kwota Netto (PLN)' },
            { key: 'amountGross', title: 'Kwota Brutto (PLN)' },
            { key: 'amountVat', title: 'Kwota VAT (PLN)' },
            { key: 'amountPaid', title: 'Kwota Odebrana (PLN)' },
            { key: 'amountRemaining', title: 'Kwota Pozostała (PLN)' },
            { key: 'statusLabel', title: 'Status' }
        ];

        if (format === 'XLSX') {
            await exportToXLSX(mappedData, 'naleznosci', columns);
        } else {
            exportToCSV(mappedData, 'naleznosci', columns);
        }
    };

    const handleExportMonths = async (format: 'CSV' | 'XLSX') => {
        if (monthsViewMode === 'overview') {
            const items = monthlyData || [];
            if (items.length === 0) {
                message.warning('Brak danych do wyeksportowania.');
                return;
            }

            const mappedData = items.map((record: any) => ({
                ...record,
                przychody: record.przychody ? Number(record.przychody) : 0,
                koszty: record.koszty ? Number(record.koszty) : 0,
                wynik: record.wynik ? Number(record.wynik) : 0,
                cashPosition: record.cashPosition ? Number(record.cashPosition) : 0,
                openPayables: record.openPayables || 0,
                openReceivables: record.openReceivables || 0,
                overdueCount: record.overdueCount || 0
            }));

            const columns = [
                { key: 'monthName', title: 'Miesiąc' },
                { key: 'przychody', title: reportMode === 'accrual' ? 'Przychody wynikowo (PLN)' : 'Przychody kasowo (PLN)' },
                { key: 'koszty', title: reportMode === 'accrual' ? 'Koszty wynikowo (PLN)' : 'Koszty kasowo (PLN)' },
                { key: 'wynik', title: reportMode === 'accrual' ? 'Wynik memoriałowy (PLN)' : 'Wynik kasowy (PLN)' },
                { key: 'cashPosition', title: 'Pozycja gotówkowa na koniec (PLN)' },
                { key: 'openPayables', title: 'Otwarte zobowiązania (liczba)' },
                { key: 'openReceivables', title: 'Otwarte należności (liczba)' },
                { key: 'overdueCount', title: 'Opóźnione pozycje (liczba)' }
            ];

            if (format === 'XLSX') {
                await exportToXLSX(mappedData, 'podsumowanie_miesiecy', columns);
            } else {
                exportToCSV(mappedData, 'podsumowanie_miesiecy', columns);
            }
        } else if (monthsViewMode === 'planVsExecution') {
            const items = planVsExecutionData || [];
            if (items.length === 0) {
                message.warning('Brak danych do wyeksportowania.');
                return;
            }

            const mappedData = items.map((record: any) => {
                let statusLabel = 'Brak planu';
                if (record.status === 'exceeded') statusLabel = 'Przekroczone';
                else if (record.status === 'below') statusLabel = 'Poniżej planu';
                else if (record.status === 'normal') statusLabel = 'W normie';

                let variancePercentLabel = '';
                if (record.plan === 0 && record.wykonanie === 0) variancePercentLabel = '-';
                else if (record.plan === 0 && record.wykonanie > 0) variancePercentLabel = '+100.0% (brak planu)';
                else {
                    const sign = record.variance > 0 ? '+' : '';
                    variancePercentLabel = `${sign}${record.variancePercent.toFixed(1)}%`;
                }

                return {
                    ...record,
                    plan: record.plan ? Number(record.plan) : 0,
                    wykonanie: record.wykonanie ? Number(record.wykonanie) : 0,
                    variance: record.variance ? Number(record.variance) : 0,
                    variancePercentLabel,
                    statusLabel
                };
            });

            const columns = [
                { key: 'monthName', title: 'Miesiąc' },
                { key: 'plan', title: reportMode === 'accrual' ? 'Plan kosztów wynikowo (PLN)' : 'Plan kosztów kasowo (PLN)' },
                { key: 'wykonanie', title: reportMode === 'accrual' ? 'Wykonanie kosztów wynikowo (PLN)' : 'Wykonanie kosztów kasowo (PLN)' },
                { key: 'variance', title: 'Wariancja (PLN)' },
                { key: 'variancePercentLabel', title: 'Wariancja (%)' },
                { key: 'statusLabel', title: 'Status budżetu' }
            ];

            const suffix = selectedCategoryFilter ? `_${selectedCategoryFilter.toLowerCase()}` : '';
            if (format === 'XLSX') {
                await exportToXLSX(mappedData, `plan_vs_wykonanie${suffix}`, columns);
            } else {
                exportToCSV(mappedData, `plan_vs_wykonanie${suffix}`, columns);
            }
        }
    };

    // Plan vs Execution Calculations
    const planVsExecutionData = React.useMemo(() => {
        const monthMap: Record<string, any> = {};
        const monthsList: string[] = [];
        
        // Generate last 12 months keys (YYYY-MM)
        for (let i = 11; i >= 0; i--) {
            const m = dayjs().subtract(i, 'month');
            const key = m.format('YYYY-MM');
            monthsList.push(key);
            monthMap[key] = {
                monthKey: key,
                monthName: m.format('MMMM YYYY'),
                plan: 0,
                wykonanie: 0,
                variance: 0,
                variancePercent: 0,
                byCategory: {} as Record<string, { plan: number; wykonanie: number }>
            };
        }

        // Process all entries from allEntriesForMapping
        (allEntriesForMapping || []).forEach((entry: any) => {
            if (entry.type !== 'EXPENSE') return;

            const amtNet = Number(entry.amountNet) || 0;
            const amtGross = Number(entry.amountGross) || 0;
            
            // Map category
            const category = entry.category ? getCategoryCode(entry.category) : 'Inne';

            // Check if filtered by category (at table level)
            const isCategoryMatch = !selectedCategoryFilter || category === selectedCategoryFilter;

            const date = dayjs(entry.date);
            const key = date.format('YYYY-MM');
            if (monthMap[key]) {
                const amount = reportMode === 'accrual' ? amtNet : amtGross;
                
                const isPlanItem = entry.isPlannedOccurrence === true || entry.status === 'PLANNED';
                const isExecutionItem = entry.status !== 'PLANNED';

                // Initialize category in month breakdown if not exists
                if (!monthMap[key].byCategory[category]) {
                    monthMap[key].byCategory[category] = { plan: 0, wykonanie: 0 };
                }

                if (isPlanItem) {
                    if (isCategoryMatch) {
                        monthMap[key].plan += amount;
                    }
                    monthMap[key].byCategory[category].plan += amount;
                }
                
                if (isExecutionItem) {
                    if (isCategoryMatch) {
                        monthMap[key].wykonanie += amount;
                    }
                    monthMap[key].byCategory[category].wykonanie += amount;
                }
            }
        });

        // Process Allegro campaign-level and order-level costs (excluding aggregated monthly records if any left)
        (allegroCompanyCosts || []).forEach((cost: any) => {
            if (cost.customFields?.aggregated === true) return;
            const date = dayjs(cost.date);
            const key = date.format('YYYY-MM');
            if (monthMap[key]) {
                const amount = Number(cost.amount) || 0;
                // Map category
                const category = cost.category ? getCategoryCode(cost.category) : 'Inne';
                const isCategoryMatch = !selectedCategoryFilter || category === selectedCategoryFilter;

                // Initialize category in month breakdown if not exists
                if (!monthMap[key].byCategory[category]) {
                    monthMap[key].byCategory[category] = { plan: 0, wykonanie: 0 };
                }

                if (isCategoryMatch) {
                    monthMap[key].wykonanie += amount;
                }
                monthMap[key].byCategory[category].wykonanie += amount;
            }
        });

        // Compute variances and statuses
        const result = monthsList.map(key => {
            const m = monthMap[key];
            m.variance = m.wykonanie - m.plan;
            if (m.plan > 0) {
                m.variancePercent = (m.variance / m.plan) * 100;
            } else if (m.wykonanie > 0) {
                m.variancePercent = 100;
            } else {
                m.variancePercent = 0;
            }

            // Determine status
            if (m.plan === 0 && m.wykonanie === 0) {
                m.status = 'no_plan';
            } else if (m.wykonanie > m.plan) {
                m.status = 'exceeded';
            } else if (m.wykonanie < m.plan) {
                m.status = 'below';
            } else {
                m.status = 'normal';
            }

            return m;
        });

        // Return descending (newest month first)
        return result.reverse();

    }, [allEntriesForMapping, historicalOrders, reportMode, selectedCategoryFilter, allegroCompanyCosts]);

    const categoryBreakdownData = React.useMemo(() => {
        if (!selectedMonthForDrillDown) return [];
        
        const categoriesMap = selectedMonthForDrillDown.byCategory || {};
        return Object.entries(categoriesMap).map(([category, data]: [string, any]) => {
            const plan = data.plan || 0;
            const wykonanie = data.wykonanie || 0;
            const variance = wykonanie - plan;
            let variancePercent = 0;
            if (plan > 0) {
                variancePercent = (variance / plan) * 100;
            } else if (wykonanie > 0) {
                variancePercent = 100;
            }
            
            let status = 'normal';
            if (plan === 0 && wykonanie === 0) status = 'no_plan';
            else if (wykonanie > plan) status = 'exceeded';
            else if (wykonanie < plan) status = 'below';

            return {
                category,
                plan,
                wykonanie,
                variance,
                variancePercent,
                status
            };
        }).sort((a, b) => b.variance - a.variance); // Sort by highest overspending first
    }, [selectedMonthForDrillDown]);

    const monthEntriesForDrillDown = React.useMemo(() => {
        if (!selectedMonthForDrillDown) return { planned: [], actual: [] };

        const targetMonth = selectedMonthForDrillDown.monthKey; // e.g. "2026-05"
        
        const planned: any[] = [];
        const actual: any[] = [];

        (allEntriesForMapping || []).forEach((entry: any) => {
            if (entry.type !== 'EXPENSE') return;
            
            const date = dayjs(entry.date);
            if (date.format('YYYY-MM') !== targetMonth) return;

            const isPlanItem = entry.isPlannedOccurrence === true || entry.status === 'PLANNED';
            const isExecutionItem = entry.status !== 'PLANNED';

            if (isPlanItem) {
                planned.push(entry);
            }
            if (isExecutionItem) {
                actual.push(entry);
            }
        });

        return { planned, actual };
    }, [selectedMonthForDrillDown, allEntriesForMapping]);

    const filteredPlannedEntries = React.useMemo(() => {
        const list = monthEntriesForDrillDown.planned;
        if (!modalCategoryFilter) return list;
        return list.filter(entry => (entry.category ? getCategoryCode(entry.category) : 'Inne') === modalCategoryFilter);
    }, [monthEntriesForDrillDown.planned, modalCategoryFilter]);

    const filteredActualEntries = React.useMemo(() => {
        const list = monthEntriesForDrillDown.actual;
        if (!modalCategoryFilter) return list;
        return list.filter(entry => (entry.category ? getCategoryCode(entry.category) : 'Inne') === modalCategoryFilter);
    }, [monthEntriesForDrillDown.actual, modalCategoryFilter]);

    const modalKPIs = React.useMemo(() => {
        if (!selectedMonthForDrillDown) return { plan: 0, wykonanie: 0, variance: 0, variancePercent: 0 };

        const targetMonth = selectedMonthForDrillDown.monthKey;
        let plan = 0;
        let wykonanie = 0;

        (allEntriesForMapping || []).forEach((entry: any) => {
            if (entry.type !== 'EXPENSE') return;
            const date = dayjs(entry.date);
            if (date.format('YYYY-MM') !== targetMonth) return;

            const category = entry.category ? getCategoryCode(entry.category) : 'Inne';
            const isCategoryMatch = !modalCategoryFilter || category === modalCategoryFilter;

            const amtNet = Number(entry.amountNet) || 0;
            const amtGross = Number(entry.amountGross) || 0;
            const amount = reportMode === 'accrual' ? amtNet : amtGross;

            const isPlanItem = entry.isPlannedOccurrence === true || entry.status === 'PLANNED';
            const isExecutionItem = entry.status !== 'PLANNED';

            if (isPlanItem && isCategoryMatch) {
                plan += amount;
            }
            if (isExecutionItem && isCategoryMatch) {
                wykonanie += amount;
            }
        });

        const variance = wykonanie - plan;
        let variancePercent = 0;
        if (plan > 0) {
            variancePercent = (variance / plan) * 100;
        } else if (wykonanie > 0) {
            variancePercent = 100;
        }

        return { plan, wykonanie, variance, variancePercent };
    }, [selectedMonthForDrillDown, modalCategoryFilter, allEntriesForMapping, reportMode]);

    // kpi computed with order revenues
    // Calculate manual items (to exclude those that duplicate the automated ones)
    const filteredManualEntries = React.useMemo(() => {
        const items = entriesData?.items || [];
        return items.filter(e => {
            // Remove manual entries that represent Allegro payouts or marketplace fees to avoid duplication
            if (e.source === 'ALLEGRO' && e.type === 'TRANSFER') return false;
            // Optionally remove manual sales if we fully rely on orders now:
            // But prompt says: "nie mieszać ręcznie wpisanej sprzedaży z automatyczną sprzedażą z Zamówień."
            // So we can assume manual entries are kept unless explicitly marked as duplicated.
            return true;
        });
    }, [entriesData]);

    const transactionsFeed = React.useMemo(() => {
        const feed: any[] = [...filteredManualEntries];

        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).endOf('day');

        // 1. Add aggregated revenue from Orders per Channel (if REVENUE is allowed by filters)
        const showRevenue = !filterType || filterType === 'REVENUE';
        const showSalesCategory = !filterCategory || filterCategory === 'SALES_REVENUE' || filterCategory === 'Sprzedaż';
        const matchesClass = !filterClass || filterClass === (reportMode === 'cash' ? 'CASH' : 'ACCRUAL');

        if (showRevenue && showSalesCategory && matchesClass && channels && historicalOrders) {
            const paidOrders = historicalOrders.filter(o => {
                // Exclude fake/test/dummy orders (without externalId)
                if (!o.externalId) return false;

                const date = dayjs(o.orderDate || o.createdAt);
                if (date.isBefore(start) || date.isAfter(end)) return false;

                if (reportMode === 'accrual') {
                    const isExcludedStatus = ['PENDING', 'CANCELLED', 'REFUNDED'].includes(o.status);
                    const isPaid = o.paymentStatus === 'PAID';
                    return !isExcludedStatus && isPaid;
                } else {
                    return o.status !== 'CANCELLED';
                }
            });
            channels.forEach(ch => {
                const channelOrders = paidOrders.filter(o => o.channelId === ch.id);
                if (channelOrders.length > 0) {
                    const totalRevenue = channelOrders.reduce((sum, o) => {
                        if (reportMode === 'accrual') {
                            return sum + (Number(o.total || 0) - Number(o.refundAmount || 0));
                        } else {
                            return sum + Number(o.paidAmount || 0);
                        }
                    }, 0);
                    if (totalRevenue > 0) {
                        feed.push({
                            id: `agg-revenue-${ch.id}`,
                            isSynthetic: true,
                            type: 'REVENUE',
                            classification: reportMode === 'cash' ? 'CASH' : 'ACCRUAL', // Business sale
                            date: endDate,
                            dueDate: endDate,
                            category: 'SALES_REVENUE', // Use standard category code
                            description: `Przychody sprzedażowe - ${ch.name}`,
                            contractorName: 'Klienci Detaliczni',
                            amountGross: totalRevenue,
                            amountPaid: totalRevenue,
                            status: 'COMPLETED',
                            source: 'ORDERS',
                            channelId: ch.id,
                        });
                    }
                }
            });
        }

        // 2. Add Allegro Marketplace Costs (Aggregated into management groups)
        const showExpense = !filterType || filterType === 'EXPENSE';
        if (showExpense && matchesClass && allegroSummary) {
            const groups = [
                {
                    key: 'EXP_MARKETING',
                    amount: Number(allegroSummary.promotion || 0),
                    desc: 'Koszty Allegro - Marketing (Ads i Promocje)'
                },
                {
                    key: 'LOGISTICS',
                    amount: Number(allegroSummary.fulfillment || 0) + Number(allegroSummary.smart || 0),
                    desc: 'Koszty Allegro - Logistyka (Fulfillment i Smart)'
                },
                {
                    key: 'OPERATIONS',
                    amount: Number(allegroSummary.commission || 0) + Number(allegroSummary.payment || 0),
                    desc: 'Koszty Allegro - Operacje (Prowizje i Płatności)'
                },
                {
                    key: 'OTHER',
                    amount: Number(allegroSummary.other || 0),
                    desc: 'Koszty Allegro - Pozostałe'
                }
            ];

            groups.forEach(g => {
                if (g.amount > 0) {
                    const matchesCategory = !filterCategory || filterCategory === g.key;
                    if (matchesCategory) {
                        feed.push({
                            id: `agg-costs-allegro-${g.key.toLowerCase()}`,
                            isSynthetic: true,
                            type: 'EXPENSE',
                            classification: reportMode === 'cash' ? 'CASH' : 'ACCRUAL',
                            date: endDate,
                            dueDate: endDate,
                            category: g.key,
                            description: g.desc,
                            contractorName: 'Allegro',
                            amountGross: g.amount,
                            amountPaid: g.amount,
                            status: 'COMPLETED',
                            source: 'ALLEGRO',
                        });
                    }
                }
            });
        }

        // 3. Add actual Allegro Payouts (Transfers)
        const showTransfer = !filterType || filterType === 'TRANSFER';
        const showTransferCategory = !filterCategory || filterCategory === 'BANK_TRANSFER' || filterCategory === 'Transfer środków';
        if (showTransfer && showTransferCategory && (!filterClass || filterClass === 'TRANSFER') && allegroPayouts && Array.isArray(allegroPayouts)) {
            allegroPayouts.forEach(p => {
                feed.push({
                    id: p.id,
                    isSynthetic: true, // Mark as synthetic to prevent manual edit
                    type: 'TRANSFER',
                    classification: 'CASH',
                    date: p.settlementDate,
                    dueDate: p.settlementDate,
                    category: 'BANK_TRANSFER',
                    description: `Wypłata środków z Allegro (${p.type})`,
                    contractorName: 'Allegro',
                    amountGross: p.amount,
                    amountPaid: p.amount,
                    status: 'COMPLETED',
                    source: 'ALLEGRO',
                });
            });
        }

        // Sort feed by date descending
        return feed.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [filteredManualEntries, historicalOrders, channels, allegroSummary, allegroPayouts, endDate, startDate, filterType, filterCategory, reportMode, filterClass]);

    const kpi = React.useMemo(() => {
        let accrualOrderRevenue = 0;
        let cashOrderRevenue = 0;
        let openReceivablesFromOrders = 0;
        let overdueAmountFromOrders = 0;
        let overdueCountFromOrders = 0;
        let allegroAccrualCosts = 0;
        let allegroCashCosts = 0;

        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).endOf('day');

        (historicalOrders || []).forEach((order: any) => {
            // Exclude fake/test/dummy orders (without externalId)
            if (!order.externalId) return;

            const date = dayjs(order.orderDate || order.createdAt);
            if (date.isBefore(start) || date.isAfter(end)) return;

            const isExcludedStatus = ['PENDING', 'CANCELLED', 'REFUNDED'].includes(order.status);
            const isPaid = order.paymentStatus === 'PAID';
            if (!isExcludedStatus && isPaid) {
                accrualOrderRevenue += Number(order.total || 0) - Number(order.refundAmount || 0);
            }

            if (order.status !== 'CANCELLED') {
                cashOrderRevenue += Number(order.paidAmount || 0);
            }

            if (order.status !== 'CANCELLED' && order.status !== 'REFUNDED') {
                const remaining = Math.max(0, Number(order.total || 0) - Number(order.paidAmount || 0));
                if (order.paymentStatus !== 'PAID' && remaining > 0) {
                    openReceivablesFromOrders += remaining;

                    const isOverdue = order.status === 'OVERDUE' || order.paymentStatus === 'FAILED' ||
                                      (order.dueDate && new Date(order.dueDate) < new Date());
                    if (isOverdue) {
                        overdueAmountFromOrders += remaining;
                        overdueCountFromOrders += 1;
                    }
                }
            }
        });

        // Add Allegro campaign-level and order-level costs (excluding aggregated monthly records if any left)
        (allegroCompanyCosts || []).forEach((cost: any) => {
            if (cost.customFields?.aggregated === true) return;
            const date = dayjs(cost.date);
            if (date.isBefore(start) || date.isAfter(end)) return;
            const amount = Number(cost.amount) || 0;
            allegroAccrualCosts += amount;
            allegroCashCosts += amount;
        });

        const baseCashRevenue = summary?.cash?.revenue ?? 0;
        const baseAccrualRevenue = summary?.accrual?.revenue ?? 0;
        const baseCashCosts = summary?.cash?.costs ?? 0;
        const baseAccrualCosts = summary?.accrual?.costs ?? 0;

        const totalCashCosts = baseCashCosts + allegroCashCosts;
        const totalAccrualCosts = baseAccrualCosts + allegroAccrualCosts;

        return {
            cashState: summary?.cashState ?? null,
            revenue: reportMode === 'cash' ? (baseCashRevenue + cashOrderRevenue) : (baseAccrualRevenue + accrualOrderRevenue),
            costs: reportMode === 'cash' ? totalCashCosts : totalAccrualCosts,
            result: reportMode === 'cash' ? ((baseCashRevenue + cashOrderRevenue) - totalCashCosts) : ((baseAccrualRevenue + accrualOrderRevenue) - totalAccrualCosts),
            openPayables: summary?.openPayables ?? 0,
            openReceivables: (summary?.openReceivables ?? 0) + openReceivablesFromOrders,
            plannedCosts: summary?.plannedCostsCurrentMonth ?? 0,
            overdue: (summary?.overdueAmount ?? 0) + overdueAmountFromOrders,
            overdueCount: (summary?.overdueCount ?? 0) + overdueCountFromOrders,
            pendingCount: summary?.pendingCount ?? 0,
        };
    }, [historicalOrders, summary, reportMode, startDate, endDate, allegroCompanyCosts]);

    const cashflowForecast = React.useMemo(() => {
        const weeks: any[] = [];
        const today = dayjs();
        const currentMonday = getMonday(today);

        for (let i = 0; i < 6; i++) {
            const start = currentMonday.add(i, 'week').startOf('day');
            const end = start.add(6, 'day').endOf('day');
            weeks.push({
                index: i + 1,
                startDate: start,
                endDate: end,
                label: i === 0 ? 'Bieżący tydzień' : `Tydzień ${i + 1}`,
                dateRangeStr: `${start.format('DD.MM.YYYY')} - ${end.format('DD.MM.YYYY')}`,
                actualInflows: 0,
                expectedInflows: 0,
                actualOutflows: 0,
                expectedOutflows: 0,
                inflowItems: [],
                outflowItems: [],
            });
        }

        const entries = allEntriesForMapping || [];

        entries.forEach((entry: any) => {
            const amountGross = Number(entry.amountGross) || 0;
            const amountPaid = Number(entry.amountPaid) || 0;
            const amountRemaining = Math.max(0, amountGross - amountPaid);
            const isRevenue = entry.type === 'REVENUE';
            const isExpense = entry.type === 'EXPENSE';

            if (!isRevenue && !isExpense) return;

            if (entry.status === 'COMPLETED') {
                const date = dayjs(entry.date);
                weeks.forEach(w => {
                    if ((date.isAfter(w.startDate) || date.isSame(w.startDate)) && 
                        (date.isBefore(w.endDate) || date.isSame(w.endDate))) {
                        if (isRevenue) {
                            w.actualInflows += amountPaid > 0 ? amountPaid : amountGross;
                            w.inflowItems.push({ ...entry, _forecastType: 'actual' });
                        } else {
                            w.actualOutflows += amountPaid > 0 ? amountPaid : amountGross;
                            w.outflowItems.push({ ...entry, _forecastType: 'actual' });
                        }
                    }
                });
            } else {
                const dueDateStr = entry.dueDate || entry.date;
                if (!dueDateStr) return;

                const dueDate = dayjs(dueDateStr);
                const isOverdue = dueDate.isBefore(currentMonday, 'day');

                if (isOverdue) {
                    const w = weeks[0];
                    if (isRevenue) {
                        w.expectedInflows += amountRemaining;
                        w.inflowItems.push({ ...entry, _forecastType: 'overdue' });
                    } else {
                        w.expectedOutflows += amountRemaining;
                        w.outflowItems.push({ ...entry, _forecastType: 'overdue' });
                    }
                } else {
                    weeks.forEach(w => {
                        if ((dueDate.isAfter(w.startDate) || dueDate.isSame(w.startDate)) && 
                            (dueDate.isBefore(w.endDate) || dueDate.isSame(w.endDate))) {
                            if (isRevenue) {
                                w.expectedInflows += amountRemaining;
                                w.inflowItems.push({ ...entry, _forecastType: 'expected' });
                            } else {
                                w.expectedOutflows += amountRemaining;
                                w.outflowItems.push({ ...entry, _forecastType: 'expected' });
                            }
                        }
                    });
                }
            }
        });

        (historicalOrders || []).forEach((order: any) => {
            // Exclude fake/test/dummy orders (without externalId)
            if (!order.externalId) return;

            const amountGross = Number(order.total || 0);
            const amountPaid = Number(order.paidAmount || 0);
            const amountRemaining = Math.max(0, amountGross - amountPaid);
            
            if (order.status === 'CANCELLED' || order.status === 'REFUNDED') return;

            if (order.paymentStatus === 'PAID' || amountRemaining === 0) {
                const date = dayjs(order.orderDate || order.createdAt);
                weeks.forEach(w => {
                    if ((date.isAfter(w.startDate) || date.isSame(w.startDate)) && 
                        (date.isBefore(w.endDate) || date.isSame(w.endDate))) {
                        w.actualInflows += amountPaid > 0 ? amountPaid : amountGross;
                        w.inflowItems.push({ ...order, type: 'REVENUE', _forecastType: 'actual' });
                    }
                });
            } else {
                const dueDateStr = order.dueDate || order.orderDate || order.createdAt;
                if (!dueDateStr) return;

                const dueDate = dayjs(dueDateStr);
                const isOverdue = dueDate.isBefore(currentMonday, 'day');

                if (isOverdue) {
                    const w = weeks[0];
                    w.expectedInflows += amountRemaining;
                    w.inflowItems.push({ ...order, type: 'REVENUE', _forecastType: 'overdue' });
                } else {
                    weeks.forEach(w => {
                        if ((dueDate.isAfter(w.startDate) || dueDate.isSame(w.startDate)) && 
                            (dueDate.isBefore(w.endDate) || dueDate.isSame(w.endDate))) {
                            w.expectedInflows += amountRemaining;
                            w.inflowItems.push({ ...order, type: 'REVENUE', _forecastType: 'expected' });
                        }
                    });
                }
            }
        });

        let currentCash = kpi.cashState ?? 0;
        const processedWeeks = weeks.map(w => {
            const totalInflow = w.actualInflows + w.expectedInflows;
            const totalOutflow = w.actualOutflows + w.expectedOutflows;
            const netFlow = totalInflow - totalOutflow;
            const startCash = currentCash;
            const endCash = currentCash + netFlow;
            currentCash = endCash;

            w.inflowItems.sort((a: any, b: any) => {
                const dateA = a.dueDate || a.date;
                const dateB = b.dueDate || b.date;
                return new Date(dateA || 0).getTime() - new Date(dateB || 0).getTime();
            });
            w.outflowItems.sort((a: any, b: any) => {
                const dateA = a.dueDate || a.date;
                const dateB = b.dueDate || b.date;
                return new Date(dateA || 0).getTime() - new Date(dateB || 0).getTime();
            });

            return {
                ...w,
                totalInflow,
                totalOutflow,
                netFlow,
                startCash,
                endCash,
            };
        });

        return processedWeeks;
    }, [allEntriesForMapping, historicalOrders, kpi.cashState]);

    const totalForecastInflow = cashflowForecast.reduce((sum, w) => sum + w.totalInflow, 0);
    const totalForecastOutflow = cashflowForecast.reduce((sum, w) => sum + w.totalOutflow, 0);
    const hasLiquidityRisk = cashflowForecast.some(w => w.endCash < 0);

    // Aggregate monthly data from historical entries list for chart rendering (independent of filters)
    const getChartData = () => {
        const monthGroups: Record<string, { przychody: number; koszty: number; wplywy: number; wydatki: number }> = {};
        const months = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];

        // Seed with standard empty buckets for the current year months
        for (let i = 0; i <= new Date().getMonth(); i++) {
            monthGroups[months[i]] = { przychody: 0, koszty: 0, wplywy: 0, wydatki: 0 };
        }

        const entriesToAggregate = historicalEntries || [];
        const currentYear = new Date().getFullYear();

        entriesToAggregate.forEach((item: any) => {
            const dateObj = new Date(item.date);
            if (dateObj.getFullYear() !== currentYear) return;

            const monthName = months[dateObj.getMonth()];
            const amtNet = Number(item.amountNet) || 0;
            const amtGross = Number(item.amountGross) || 0;

            if (!monthGroups[monthName]) {
                monthGroups[monthName] = { przychody: 0, koszty: 0, wplywy: 0, wydatki: 0 };
            }

            if (item.type === 'REVENUE' || item.type === 'EXPENSE') {
                // Accrual (Wynikowo)
                if (item.type === 'REVENUE') {
                    monthGroups[monthName].przychody += amtNet;
                } else if (item.type === 'EXPENSE') {
                    monthGroups[monthName].koszty += amtNet;
                }

                // Cash (Gotówkowo)
                if (item.paymentHistory && item.paymentHistory.length > 0) {
                    item.paymentHistory.forEach((p: any) => {
                        const pDate = new Date(p.date);
                        if (pDate.getFullYear() === currentYear) {
                            const pMonthName = months[pDate.getMonth()];
                            if (monthGroups[pMonthName]) {
                                if (item.type === 'REVENUE') {
                                    monthGroups[pMonthName].wplywy += Number(p.amount) || 0;
                                } else if (item.type === 'EXPENSE') {
                                    monthGroups[pMonthName].wydatki += Number(p.amount) || 0;
                                }
                            }
                        }
                    });
                } else if (item.status === 'COMPLETED') {
                    if (item.type === 'REVENUE') {
                        monthGroups[monthName].wplywy += amtGross;
                    } else if (item.type === 'EXPENSE') {
                        monthGroups[monthName].wydatki += amtGross;
                    }
                }
            }
        });

        // Add order revenues and Allegro costs to chart
        (historicalOrders || []).forEach((order: any) => {
            // Exclude fake/test/dummy orders (without externalId)
            if (!order.externalId) return;

            const dateObj = new Date(order.orderDate || order.createdAt);
            if (dateObj.getFullYear() !== currentYear) return;

            const monthName = months[dateObj.getMonth()];

            if (!monthGroups[monthName]) {
                monthGroups[monthName] = { przychody: 0, koszty: 0, wplywy: 0, wydatki: 0 };
            }

            const isExcludedStatus = ['PENDING', 'CANCELLED', 'REFUNDED'].includes(order.status);
            const isPaid = order.paymentStatus === 'PAID';
            if (!isExcludedStatus && isPaid) {
                const amtNet = Number(order.total || 0) - Number(order.refundAmount || 0);
                monthGroups[monthName].przychody += amtNet;
            }

            if (order.status !== 'CANCELLED') {
                const amtCash = Number(order.paidAmount || 0);
                monthGroups[monthName].wplywy += amtCash;
            }

        });

        // Add Allegro campaign-level and order-level costs (excluding aggregated monthly records if any left)
        (allegroCompanyCosts || []).forEach((cost: any) => {
            if (cost.customFields?.aggregated === true) return;
            const dateObj = new Date(cost.date);
            if (dateObj.getFullYear() !== currentYear) return;

            const monthName = months[dateObj.getMonth()];

            if (!monthGroups[monthName]) {
                monthGroups[monthName] = { przychody: 0, koszty: 0, wplywy: 0, wydatki: 0 };
            }

            const amount = Number(cost.amount) || 0;
            monthGroups[monthName].koszty += amount;
            monthGroups[monthName].wydatki += amount;
        });

        return Object.keys(monthGroups).map(name => ({
            name,
            przychody: monthGroups[name].przychody,
            koszty: monthGroups[name].koszty,
            wplywy: monthGroups[name].wplywy,
            wydatki: monthGroups[name].wydatki,
            'Przychody/Wpływy': reportMode === 'accrual' ? monthGroups[name].przychody : monthGroups[name].wplywy,
            'Koszty/Wydatki': reportMode === 'accrual' ? monthGroups[name].koszty : monthGroups[name].wydatki,
        }));
    };

    const chartData = getChartData();

    // Get list of active overdue items for Attention Box (alerty) from ALL entries to maintain data consistency
    const overdueItemsList = React.useMemo(() => {
        const list = allEntriesForMapping || [];
        return list.filter((e: any) => {
            const isOverdue = e.status === 'OVERDUE' || (e.status === 'PENDING' && e.dueDate && new Date(e.dueDate) < new Date());
            return isOverdue && e.status !== 'COMPLETED';
        });
    }, [allEntriesForMapping]);

    // Get list of upcoming expenses in next 7 days for Attention Box (alerty)
    const upcomingExpensesList = React.useMemo(() => {
        const list = allEntriesForMapping || [];
        const today = dayjs().startOf('day');
        const inSevenDays = today.add(7, 'day').endOf('day');
        return list.filter((e: any) => {
            if (e.type !== 'EXPENSE' || e.status !== 'PENDING' || !e.dueDate) return false;
            const dueDate = dayjs(e.dueDate);
            return (dueDate.isAfter(today) || dueDate.isSame(today)) && dueDate.isBefore(inSevenDays);
        });
    }, [allEntriesForMapping]);

    const authStore = localStorage.getItem('curecraft-auth');
    let tokenVal = '';
    if (authStore) {
        try { tokenVal = JSON.parse(authStore).state?.token || ''; } catch { }
    }

    const isFilePreviewable = (mimeType?: string) => {
        if (!mimeType) return false;
        const previewable = [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/webp',
            'image/svg+xml',
            'application/pdf',
            'text/plain',
            'text/html',
            'text/css',
            'application/json',
        ];
        return previewable.includes(mimeType);
    };

    return (
        <div style={{ padding: '0 8px 24px 8px' }}>
            {/* Control Panel */}
            <div
                style={{
                    background: token.colorBgContainer,
                    padding: '20px 24px',
                    borderRadius: token.borderRadiusLG,
                    border: `1px solid ${token.colorBorderSecondary}`,
                    marginBottom: '24px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.02)',
                }}
            >
                <Row align="middle" justify="space-between" gutter={[16, 16]}>
                    <Col xs={24} lg={10}>
                        <Space direction="vertical" size={2}>
                            <Title level={2} style={{ margin: 0 }}>
                                Finanse <Tag color="blue" style={{ marginLeft: 8 }}>Hub</Tag>
                            </Title>
                            <Text type="secondary">
                                Podgląd kapitałowy, planowane zobowiązania, należności oraz księga rozliczeń.
                            </Text>
                        </Space>
                    </Col>
                    <Col xs={24} lg={14} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                        <Space wrap size="middle">
                            <Space>
                                <Text style={{ fontWeight: '500' }}>Tryb raportowania:</Text>
                                <Radio.Group
                                    value={reportMode}
                                    onChange={(e) => setReportMode(e.target.value)}
                                    buttonStyle="solid"
                                >
                                    <Radio.Button value="accrual">Wynikowo (Memoriał)</Radio.Button>
                                    <Radio.Button value="cash">Gotówkowo (Kasa)</Radio.Button>
                                </Radio.Group>
                            </Space>
                            <Divider type="vertical" />
                            <Select
                                value={period}
                                onChange={(val) => setPeriod(val)}
                                options={[
                                    { value: '7d', label: 'Ostatnie 7 dni' },
                                    { value: '30d', label: 'Ostatnie 30 dni' },
                                    { value: 'thisMonth', label: 'Bieżący miesiąc' },
                                    { value: 'lastMonth', label: 'Poprzedni miesiąc' },
                                    { value: 'thisQuarter', label: 'Bieżący kwartał' },
                                    { value: 'custom', label: 'Niestandardowy...' },
                                ]}
                                style={{ width: 170 }}
                            />
                            {period === 'custom' && (
                                <RangePicker
                                    value={customRange}
                                    onChange={(dates) => setCustomRange(dates as any)}
                                    style={{ width: 250 }}
                                />
                            )}
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Info Alert Banner about the module purpose */}
            <Alert
                message="Moduł finansowo-zarządczy"
                description="Moduł służy do rejestracji kosztów operacyjnych, transferów oraz innych ręcznych zdarzeń finansowych. Przychody ze sprzedaży produktów i zamówień B2C/B2B są automatycznie synchronizowane z modułu Zamówienia."
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{ marginBottom: '24px', borderRadius: token.borderRadiusLG }}
            />

            {/* Layout Sections */}
            <Tabs
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                type="card"
                items={[
                    {
                        key: 'overview',
                        label: (
                            <Space>
                                <LineChartOutlined />
                                <span>Przegląd</span>
                            </Space>
                        ),
                        children: (
                            <div>
                                {/* KPI cards */}
                                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid ${token.colorPrimary}` }}>
                                            {kpi.cashState !== null ? (
                                                <Statistic
                                                    title={<Text type="secondary" style={{ fontSize: '13px' }}><WalletOutlined style={{ marginRight: 6 }} />Stan gotówki firmy</Text>}
                                                    value={kpi.cashState}
                                                    precision={2}
                                                    suffix=" PLN"
                                                    valueStyle={{ color: token.colorPrimary, fontWeight: '700' }}
                                                />
                                            ) : (
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: '13px' }}><WalletOutlined style={{ marginRight: 6 }} />Stan gotówki firmy</Text>
                                                    <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: '14px' }}>—</div>
                                                </div>
                                            )}
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                {kpi.cashState !== null ? 'Saldo otwarcia + rzeczywiste ruchy gotówkowe' : (
                                                    <span style={{ color: '#fa8c16' }}>Brak danych — dodaj saldo otwarcia</span>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #52c41a` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><RiseOutlined style={{ marginRight: 6 }} />{reportMode === 'accrual' ? 'Przychody (Fakturowane)' : 'Wpływy Rzeczywiste'}</Text>}
                                                value={kpi.revenue}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#52c41a', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Suma przychodów w okresie
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #faad14` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><FallOutlined style={{ marginRight: 6 }} />{reportMode === 'accrual' ? 'Koszty (Zaksięgowane)' : 'Wydatki Rzeczywiste'}</Text>}
                                                value={kpi.costs}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#faad14', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Zarejestrowane obciążenia
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #13c2c2` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><DollarOutlined style={{ marginRight: 6 }} />{reportMode === 'accrual' ? 'Wynik Finansowy (Zysk)' : 'Przepływ Netto (Cashflow)'}</Text>}
                                                value={kpi.result}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#13c2c2', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Bilans operacyjny okresu
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #fa8c16` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><ClockCircleOutlined style={{ marginRight: 6 }} />Zobowiązania otwarte</Text>}
                                                value={kpi.openPayables}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#fa8c16', fontWeight: '600' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Koszt pozostały do spłaty
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #1890ff` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><ShoppingOutlined style={{ marginRight: 6 }} />Należności otwarte</Text>}
                                                value={kpi.openReceivables}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#1890ff', fontWeight: '600' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Wpływy pozostałe do pobrania
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #722ed1` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><CalendarOutlined style={{ marginRight: 6 }} />Zaplanowane koszty (m-c)</Text>}
                                                value={kpi.plannedCosts}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#722ed1', fontWeight: '600' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Prognoza obciążeń w tym miesiącu
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #ff4d4f` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><WarningOutlined style={{ marginRight: 6 }} />Zaległości po terminie</Text>}
                                                value={kpi.overdue}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#ff4d4f', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                <Badge status={kpi.overdueCount > 0 ? 'error' : 'success'} text={`${kpi.overdueCount} pozycji po terminie`} />
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Liquidity Management Panel */}
                                <Card
                                    style={{ marginBottom: '24px', borderRadius: token.borderRadiusLG, border: `1px solid ${token.colorBorderSecondary}`, background: token.colorFillQuaternary }}
                                    bodyStyle={{ padding: '12px 16px' }}
                                >
                                    <Space wrap>
                                        <Text type="secondary" style={{ fontSize: '13px' }}>
                                            <WalletOutlined style={{ marginRight: 6 }} />
                                            <strong>Zarządzanie saldem gotówki:</strong>
                                        </Text>
                                        <Button
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() => openLiquidityModal('OPENING_BALANCE')}
                                        >
                                            Saldo otwarcia
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={() => openLiquidityModal('BALANCE_CORRECTION')}
                                        >
                                            Korekta salda
                                        </Button>
                                        <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                            Operacje administracyjne — nie są traktowane jako przychody ani koszty operacyjne
                                        </Text>
                                    </Space>
                                </Card>

                                {/* Alerts, graph, integrations */}
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} lg={16}>
                                        {/* Alerts */}
                                        <Card
                                            title={
                                                <Space>
                                                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                                                    <span style={{ fontWeight: '600' }}>Wymaga uwagi (Alerty systemowe)</span>
                                                </Space>
                                            }
                                            style={{ marginBottom: '24px', borderRadius: token.borderRadiusLG }}
                                        >
                                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                {overdueItemsList.length > 0 && (
                                                    overdueItemsList.map((item: any) => {
                                                        const delay = getDelayDays(item.dueDate);
                                                        const isExpense = item.type === 'EXPENSE';
                                                        return (
                                                            <Alert
                                                                key={item.id}
                                                                message={
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <span>
                                                                            <strong>{isExpense ? 'Zobowiązanie' : 'Należność'}:</strong> {item.description} ({item.contractorName || 'Brak kontrahenta'})
                                                                        </span>
                                                                        <Button size="small" type="link" onClick={() => showDetails(item)}>
                                                                            Szczegóły <RightOutlined />
                                                                        </Button>
                                                                    </div>
                                                                }
                                                                description={
                                                                    <div>
                                                                        Termin minął {dayjs(item.dueDate).format('DD.MM.YYYY')} (<strong>Opóźnienie: {delay} dni</strong>). Kwota brutto: {Number(item.amountGross).toFixed(2)} PLN, pozostało: {(Number(item.amountGross) - Number(item.amountPaid)).toFixed(2)} PLN.
                                                                    </div>
                                                                }
                                                                type="error"
                                                                showIcon
                                                            />
                                                        );
                                                    })
                                                )}
                                                {upcomingExpensesList.length > 0 && (
                                                    upcomingExpensesList.map((item: any) => {
                                                        const daysLeft = dayjs(item.dueDate).diff(dayjs().startOf('day'), 'day');
                                                        return (
                                                            <Alert
                                                                key={item.id}
                                                                message={
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <span>
                                                                            <strong>Nadchodzące zobowiązanie:</strong> {item.description} ({item.contractorName || 'Brak kontrahenta'})
                                                                        </span>
                                                                        <Button size="small" type="link" onClick={() => showDetails(item)}>
                                                                            Szczegóły <RightOutlined />
                                                                        </Button>
                                                                    </div>
                                                                }
                                                                description={
                                                                    <div>
                                                                        Termin płatności: {dayjs(item.dueDate).format('DD.MM.YYYY')} (<strong>Pozostało: {daysLeft} dni</strong>). Kwota brutto: {Number(item.amountGross).toFixed(2)} PLN.
                                                                    </div>
                                                                }
                                                                type="warning"
                                                                showIcon
                                                            />
                                                        );
                                                    })
                                                )}
                                                {overdueItemsList.length === 0 && upcomingExpensesList.length === 0 && (
                                                    <Alert
                                                        message="Brak pilnych spraw finansowych"
                                                        description="Wszystkie zobowiązania i należności są rozliczane w terminie. Brak nadchodzących dużych płatności w ciągu najbliższych 7 dni."
                                                        type="success"
                                                        showIcon
                                                    />
                                                )}
                                                {kpi.openReceivables > 0 && overdueItemsList.filter(item => item.type === 'REVENUE').length === 0 && (
                                                    <Alert
                                                        message="Oczekujące należności"
                                                        description={`Brak opóźnień. System oczekuje na wpływ ${kpi.openReceivables.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN od kontrahentów.`}
                                                        type="info"
                                                        showIcon
                                                    />
                                                )}
                                            </Space>
                                        </Card>

                                        {/* Chart */}
                                        <Card
                                            title={
                                                <Space>
                                                    <LineChartOutlined />
                                                    <span>Porównanie finansowe miesięcy ({reportMode === 'accrual' ? 'Wynikowo - Zysk' : 'Gotówkowo - Obroty'})</span>
                                                </Space>
                                            }
                                            style={{ borderRadius: token.borderRadiusLG }}
                                        >
                                            <div style={{ width: '100%', height: 350 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={chartData}
                                                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                        <XAxis dataKey="name" />
                                                        <YAxis tickFormatter={(val) => `${val / 1000}k`} />
                                                        <ChartTooltip formatter={(val) => `${val.toLocaleString()} PLN`} />
                                                        <Legend />
                                                        <Bar dataKey="Przychody/Wpływy" fill="#52c41a" radius={[4, 4, 0, 0]} />
                                                        <Bar dataKey="Koszty/Wydatki" fill="#faad14" radius={[4, 4, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </Card>
                                    </Col>

                                    <Col xs={24} lg={8}>
                                        {/* Allegro integration card */}
                                        <Card
                                            title={
                                                <Space>
                                                    <ShoppingOutlined style={{ color: '#ff5a00' }} />
                                                    <span>Skrót Allegro Integrator</span>
                                                </Space>
                                            }
                                            style={{ marginBottom: '24px', borderRadius: token.borderRadiusLG }}
                                        >
                                            {allegroStatus?.connected ? (
                                                <>
                                                    <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff5a00' }}>
                                                            {(allegroSummary?.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                        </div>
                                                        <Text type="secondary">Zrealizowane koszty i prowizje Allegro</Text>
                                                    </div>
                                                    <Divider style={{ margin: '12px 0' }} />
                                                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text type="secondary">Prowizje sprzedaży:</Text>
                                                            <Text style={{ fontWeight: '500' }}>{(allegroSummary?.commission || 0).toLocaleString()} PLN</Text>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text type="secondary">Koszty wysyłek (Smart):</Text>
                                                            <Text style={{ fontWeight: '500' }}>{(allegroSummary?.smart || 0).toLocaleString()} PLN</Text>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text type="secondary">Inne koszty:</Text>
                                                            <Text style={{ fontWeight: '500' }}>{((allegroSummary?.promotion || 0) + (allegroSummary?.fulfillment || 0) + (allegroSummary?.payment || 0) + (allegroSummary?.other || 0)).toLocaleString()} PLN</Text>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text type="secondary">Status integracji:</Text>
                                                            <Tag color="success">Połączono {allegroStatus?.login ? `(${allegroStatus.login})` : ''}</Tag>
                                                        </div>
                                                    </Space>
                                                </>
                                            ) : (
                                                <div style={{ padding: '10px 0' }}>
                                                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                                                        <SyncOutlined style={{ fontSize: 24, color: '#ff5a00', marginBottom: 8 }} />
                                                        <div><Text strong>Brak połączenia z Allegro</Text></div>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            Skonfiguruj integrację z Allegro, aby pobierać dane o kosztach.
                                                        </Text>
                                                    </div>
                                                    <Divider style={{ margin: '12px 0' }} />
                                                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <Text type="secondary">Status połączenia:</Text>
                                                            <Tag color="orange">Oczekiwanie</Tag>
                                                        </div>
                                                    </Space>
                                                </div>
                                            )}
                                            <Divider style={{ margin: '16px 0' }} />
                                            <Button
                                                type="primary"
                                                ghost
                                                block
                                                icon={<ReloadOutlined />}
                                                onClick={() => {
                                                    if (allegroStatus?.connected) {
                                                        message.info('Odświeżono dane z Allegro...');
                                                    } else {
                                                        message.warning('Brak połączenia z Allegro');
                                                    }
                                                }}
                                            >
                                                Odśwież dane
                                            </Button>
                                        </Card>

                                        <Card
                                            title="Szybkie Akcje"
                                            style={{ borderRadius: token.borderRadiusLG }}
                                        >
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <Button
                                                    block
                                                    icon={<PlusOutlined />}
                                                    type="primary"
                                                    onClick={() => openNewEntryModal('EXPENSE')}
                                                >
                                                    Nowy wpis finansowy
                                                </Button>
                                            </Space>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        ),
                    },
                    {
                        key: 'transactions',
                        label: (
                            <Space>
                                <DollarOutlined />
                                <span>Transakcje</span>
                            </Space>
                        ),
                        children: (
                            <Card bordered={false}>
                                {/* Filters toolbar */}
                                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                                    <Space wrap>
                                        <Input
                                            placeholder="Wyszukaj opis..."
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            style={{ width: 180 }}
                                            allowClear
                                        />
                                        <Select
                                            placeholder="Typ"
                                            value={filterType}
                                            onChange={setFilterType}
                                            style={{ width: 120 }}
                                            allowClear
                                        >
                                            <Select.Option value="REVENUE">Przychód</Select.Option>
                                            <Select.Option value="EXPENSE">Koszt</Select.Option>
                                            <Select.Option value="TRANSFER">Transfer</Select.Option>
                                        </Select>
                                        <Select
                                            placeholder="Klasyfikacja"
                                            value={filterClass}
                                            onChange={setFilterClass}
                                            style={{ width: 140 }}
                                            allowClear
                                        >
                                            <Select.Option value="ACCRUAL">Wynikowe</Select.Option>
                                            <Select.Option value="CASH">Gotówkowe</Select.Option>
                                            <Select.Option value="TRANSFER">Transfer</Select.Option>
                                        </Select>
                                        <Select
                                            placeholder="Status"
                                            value={filterStatus}
                                            onChange={setFilterStatus}
                                            style={{ width: 130 }}
                                            allowClear
                                        >
                                            <Select.Option value="PLANNED">Planowane</Select.Option>
                                            <Select.Option value="PENDING">Oczekujące</Select.Option>
                                            <Select.Option value="COMPLETED">Zrealizowane</Select.Option>
                                            <Select.Option value="OVERDUE">Po terminie</Select.Option>
                                        </Select>
                                        <Select
                                            placeholder="Kategoria"
                                            value={filterCategory}
                                            onChange={setFilterCategory}
                                            style={{ width: 150 }}
                                            allowClear
                                        >
                                            {displayedCategories.map((cat) => (
                                                <Select.Option key={cat.code} value={cat.code}>{cat.name}</Select.Option>
                                            ))}
                                        </Select>
                                    </Space>

                                    <Space>
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: 'csv',
                                                        label: 'Eksportuj CSV',
                                                        onClick: () => handleExportTransactions('CSV'),
                                                    },
                                                    {
                                                        key: 'xlsx',
                                                        label: 'Eksportuj XLSX (Excel)',
                                                        onClick: () => handleExportTransactions('XLSX'),
                                                    },
                                                ],
                                            }}
                                        >
                                            <Button icon={<DownloadOutlined />}>Eksportuj</Button>
                                        </Dropdown>
                                        <Button
                                            type="primary"
                                            icon={<PlusOutlined />}
                                            onClick={() => openNewEntryModal('EXPENSE')}
                                        >
                                            Dodaj wpis
                                        </Button>
                                    </Space>
                                </div>

                                <Table
                                    loading={!entriesData}
                                    columns={[
                                        {
                                            title: 'Typ',
                                            dataIndex: 'type',
                                            key: 'type',
                                            width: 130,
                                            render: (type) => {
                                                if (type === 'REVENUE') return <Tag color="success">Przychód</Tag>;
                                                if (type === 'EXPENSE') return <Tag color="error">Koszt</Tag>;
                                                if (type === 'OPENING_BALANCE') return <Tag color="purple">Saldo otwarcia</Tag>;
                                                if (type === 'BALANCE_CORRECTION') return <Tag color="volcano">Korekta salda</Tag>;
                                                return <Tag color="processing">Transfer</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Sygnatura / Data',
                                            dataIndex: 'date',
                                            key: 'date',
                                            width: 120,
                                            sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
                                            defaultSortOrder: 'descend',
                                            render: (d) => dayjs(d).format('DD.MM.YYYY'),
                                        },
                                        {
                                            title: 'Termin płatności',
                                            dataIndex: 'dueDate',
                                            key: 'dueDate',
                                            width: 120,
                                            render: (d) => d ? dayjs(d).format('DD.MM.YYYY') : <Text type="secondary">-</Text>,
                                        },
                                        {
                                            title: (
                                                <Tooltip title="Klasyfikacja jest wyznaczana automatycznie. Tryb wynikowy (memoriał) bazuje na dacie zdarzenia; gotówkowy — na dacie płatności. To sposób patrzenia na dane w raportach, nie ręczny wybór użytkownika.">
                                                    Klasyfikacja <InfoCircleOutlined style={{ fontSize: 11, opacity: 0.5 }} />
                                                </Tooltip>
                                            ),
                                            dataIndex: 'classification',
                                            key: 'classification',
                                            width: 130,
                                            render: (c) => {
                                                if (c === 'ACCRUAL') return <Text type="secondary">Wynikowe</Text>;
                                                if (c === 'CASH') return <Text style={{ color: '#722ed1' }}>Gotówkowe</Text>;
                                                return <Text style={{ color: '#13c2c2' }}>Transfer</Text>;
                                            }
                                        },
                                        {
                                            title: 'Kategoria',
                                            dataIndex: 'category',
                                            key: 'category',
                                            width: 160,
                                            render: (val, record) => {
                                                const readableName = getCategoryName(val);
                                                // Fallback translation if dictionary doesn't resolve it properly
                                                let finalName = readableName;
                                                if (val === 'SALES_REVENUE') finalName = 'Przychody ze sprzedaży';
                                                else if (val === 'MARKETPLACE_FEE') finalName = 'Opłaty Marketplace';
                                                else if (val === 'MARKETING') finalName = 'Marketing';
                                                else if (val === 'LOGISTICS') finalName = 'Logistyka';
                                                else if (val === 'OPERATIONS') finalName = 'Operacje';
                                                else if (val === 'OPENING_BALANCE') finalName = 'Saldo otwarcia';
                                                else if (val === 'BALANCE_CORRECTION') finalName = 'Korekta salda';
                                                else if (val === 'OTHER') finalName = 'Inne';
                                                else if (val === 'BANK_TRANSFER') finalName = 'Transfer Bankowy';
                                                
                                                return (
                                                    <Space direction="vertical" size={0}>
                                                        <span style={{ fontWeight: 500 }}>{finalName}</span>
                                                        {record.isSynthetic && (
                                                            <Tag color="blue" style={{ marginTop: 2, fontSize: 10, lineHeight: '14px' }}>Automatyczne / Agregat</Tag>
                                                        )}
                                                    </Space>
                                                );
                                            },
                                        },
                                        {
                                            title: 'Opis',
                                            dataIndex: 'description',
                                            key: 'description',
                                            ellipsis: true,
                                        },
                                        {
                                            title: 'Kontrahent',
                                            dataIndex: 'contractorName',
                                            key: 'contractorName',
                                            width: 180,
                                            render: (name) => name || <Text type="secondary">-</Text>,
                                        },
                                        {
                                            title: 'Kwota Brutto',
                                            dataIndex: 'amountGross',
                                            key: 'amountGross',
                                            align: 'right' as const,
                                            width: 130,
                                            render: (val) => <Text style={{ fontWeight: '600' }}>{Number(val).toFixed(2)} PLN</Text>,
                                        },
                                        {
                                            title: 'Status',
                                            dataIndex: 'status',
                                            key: 'status',
                                            width: 120,
                                            render: (status, record) => {
                                                if (status === 'COMPLETED') return <Badge status="success" text="Zrealizowane" />;
                                                if (status === 'OVERDUE' || (status === 'PENDING' && record.dueDate && new Date(record.dueDate) < new Date())) {
                                                    return <Badge status="error" text="Po terminie" />;
                                                }
                                                if (status === 'PLANNED') return <Badge status="default" text="Planowane" />;
                                                return <Badge status="warning" text="Oczekujące" />;
                                            }
                                        },
                                        {
                                            title: 'Akcje',
                                            key: 'actions',
                                            width: 100,
                                            render: (_, record) => record.isSynthetic ? null : (
                                                <Space onClick={(e) => e.stopPropagation()}>
                                                    <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(record)} />
                                                    <Popconfirm
                                                        title="Czy na pewno chcesz usunąć ten wpis?"
                                                        onConfirm={() => deleteMutation.mutate(record.id)}
                                                        okText="Tak"
                                                        cancelText="Nie"
                                                    >
                                                        <Button size="small" danger icon={<DeleteOutlined />} />
                                                    </Popconfirm>
                                                </Space>
                                            )
                                        }
                                    ]}
                                    dataSource={transactionsFeed}
                                    rowKey="id"
                                    bordered
                                    onRow={(record) => ({
                                        onClick: (event) => {
                                            const target = event.target as HTMLElement;
                                            if (target.closest('.ant-btn') || target.closest('.ant-popconfirm') || target.closest('.ant-select') || target.closest('.ant-picker') || target.closest('.ant-popover')) {
                                                return;
                                            }
                                            showDetails(record);
                                        },
                                        style: { cursor: 'pointer' }
                                    })}
                                />
                            </Card>
                        ),
                    },
                    {
                        key: 'payables',
                        label: (
                            <Space>
                                <FallOutlined />
                                <span>Zobowiązania</span>
                            </Space>
                        ),
                        children: (
                            <Card bordered={false}>
                                {/* Sub-tab selection */}
                                <div style={{ marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                    <Radio.Group 
                                        optionType="button" 
                                        buttonStyle="solid" 
                                        value={payablesSubTab} 
                                        onChange={e => setPayablesSubTab(e.target.value)}
                                    >
                                        <Radio.Button value="active">
                                            <Space>
                                                <FallOutlined />
                                                <span>Bieżące zobowiązania</span>
                                            </Space>
                                        </Radio.Button>
                                        <Radio.Button value="upcoming">
                                            <Space>
                                                <CalendarOutlined />
                                                <span>Planowane obciążenia</span>
                                            </Space>
                                        </Radio.Button>
                                        <Radio.Button value="templates">
                                            <Space>
                                                <SettingOutlined />
                                                <span>Szablony kosztów</span>
                                            </Space>
                                        </Radio.Button>
                                    </Radio.Group>

                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: 'csv',
                                                    label: 'Eksportuj CSV',
                                                    onClick: () => handleExportPayables('CSV'),
                                                },
                                                {
                                                    key: 'xlsx',
                                                    label: 'Eksportuj XLSX (Excel)',
                                                    onClick: () => handleExportPayables('XLSX'),
                                                },
                                            ],
                                        }}
                                    >
                                        <Button icon={<DownloadOutlined />}>Eksportuj</Button>
                                    </Dropdown>
                                </div>

                                {payablesSubTab === 'active' && (
                                    <>
                                        {/* Payables filters toolbar */}
                                        <div style={{ marginBottom: 16, background: token.colorFillAlter, padding: '12px 16px', borderRadius: token.borderRadius, border: `1px solid ${token.colorBorderSecondary}` }}>
                                            <Row gutter={[8, 8]} align="middle">
                                                <Col xs={24} md={10}>
                                                    <Input
                                                        placeholder="Wyszukaj opis / dostawcę..."
                                                        value={pSearch}
                                                        onChange={e => setPSearch(e.target.value)}
                                                        allowClear
                                                    />
                                                </Col>
                                                <Col xs={12} md={6}>
                                                    <Select
                                                        placeholder="Status"
                                                        value={pStatus}
                                                        onChange={setPStatus}
                                                        style={{ width: '100%' }}
                                                        allowClear
                                                    >
                                                        <Select.Option value="ALL">Wszystkie</Select.Option>
                                                        <Select.Option value="PENDING">Oczekujące</Select.Option>
                                                        <Select.Option value="OVERDUE">Po terminie</Select.Option>
                                                        <Select.Option value="COMPLETED">Opłacone</Select.Option>
                                                    </Select>
                                                </Col>
                                                <Col xs={12} md={8}>
                                                    <RangePicker
                                                        value={pDateRange}
                                                        onChange={(dates) => setPDateRange(dates as any)}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Col>
                                            </Row>
                                        </div>

                                        <Table
                                            loading={!payablesData}
                                            columns={[
                                                {
                                                    title: 'Numer faktury / Opis',
                                                    dataIndex: 'description',
                                                    key: 'description',
                                                    render: (desc, record) => (
                                                        <Button type="link" onClick={() => showDetails(record)} style={{ padding: 0, height: 'auto', textAlign: 'left' }}>
                                                            {desc}
                                                        </Button>
                                                    )
                                                },
                                                { title: 'Dostawca', dataIndex: 'contractorName', key: 'contractorName', render: (name) => name || '-' },
                                                { 
                                                    title: 'Termin płatności', 
                                                    dataIndex: 'dueDate', 
                                                    key: 'dueDate', 
                                                    sorter: (a, b) => new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime(),
                                                    defaultSortOrder: 'descend',
                                                    render: (d) => d ? dayjs(d).format('DD.MM.YYYY') : '-' 
                                                },
                                                { title: 'Kwota Brutto', dataIndex: 'amountGross', key: 'amountGross', align: 'right' as const, render: (val) => `${Number(val).toFixed(2)} PLN` },
                                                {
                                                    title: 'Kwota Pozostała',
                                                    key: 'amountRemaining',
                                                    align: 'right' as const,
                                                    render: (_, record) => {
                                                        const rem = Number(record.amountGross) - (Number(record.amountPaid) || 0);
                                                        return <Text style={{ fontWeight: '600', color: rem > 0 ? '#fa8c16' : '#52c41a' }}>{rem.toFixed(2)} PLN</Text>;
                                                    }
                                                },
                                                {
                                                    title: 'Status / Opóźnienie',
                                                    key: 'status',
                                                    render: (_, record) => {
                                                        if (record.status === 'COMPLETED') {
                                                            return <Tag color="success">Opłacone</Tag>;
                                                        }
                                                        const delay = getDelayDays(record.dueDate);
                                                        if (delay > 0) {
                                                            return (
                                                                <Tooltip title={`Termin minął: ${dayjs(record.dueDate).format('DD.MM.YYYY')}`}>
                                                                    <Tag color="error">Po terminie o {delay} {delay === 1 ? 'dzień' : 'dni'}</Tag>
                                                                </Tooltip>
                                                            );
                                                        }
                                                        return <Tag color="orange">Oczekuje</Tag>;
                                                    }
                                                },
                                                {
                                                    title: 'Akcje',
                                                    key: 'actions',
                                                    width: 250,
                                                    render: (_, record) => (
                                                        <Space>
                                                            <Button
                                                                size="small"
                                                                type="primary"
                                                                ghost
                                                                icon={<CheckCircleOutlined />}
                                                                onClick={() => {
                                                                    updateMutation.mutate({
                                                                        id: record.id,
                                                                        data: { status: 'COMPLETED' }
                                                                    });
                                                                }}
                                                            >
                                                                Opłać w całości
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                icon={<PlusOutlined />}
                                                                onClick={() => showDetails(record)}
                                                            >
                                                                Częściowa spłata
                                                            </Button>
                                                        </Space>
                                                    )
                                                }
                                            ]}
                                            dataSource={(payablesData || []).filter((e: any) => e.status !== 'PLANNED')}
                                            rowKey="id"
                                            bordered
                                        />
                                    </>
                                )}

                                {payablesSubTab === 'upcoming' && (
                                    <>
                                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">
                                                Lista nadchodzących i historycznych obciążeń kosztów cyklicznych.
                                            </Text>
                                            <Button 
                                                type="primary"
                                                icon={<SyncOutlined spin={createTemplateMutation.isPending || updateTemplateMutation.isPending} />}
                                                onClick={() => {
                                                    refetchRecurringOccurrences();
                                                    message.success('Odświeżono pozycje planowane.');
                                                }}
                                            >
                                                Synchronizuj pozycje
                                            </Button>
                                        </div>

                                        <Table
                                            loading={!recurringOccurrencesData}
                                            columns={[
                                                {
                                                    title: 'Opis / Szablon',
                                                    dataIndex: 'description',
                                                    key: 'description',
                                                    render: (desc, record) => (
                                                        <Button type="link" onClick={() => showDetails(record)} style={{ padding: 0, height: 'auto', textAlign: 'left', fontWeight: 'bold' }}>
                                                            {desc}
                                                        </Button>
                                                    )
                                                },
                                                { 
                                                    title: 'Kontrahent', 
                                                    dataIndex: 'contractorName', 
                                                    key: 'contractorName', 
                                                    render: (name) => name || <Text type="secondary">-</Text> 
                                                },
                                                {
                                                    title: 'Kategoria',
                                                    dataIndex: 'category',
                                                    key: 'category',
                                                    render: (cat) => cat ? <Tag color="cyan">{renderCategory(cat)}</Tag> : <Text type="secondary">-</Text>
                                                },
                                                { 
                                                    title: 'Planowany termin', 
                                                    dataIndex: 'dueDate', 
                                                    key: 'dueDate', 
                                                    sorter: (a, b) => new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime(),
                                                    defaultSortOrder: 'ascend',
                                                    render: (d) => {
                                                        const isPast = dayjs(d).isBefore(dayjs(), 'day');
                                                        return (
                                                            <Text type={isPast ? 'danger' : undefined} style={{ fontWeight: isPast ? 'bold' : 'normal' }}>
                                                                {d ? dayjs(d).format('DD.MM.YYYY') : '-'}
                                                            </Text>
                                                        );
                                                    }
                                                },
                                                { 
                                                    title: 'Kwota Brutto', 
                                                    dataIndex: 'amountGross', 
                                                    key: 'amountGross', 
                                                    align: 'right' as const, 
                                                    render: (val) => <Text strong>{Number(val).toFixed(2)} PLN</Text> 
                                                },
                                                {
                                                    title: 'Status',
                                                    key: 'status',
                                                    render: (_, record) => {
                                                        if (record.status === 'COMPLETED') {
                                                            return <Tag color="success">Zrealizowano (Opłacone)</Tag>;
                                                        }
                                                        if (record.status === 'PENDING') {
                                                            return <Tag color="warning">Oczekujące (Zaksięgowane)</Tag>;
                                                        }
                                                        
                                                        const isPast = dayjs(record.dueDate).isBefore(dayjs(), 'day');
                                                        if (isPast) {
                                                            return (
                                                                <Tooltip title="Termin minął, a koszt nie został zrealizowany.">
                                                                    <Tag color="error">Przeterminowane</Tag>
                                                                </Tooltip>
                                                            );
                                                        }
                                                        
                                                        return <Tag color="processing">Nadchodzące</Tag>;
                                                    }
                                                },
                                                {
                                                    title: 'Akcje',
                                                    key: 'actions',
                                                    width: 320,
                                                    render: (_, record) => {
                                                        const isPlanned = record.status === 'PLANNED';
                                                        return (
                                                            <Space>
                                                                {isPlanned ? (
                                                                    <>
                                                                        <Popconfirm
                                                                            title="Czy chcesz oznaczyć ten koszt jako opłacony?"
                                                                            description="Status zostanie zmieniony na 'Zrealizowane'."
                                                                            onConfirm={() => {
                                                                                updateMutation.mutate({
                                                                                    id: record.id,
                                                                                    data: { status: 'COMPLETED' }
                                                                                });
                                                                            }}
                                                                            okText="Tak, opłać"
                                                                            cancelText="Anuluj"
                                                                        >
                                                                            <Button
                                                                                size="small"
                                                                                type="primary"
                                                                                icon={<CheckCircleOutlined />}
                                                                            >
                                                                                Zrealizuj (Opłać)
                                                                            </Button>
                                                                        </Popconfirm>
                                                                        <Popconfirm
                                                                            title="Czy chcesz zaksięgować ten koszt bez opłacania?"
                                                                            description="Status zmieni się na 'Oczekujące' i trafi do bieżących zobowiązań."
                                                                            onConfirm={() => {
                                                                                updateMutation.mutate({
                                                                                    id: record.id,
                                                                                    data: { status: 'PENDING' }
                                                                                });
                                                                            }}
                                                                            okText="Zaksięguj"
                                                                            cancelText="Anuluj"
                                                                        >
                                                                            <Button
                                                                                size="small"
                                                                                ghost
                                                                                type="primary"
                                                                                icon={<PlusOutlined />}
                                                                            >
                                                                                Zaksięguj (Pending)
                                                                            </Button>
                                                                        </Popconfirm>
                                                                    </>
                                                                ) : (
                                                                    <Text type="secondary" italic>Brak szybkich akcji</Text>
                                                                )}
                                                                <Button
                                                                    size="small"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => showDetails(record)}
                                                                >
                                                                    Szczegóły / Edycja ERP
                                                                </Button>
                                                            </Space>
                                                        );
                                                    }
                                                }
                                            ]}
                                            dataSource={recurringOccurrencesData || []}
                                            rowKey="id"
                                            bordered
                                        />
                                    </>
                                )}

                                {payablesSubTab === 'templates' && (
                                    <>
                                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">
                                                Definiuj szablony kosztów cyklicznych do automatycznego prognozowania obciążeń.
                                            </Text>
                                            <Button 
                                                type="primary" 
                                                icon={<PlusOutlined />} 
                                                onClick={openNewTemplateModal}
                                            >
                                                Dodaj szablon
                                            </Button>
                                        </div>

                                        <Table
                                            loading={!templatesData}
                                            columns={[
                                                {
                                                    title: 'Nazwa szablonu',
                                                    dataIndex: 'name',
                                                    key: 'name',
                                                    render: (name, record) => (
                                                        <Button type="link" onClick={() => startEditTemplate(record)} style={{ padding: 0, height: 'auto', textAlign: 'left', fontWeight: 'bold' }}>
                                                            {name}
                                                        </Button>
                                                    )
                                                },
                                                { 
                                                    title: 'Kontrahent', 
                                                    dataIndex: 'contractorName', 
                                                    key: 'contractorName', 
                                                    render: (name) => name || <Text type="secondary">-</Text> 
                                                },
                                                {
                                                    title: 'Kategoria',
                                                    dataIndex: 'category',
                                                    key: 'category',
                                                    render: (cat) => cat ? <Tag color="cyan">{renderCategory(cat)}</Tag> : <Text type="secondary">-</Text>
                                                },
                                                { 
                                                    title: 'Częstotliwość', 
                                                    dataIndex: 'frequency', 
                                                    key: 'frequency', 
                                                    render: (f) => {
                                                        if (f === 'MONTHLY') return <Tag color="blue">Miesięcznie</Tag>;
                                                        if (f === 'QUARTERLY') return <Tag color="purple">Kwartalnie</Tag>;
                                                        if (f === 'YEARLY') return <Tag color="magenta">Rocznie</Tag>;
                                                        return f;
                                                    }
                                                },
                                                { 
                                                    title: 'Rozliczenie', 
                                                    key: 'billing',
                                                    render: (_, record) => `Dzień ${record.billingDay}`
                                                },
                                                { 
                                                    title: 'Kwota Brutto', 
                                                    dataIndex: 'amountGross', 
                                                    key: 'amountGross', 
                                                    align: 'right' as const, 
                                                    render: (val) => `${Number(val).toFixed(2)} PLN` 
                                                },
                                                {
                                                    title: 'Status',
                                                    dataIndex: 'isActive',
                                                    key: 'isActive',
                                                    render: (active, record) => (
                                                        <Switch 
                                                            checked={active} 
                                                            onChange={(checked) => {
                                                                updateTemplateMutation.mutate({
                                                                    id: record.id,
                                                                    data: { isActive: checked }
                                                                });
                                                            }}
                                                            checkedChildren="Aktywny"
                                                            unCheckedChildren="Nieaktywny"
                                                        />
                                                    )
                                                },
                                                {
                                                    title: 'Akcje',
                                                    key: 'actions',
                                                    width: 180,
                                                    render: (_, record) => (
                                                        <Space>
                                                            <Button
                                                                size="small"
                                                                icon={<EditOutlined />}
                                                                onClick={() => startEditTemplate(record)}
                                                            >
                                                                Edytuj
                                                            </Button>
                                                            <Popconfirm
                                                                title="Usuń szablon"
                                                                description="Czy na pewno chcesz usunąć ten szablon? Usunie to również wszystkie przyszłe nieopłacone wystąpienia planowane."
                                                                onConfirm={() => deleteTemplateMutation.mutate(record.id)}
                                                                okText="Tak"
                                                                cancelText="Nie"
                                                            >
                                                                <Button
                                                                    size="small"
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                />
                                                            </Popconfirm>
                                                        </Space>
                                                    )
                                                }
                                            ]}
                                            dataSource={templatesData || []}
                                            rowKey="id"
                                            bordered
                                        />
                                    </>
                                )}
                            </Card>
                        ),
                    },
                    {
                        key: 'receivables',
                        label: (
                            <Space>
                                <RiseOutlined />
                                <span>Należności</span>
                            </Space>
                        ),
                        children: (
                            <Card bordered={false}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: 'csv',
                                                    label: 'Eksportuj CSV',
                                                    onClick: () => handleExportReceivables('CSV'),
                                                },
                                                {
                                                    key: 'xlsx',
                                                    label: 'Eksportuj XLSX (Excel)',
                                                    onClick: () => handleExportReceivables('XLSX'),
                                                },
                                            ],
                                        }}
                                    >
                                        <Button icon={<DownloadOutlined />}>Eksportuj</Button>
                                    </Dropdown>
                                </div>
                                {/* Receivables filters toolbar */}
                                <div style={{ marginBottom: 16, background: token.colorFillAlter, padding: '12px 16px', borderRadius: token.borderRadius, border: `1px solid ${token.colorBorderSecondary}` }}>
                                    <Row gutter={[8, 8]} align="middle">
                                        <Col xs={24} md={10}>
                                            <Input
                                                placeholder="Wyszukaj opis / klienta..."
                                                value={rSearch}
                                                onChange={e => setRSearch(e.target.value)}
                                                allowClear
                                            />
                                        </Col>
                                        <Col xs={12} md={6}>
                                            <Select
                                                placeholder="Status"
                                                value={rStatus}
                                                onChange={setRStatus}
                                                style={{ width: '100%' }}
                                                allowClear
                                            >
                                                <Select.Option value="ALL">Wszystkie</Select.Option>
                                                <Select.Option value="PENDING">Oczekujące</Select.Option>
                                                <Select.Option value="OVERDUE">Po terminie</Select.Option>
                                                <Select.Option value="PLANNED">Planowane</Select.Option>
                                                <Select.Option value="COMPLETED">Odebrane</Select.Option>
                                            </Select>
                                        </Col>
                                        <Col xs={12} md={8}>
                                            <RangePicker
                                                value={rDateRange}
                                                onChange={(dates) => setRDateRange(dates as any)}
                                                style={{ width: '100%' }}
                                            />
                                        </Col>
                                    </Row>
                                </div>

                                <Table
                                    loading={!receivablesData}
                                    columns={[
                                        {
                                            title: 'Sygnatura / Opis',
                                            dataIndex: 'description',
                                            key: 'description',
                                            render: (desc, record) => (
                                                <Button type="link" onClick={() => showDetails(record)} style={{ padding: 0, height: 'auto', textAlign: 'left' }}>
                                                    {desc}
                                                </Button>
                                            )
                                        },
                                        { title: 'Klient B2B', dataIndex: 'contractorName', key: 'contractorName', render: (name) => name || '-' },
                                        { 
                                            title: 'Termin płatności', 
                                            dataIndex: 'dueDate', 
                                            key: 'dueDate', 
                                            sorter: (a, b) => new Date(a.dueDate || 0).getTime() - new Date(b.dueDate || 0).getTime(),
                                            defaultSortOrder: 'descend',
                                            render: (d) => d ? dayjs(d).format('DD.MM.YYYY') : '-' 
                                        },
                                        { title: 'Kwota Brutto', dataIndex: 'amountGross', key: 'amountGross', align: 'right' as const, render: (val) => `${Number(val).toFixed(2)} PLN` },
                                        {
                                            title: 'Kwota Pozostała',
                                            key: 'amountRemaining',
                                            align: 'right' as const,
                                            render: (_, record) => {
                                                const rem = Number(record.amountGross) - (Number(record.amountPaid) || 0);
                                                return <Text style={{ fontWeight: '600', color: rem > 0 ? '#fa8c16' : '#52c41a' }}>{rem.toFixed(2)} PLN</Text>;
                                            }
                                        },
                                        {
                                            title: 'Status / Opóźnienie',
                                            key: 'status',
                                            render: (_, record) => {
                                                if (record.status === 'COMPLETED') {
                                                    return <Tag color="success">Odebrane</Tag>;
                                                }
                                                const delay = getDelayDays(record.dueDate);
                                                if (delay > 0) {
                                                    return (
                                                        <Tooltip title={`Termin minął: ${dayjs(record.dueDate).format('DD.MM.YYYY')}`}>
                                                            <Tag color="error">Po terminie o {delay} {delay === 1 ? 'dzień' : 'dni'}</Tag>
                                                        </Tooltip>
                                                    );
                                                }
                                                if (record.status === 'PLANNED') {
                                                    return <Tag color="default">Planowane wpływy</Tag>;
                                                }
                                                return <Tag color="orange">Oczekuje na wpływ</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Akcje',
                                            key: 'actions',
                                            width: 250,
                                            render: (_, record) => (
                                                <Space>
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        ghost
                                                        icon={<CheckCircleOutlined />}
                                                        onClick={() => {
                                                            updateMutation.mutate({
                                                                id: record.id,
                                                                data: { status: 'COMPLETED' }
                                                            });
                                                        }}
                                                    >
                                                        Zarejestruj wpływ
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => showDetails(record)}
                                                    >
                                                        Częściowa spłata
                                                    </Button>
                                                </Space>
                                            )
                                        }
                                    ]}
                                    dataSource={receivablesData || []}
                                    rowKey="id"
                                    bordered
                                />
                            </Card>
                        ),
                    },
                    {
                        key: 'months',
                        label: (
                            <Space>
                                <CalendarOutlined />
                                <span>Miesiące</span>
                            </Space>
                        ),
                        children: (
                            <Card bordered={false}>
                                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                                    <Space wrap>
                                        <Radio.Group 
                                            value={monthsViewMode} 
                                            onChange={(e) => setMonthsViewMode(e.target.value)}
                                            buttonStyle="solid"
                                        >
                                            <Radio.Button value="overview">Podsumowanie ogólne</Radio.Button>
                                            <Radio.Button value="planVsExecution">Plan vs Wykonanie kosztów</Radio.Button>
                                        </Radio.Group>
                                        
                                        {monthsViewMode === 'planVsExecution' && (
                                            <Space>
                                                <span style={{ fontSize: '13px', color: '#595959' }}>Kategoria kosztów:</span>
                                                <Select
                                                    allowClear
                                                    placeholder="Wszystkie kategorie"
                                                    style={{ width: 220 }}
                                                    value={selectedCategoryFilter}
                                                    onChange={(val) => setSelectedCategoryFilter(val)}
                                                >
                                                    {displayedCategories.map((cat) => (
                                                        <Select.Option key={cat.code} value={cat.code}>{cat.name}</Select.Option>
                                                    ))}
                                                </Select>
                                            </Space>
                                        )}
                                    </Space>

                                    <Dropdown
                                        menu={{
                                            items: [
                                                {
                                                    key: 'csv',
                                                    label: 'Eksportuj CSV',
                                                    onClick: () => handleExportMonths('CSV'),
                                                },
                                                {
                                                    key: 'xlsx',
                                                    label: 'Eksportuj XLSX (Excel)',
                                                    onClick: () => handleExportMonths('XLSX'),
                                                },
                                            ],
                                        }}
                                    >
                                        <Button icon={<DownloadOutlined />}>Eksportuj</Button>
                                    </Dropdown>
                                </div>

                                {monthsViewMode === 'overview' ? (
                                    <Table
                                        columns={[
                                            {
                                                title: 'Miesiąc',
                                                dataIndex: 'monthName',
                                                key: 'monthName',
                                                render: (text: string, record: any) => (
                                                    <Button 
                                                        type="link" 
                                                        onClick={() => {
                                                            setSelectedMonthDetails(record);
                                                            setIsMonthModalOpen(true);
                                                        }}
                                                        style={{ padding: 0, fontWeight: 600, fontSize: '14px' }}
                                                    >
                                                        {text}
                                                    </Button>
                                                )
                                            },
                                            {
                                                title: reportMode === 'accrual' ? 'Przychody (wynikowo)' : 'Przychody (kasowo)',
                                                dataIndex: 'przychody',
                                                key: 'przychody',
                                                align: 'right' as const,
                                                render: (val: number, record: any) => {
                                                    const renderTrend = (value?: number, label?: string) => {
                                                        if (value === undefined || isNaN(value)) {
                                                            if (label === 'YoY') return <span style={{ color: '#aaa', fontSize: '11px', display: 'block' }}>YoY: brak danych</span>;
                                                            return null;
                                                        }
                                                        const color = value >= 0 ? '#52c41a' : '#f5222d';
                                                        const Icon = value >= 0 ? RiseOutlined : FallOutlined;
                                                        const sign = value >= 0 ? '+' : '';
                                                        return (
                                                            <span style={{ color, fontSize: '11px', whiteSpace: 'nowrap', display: 'block' }}>
                                                                {label}: <Icon style={{ marginRight: 2 }} />{sign}{value.toFixed(1)}%
                                                            </span>
                                                        );
                                                    };
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: '500' }}>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</div>
                                                            <Space size={8} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                                                                {renderTrend(record.momRevenue, 'MoM')}
                                                                {renderTrend(record.yoyRevenue, 'YoY')}
                                                            </Space>
                                                        </div>
                                                    );
                                                }
                                            },
                                            {
                                                title: reportMode === 'accrual' ? 'Koszty (wynikowo)' : 'Koszty (kasowo)',
                                                dataIndex: 'koszty',
                                                key: 'koszty',
                                                align: 'right' as const,
                                                render: (val: number, record: any) => {
                                                    const renderTrend = (value?: number, label?: string) => {
                                                        if (value === undefined || isNaN(value)) {
                                                            if (label === 'YoY') return <span style={{ color: '#aaa', fontSize: '11px', display: 'block' }}>YoY: brak danych</span>;
                                                            return null;
                                                        }
                                                        const color = value >= 0 ? '#52c41a' : '#f5222d';
                                                        const Icon = value >= 0 ? RiseOutlined : FallOutlined;
                                                        const sign = value >= 0 ? '+' : '';
                                                        return (
                                                            <span style={{ color, fontSize: '11px', whiteSpace: 'nowrap', display: 'block' }}>
                                                                {label}: <Icon style={{ marginRight: 2 }} />{sign}{value.toFixed(1)}%
                                                            </span>
                                                        );
                                                    };
                                                    return (
                                                        <div>
                                                            <div style={{ fontWeight: '500' }}>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</div>
                                                            <Space size={8} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                                                                {renderTrend(record.momExpense, 'MoM')}
                                                                {renderTrend(record.yoyExpense, 'YoY')}
                                                            </Space>
                                                        </div>
                                                    );
                                                }
                                            },
                                            {
                                                title: reportMode === 'accrual' ? 'Wynik memoriałowy' : 'Wynik kasowy (Cashflow)',
                                                dataIndex: 'wynik',
                                                key: 'wynik',
                                                align: 'right' as const,
                                                render: (val: number, record: any) => {
                                                    const renderTrend = (value?: number, label?: string) => {
                                                        if (value === undefined || isNaN(value)) {
                                                            if (label === 'YoY') return <span style={{ color: '#aaa', fontSize: '11px', display: 'block' }}>YoY: brak danych</span>;
                                                            return null;
                                                        }
                                                        const color = value >= 0 ? '#52c41a' : '#f5222d';
                                                        const Icon = value >= 0 ? RiseOutlined : FallOutlined;
                                                        const sign = value >= 0 ? '+' : '';
                                                        return (
                                                            <span style={{ color, fontSize: '11px', whiteSpace: 'nowrap', display: 'block' }}>
                                                                {label}: <Icon style={{ marginRight: 2 }} />{sign}{value.toFixed(1)}%
                                                            </span>
                                                        );
                                                    };
                                                    return (
                                                        <div>
                                                            <Text style={{ color: val >= 0 ? 'green' : 'red', fontWeight: '600', fontSize: '14px' }}>
                                                                {val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                            </Text>
                                                            <Space size={8} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                                                                {renderTrend(record.momResult, 'MoM')}
                                                                {renderTrend(record.yoyResult, 'YoY')}
                                                            </Space>
                                                        </div>
                                                    );
                                                }
                                            },
                                            {
                                                title: 'Pozycja gotówkowa (na koniec)',
                                                dataIndex: 'cashPosition',
                                                key: 'cashPosition',
                                                align: 'right' as const,
                                                render: (val: number) => (
                                                    <Text style={{ color: val >= 0 ? '#1890ff' : 'red', fontWeight: '500' }}>
                                                        {val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                    </Text>
                                                )
                                            },
                                            {
                                                title: 'Statusy / Otwarte pozycje',
                                                key: 'operational',
                                                render: (_: any, record: any) => (
                                                    <Space direction="vertical" size={2} style={{ fontSize: '12px' }}>
                                                        {record.overdueCount > 0 ? (
                                                            <Badge status="error" text={<Text type="danger">{record.overdueCount} po terminie</Text>} />
                                                        ) : (
                                                            <Badge status="success" text="Brak opóźnień" />
                                                        )}
                                                        <Badge status="warning" text={`${record.openPayables} otwartych zobowiązań`} />
                                                        <Badge status="processing" text={`${record.openReceivables} otwartych należności`} />
                                                    </Space>
                                                )
                                            },
                                            {
                                                title: 'Akcja',
                                                key: 'action',
                                                render: (_: any, record: any) => (
                                                    <Button 
                                                        type="primary" 
                                                        ghost 
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedMonthDetails(record);
                                                            setIsMonthModalOpen(true);
                                                        }}
                                                    >
                                                        Szczegóły
                                                    </Button>
                                                )
                                            }
                                        ]}
                                        dataSource={monthlyData}
                                        bordered
                                        pagination={false}
                                        rowKey="monthKey"
                                    />
                                ) : (
                                    <Table
                                        columns={[
                                            {
                                                title: 'Miesiąc',
                                                dataIndex: 'monthName',
                                                key: 'monthName',
                                                render: (text: string, record: any) => (
                                                    <Button 
                                                        type="link" 
                                                        onClick={() => {
                                                            setSelectedMonthForDrillDown(record);
                                                            setIsDrillDownModalOpen(true);
                                                        }}
                                                        style={{ padding: 0, fontWeight: 600, fontSize: '14px' }}
                                                    >
                                                        {text}
                                                    </Button>
                                                )
                                            },
                                            {
                                                title: reportMode === 'accrual' ? 'Plan kosztów (wynikowo)' : 'Plan kosztów (kasowo)',
                                                dataIndex: 'plan',
                                                key: 'plan',
                                                align: 'right' as const,
                                                render: (val: number) => (
                                                    <span>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</span>
                                                )
                                            },
                                            {
                                                title: reportMode === 'accrual' ? 'Wykonanie kosztów (wynikowo)' : 'Wykonanie kosztów (kasowo)',
                                                dataIndex: 'wykonanie',
                                                key: 'wykonanie',
                                                align: 'right' as const,
                                                render: (val: number) => (
                                                    <span style={{ fontWeight: '600' }}>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</span>
                                                )
                                            },
                                            {
                                                title: 'Wariancja (PLN)',
                                                dataIndex: 'variance',
                                                key: 'variance',
                                                align: 'right' as const,
                                                render: (val: number) => {
                                                    const color = val > 0 ? '#ff4d4f' : (val < 0 ? '#52c41a' : '#595959');
                                                    const sign = val > 0 ? '+' : '';
                                                    return (
                                                        <span style={{ color, fontWeight: '500' }}>
                                                            {sign}{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                        </span>
                                                    );
                                                }
                                            },
                                            {
                                                title: 'Wariancja (%)',
                                                dataIndex: 'variancePercent',
                                                key: 'variancePercent',
                                                align: 'right' as const,
                                                render: (val: number, record: any) => {
                                                    if (record.plan === 0 && record.wykonanie === 0) return <span style={{ color: '#8c8c8c' }}>-</span>;
                                                    if (record.plan === 0 && record.wykonanie > 0) return <span style={{ color: '#ff4d4f', fontWeight: '500' }}>+100.0% (brak planu)</span>;
                                                    const color = record.variance > 0 ? '#ff4d4f' : (record.variance < 0 ? '#52c41a' : '#8c8c8c');
                                                    const sign = record.variance > 0 ? '+' : '';
                                                    return (
                                                        <span style={{ color, fontWeight: '500' }}>
                                                            {sign}{val.toFixed(1)}%
                                                        </span>
                                                    );
                                                }
                                            },
                                            {
                                                title: 'Status budżetu',
                                                dataIndex: 'status',
                                                key: 'status',
                                                align: 'center' as const,
                                                render: (status: string) => {
                                                    if (status === 'exceeded') return <Tag color="error" style={{ minWidth: '95px', textAlign: 'center' }}>Przekroczone</Tag>;
                                                    if (status === 'below') return <Tag color="success" style={{ minWidth: '95px', textAlign: 'center' }}>Poniżej planu</Tag>;
                                                    if (status === 'normal') return <Tag color="processing" style={{ minWidth: '95px', textAlign: 'center' }}>W normie</Tag>;
                                                    return <Tag color="default" style={{ minWidth: '95px', textAlign: 'center' }}>Brak planu</Tag>;
                                                }
                                            },
                                            {
                                                title: 'Akcja',
                                                key: 'action',
                                                align: 'center' as const,
                                                render: (_: any, record: any) => (
                                                    <Button 
                                                        type="primary" 
                                                        ghost 
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedMonthForDrillDown(record);
                                                            setIsDrillDownModalOpen(true);
                                                        }}
                                                    >
                                                        Szczegóły
                                                    </Button>
                                                )
                                            }
                                        ]}
                                        dataSource={planVsExecutionData}
                                        bordered
                                        pagination={false}
                                        rowKey="monthKey"
                                    />
                                )}
                            </Card>
                        ),
                    },
                    {
                        key: 'documents',
                        label: (
                            <Space>
                                <FileTextOutlined />
                                <span>Dokumenty</span>
                            </Space>
                        ),
                        children: (
                            <Card bordered={false} bodyStyle={{ padding: '0px' }}>
                                {/* Filters Toolbar */}
                                <div style={{ 
                                    padding: '16px', 
                                    background: token.colorFillAlter, 
                                    borderBottom: `1px solid ${token.colorBorderSecondary}`, 
                                    borderRadius: `${token.borderRadiusLG}px ${token.borderRadiusLG}px 0 0` 
                                }}>
                                    <Row gutter={[12, 12]} align="middle">
                                        <Col xs={24} md={6}>
                                            <Input
                                                placeholder="Szukaj po nazwie pliku, opisie..."
                                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                value={docSearchQuery}
                                                onChange={e => setDocSearchQuery(e.target.value)}
                                                allowClear
                                            />
                                        </Col>
                                        <Col xs={12} sm={8} md={4}>
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Typ pliku"
                                                value={docFileTypeFilter}
                                                onChange={val => setDocFileTypeFilter(val)}
                                                allowClear
                                                options={[
                                                    { label: 'Wszystkie typy', value: '' },
                                                    { label: 'Pliki PDF', value: 'pdf' },
                                                    { label: 'Obrazy', value: 'image' },
                                                    { label: 'Arkusze (Excel/CSV)', value: 'spreadsheet' },
                                                    { label: 'Inne', value: 'other' },
                                                ]}
                                            />
                                        </Col>
                                        <Col xs={12} sm={8} md={4}>
                                            <Select
                                                style={{ width: '100%' }}
                                                placeholder="Rodzaj wpisu"
                                                value={docEntryTypeFilter}
                                                onChange={val => setDocEntryTypeFilter(val)}
                                                allowClear
                                                options={[
                                                    { label: 'Wszystkie rodzaje', value: '' },
                                                    { label: 'Przychody', value: 'REVENUE' },
                                                    { label: 'Koszty', value: 'EXPENSE' },
                                                    { label: 'Transfery', value: 'TRANSFER' },
                                                ]}
                                            />
                                        </Col>
                                        <Col xs={24} sm={8} md={6}>
                                            <RangePicker
                                                style={{ width: '100%' }}
                                                placeholder={['Dodano od', 'Dodano do']}
                                                value={docDateRangeFilter}
                                                onChange={val => setDocDateRangeFilter(val as any)}
                                            />
                                        </Col>
                                        <Col xs={24} md={4} style={{ textAlign: 'right' }}>
                                            <Space>
                                                <Button 
                                                    icon={<ReloadOutlined />} 
                                                    onClick={() => refetchFinancialDocuments()}
                                                    title="Odśwież listę"
                                                />
                                                {(docSearchQuery || docFileTypeFilter || docEntryTypeFilter || docDateRangeFilter) && (
                                                    <Button 
                                                        onClick={() => {
                                                            setDocSearchQuery('');
                                                            setDocFileTypeFilter(undefined);
                                                            setDocEntryTypeFilter(undefined);
                                                            setDocDateRangeFilter(null);
                                                        }}
                                                    >
                                                        Reset
                                                    </Button>
                                                )}
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Table / List */}
                                <Table
                                    loading={isLoadingDocs}
                                    dataSource={filteredDocuments}
                                    rowKey="id"
                                    pagination={{
                                        pageSize: 15,
                                        showSizeChanger: true,
                                        showTotal: (total) => `Łącznie: ${total} dokumentów`,
                                    }}
                                    columns={[
                                        {
                                            title: 'Nazwa pliku',
                                            dataIndex: 'originalName',
                                            key: 'originalName',
                                            render: (name, record) => {
                                                const downloadUrl = `${filesApi.getDownloadUrl(record.id)}?token=${tokenVal}`;
                                                const sizeKB = (record.size / 1024).toFixed(1);
                                                return (
                                                    <Space align="start">
                                                        {getFileIcon(record.mimeType)}
                                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                            <a 
                                                                href={downloadUrl} 
                                                                download 
                                                                style={{ fontWeight: '500', color: token.colorPrimary }}
                                                            >
                                                                {name}
                                                            </a>
                                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                Rozmiar: {sizeKB} KB
                                                            </Text>
                                                        </div>
                                                    </Space>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Typ',
                                            dataIndex: 'mimeType',
                                            key: 'mimeType',
                                            width: 100,
                                            render: (mime, record) => {
                                                const ext = record.originalName?.split('.').pop()?.toUpperCase() || '';
                                                return <Tag color="default">{ext || mime?.split('/').pop()?.toUpperCase()}</Tag>;
                                            }
                                        },
                                        {
                                            title: 'Data dodania',
                                            dataIndex: 'createdAt',
                                            key: 'createdAt',
                                            width: 150,
                                            render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm')
                                        },
                                        {
                                            title: 'Powiązany wpis finansowy',
                                            key: 'entityId',
                                            render: (_, record) => {
                                                const entry = (allEntriesForMapping || []).find((e: any) => e.id === record.entityId);
                                                if (!entry) {
                                                    return (
                                                        <Badge 
                                                            status="warning" 
                                                            text={
                                                                <Tooltip title="Ten załącznik ma przypisane ID wpisu, który nie został odnaleziony w systemie (np. został usunięty).">
                                                                    <span style={{ color: '#fa8c16', cursor: 'help' }}>
                                                                        ⚠️ Brak powiązania (ID: {record.entityId?.slice(0, 8)}...)
                                                                    </span>
                                                                </Tooltip>
                                                            } 
                                                        />
                                                    );
                                                }
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <Space>
                                                            <span style={{ fontWeight: '500' }}>
                                                                {entry.description || <Text type="secondary">(brak opisu)</Text>}
                                                            </span>
                                                            <Button 
                                                                type="link" 
                                                                size="small" 
                                                                icon={<RightOutlined style={{ fontSize: 10 }} />}
                                                                style={{ padding: 0, height: 'auto', fontSize: 12 }}
                                                                onClick={() => {
                                                                    setSelectedEntry(entry);
                                                                    setIsDetailModalOpen(true);
                                                                }}
                                                            >
                                                                Szczegóły
                                                            </Button>
                                                        </Space>
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                                            Data: {dayjs(entry.date).format('DD.MM.YYYY')} | Kwota: {Number(entry.amountGross).toFixed(2)} PLN
                                                        </Text>
                                                        {entry.contractorName && (
                                                            <Text type="secondary" style={{ fontSize: '11px', fontStyle: 'italic' }}>
                                                                Kontrahent: {entry.contractorName}
                                                            </Text>
                                                        )}
                                                    </div>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Kategoria / Typ wpisu',
                                            key: 'entryDetails',
                                            width: 180,
                                            render: (_, record) => {
                                                const entry = (allEntriesForMapping || []).find((e: any) => e.id === record.entityId);
                                                if (!entry) return '-';
                                                
                                                const isRevenue = entry.type === 'REVENUE';
                                                const isExpense = entry.type === 'EXPENSE';
                                                const typeColor = isRevenue ? 'success' : isExpense ? 'error' : 'processing';
                                                const typeLabel = isRevenue ? 'Przychód' : isExpense ? 'Koszt' : 'Transfer';
                                                
                                                return (
                                                    <Space direction="vertical" size={2} style={{ display: 'flex' }}>
                                                        <Tag color={typeColor} style={{ margin: 0 }}>{typeLabel}</Tag>
                                                        {entry.category && (
                                                            <Tag color="cyan" style={{ margin: 0, fontSize: '11px' }}>
                                                                {getCategoryName(entry.category)}
                                                            </Tag>
                                                        )}
                                                    </Space>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Dodane przez',
                                            dataIndex: 'uploadedBy',
                                            key: 'uploadedBy',
                                            width: 140,
                                            render: (uploader, record) => {
                                                if (uploader?.name) return uploader.name;
                                                const entry = (allEntriesForMapping || []).find((e: any) => e.id === record.entityId);
                                                if (entry?.createdBy?.name) return entry.createdBy.name;
                                                return <Text type="secondary">System</Text>;
                                            }
                                        },
                                        {
                                            title: 'Akcje',
                                            key: 'actions',
                                            align: 'right' as const,
                                            width: 150,
                                            render: (_, record) => {
                                                const downloadUrl = `${filesApi.getDownloadUrl(record.id)}?token=${tokenVal}`;
                                                const previewUrl = `${filesApi.getPreviewUrl(record.id)}?token=${tokenVal}`;
                                                const isPreviewable = isFilePreviewable(record.mimeType);
                                                const entry = (allEntriesForMapping || []).find((e: any) => e.id === record.entityId);
                                                
                                                return (
                                                    <Space size={4}>
                                                        {isPreviewable && (
                                                            <Tooltip title="Podgląd dokumentu">
                                                                <Button 
                                                                    type="text" 
                                                                    size="small" 
                                                                    icon={<EyeOutlined />} 
                                                                    href={previewUrl} 
                                                                    target="_blank"
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        <Tooltip title="Pobierz plik">
                                                            <Button 
                                                                type="text" 
                                                                size="small" 
                                                                icon={<DownloadOutlined />} 
                                                                href={downloadUrl} 
                                                                download
                                                            />
                                                        </Tooltip>
                                                        {entry && (
                                                            <Tooltip title="Przejdź do wpisu finansowego">
                                                                <Button 
                                                                    type="text" 
                                                                    size="small" 
                                                                    icon={<FileTextOutlined style={{ color: token.colorPrimary }} />} 
                                                                    onClick={() => {
                                                                        setSelectedEntry(entry);
                                                                        setIsDetailModalOpen(true);
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        <Popconfirm
                                                            title="Usuń dokument"
                                                            description="Czy na pewno chcesz usunąć ten dokument? Tej operacji nie można cofnąć."
                                                            onConfirm={() => deleteDocumentMutation.mutate(record.id)}
                                                            okText="Usuń"
                                                            cancelText="Anuluj"
                                                            okButtonProps={{ danger: true }}
                                                        >
                                                            <Tooltip title="Usuń dokument">
                                                                <Button 
                                                                    type="text" 
                                                                    size="small" 
                                                                    danger
                                                                    icon={<DeleteOutlined />}
                                                                    loading={deleteDocumentMutation.isPending}
                                                                />
                                                            </Tooltip>
                                                        </Popconfirm>
                                                    </Space>
                                                );
                                            }
                                        }
                                    ]}
                                />
                            </Card>
                        ),
                    },
                    {
                        key: 'forecasting',
                        label: (
                            <Space>
                                <LineChartOutlined />
                                <span>Prognoza Cashflow</span>
                            </Space>
                        ),
                        children: (
                            <div style={{ padding: '4px' }}>
                                <Alert
                                    message="Prognoza Cashflow (Horyzont 6-tygodniowy)"
                                    description="Prognoza zarządcza przedstawiająca przewidywane przepływy gotówki na podstawie terminów płatności należności, zobowiązań oraz kosztów cyklicznych, startując od bieżącego stanu środków."
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: '24px', borderRadius: token.borderRadiusLG }}
                                />

                                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid ${token.colorPrimary}` }}>
                                            {kpi.cashState !== null ? (
                                                <Statistic
                                                    title={<Text type="secondary" style={{ fontSize: '13px' }}><WalletOutlined style={{ marginRight: 6 }} />Stan gotówki na start</Text>}
                                                    value={kpi.cashState}
                                                    precision={2}
                                                    suffix=" PLN"
                                                    valueStyle={{ color: token.colorPrimary, fontWeight: '700' }}
                                                />
                                            ) : (
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: '13px' }}><WalletOutlined style={{ marginRight: 6 }} />Stan gotówki na start</Text>
                                                    <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: '14px' }}>—</div>
                                                </div>
                                            )}
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                {kpi.cashState !== null ? 'Bieżące fizyczne saldo środków' : (
                                                    <span style={{ color: '#fa8c16' }}>Brak danych — dodaj saldo otwarcia</span>
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #52c41a` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><RiseOutlined style={{ marginRight: 6 }} />Przewidywane wpływy</Text>}
                                                value={totalForecastInflow}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#52c41a', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Suma wpływów w horyzoncie 6 tyg.
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid #faad14` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><FallOutlined style={{ marginRight: 6 }} />Przewidywane wypływy</Text>}
                                                value={totalForecastOutflow}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: '#faad14', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                Suma wydatków w horyzoncie 6 tyg.
                                            </div>
                                        </Card>
                                    </Col>
                                    <Col xs={24} sm={12} lg={6}>
                                        <Card bordered style={{ borderLeft: `4px solid ${hasLiquidityRisk ? '#ff4d4f' : '#13c2c2'}` }}>
                                            <Statistic
                                                title={<Text type="secondary" style={{ fontSize: '13px' }}><DollarOutlined style={{ marginRight: 6 }} />Prognozowany koniec</Text>}
                                                value={cashflowForecast[5]?.endCash ?? kpi.cashState}
                                                precision={2}
                                                suffix=" PLN"
                                                valueStyle={{ color: hasLiquidityRisk ? '#ff4d4f' : '#13c2c2', fontWeight: '700' }}
                                            />
                                            <div style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>
                                                {hasLiquidityRisk ? (
                                                    <Badge status="error" text="Ryzyko braku płynności!" />
                                                ) : (
                                                    <Badge status="success" text="Płynność zabezpieczona" />
                                                )}
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                                    <Col xs={24} xl={16}>
                                        <Card title="Harmonogram prognozy przepływów pieniężnych" style={{ borderRadius: token.borderRadiusLG }}>
                                            <Table
                                                dataSource={cashflowForecast}
                                                rowKey="index"
                                                pagination={false}
                                                bordered
                                                onRow={(record) => ({
                                                    onClick: () => setSelectedForecastWeek(record.index),
                                                    style: { 
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedForecastWeek === record.index ? `${token.colorPrimaryBg}` : 'transparent',
                                                    }
                                                })}
                                                columns={[
                                                    {
                                                        title: 'Okres (Tydzień)',
                                                        dataIndex: 'dateRangeStr',
                                                        key: 'dateRangeStr',
                                                        render: (text, record) => (
                                                            <div>
                                                                <Text strong style={selectedForecastWeek === record.index ? { color: token.colorPrimary } : undefined}>{record.label}</Text>
                                                                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{text}</div>
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        title: 'Wpływy (Przychody)',
                                                        key: 'inflows',
                                                        align: 'right' as const,
                                                        render: (_, record) => (
                                                            <div style={{ fontSize: '13px' }}>
                                                                <div style={{ color: '#52c41a' }}>
                                                                    Rzeczywiste: {record.actualInflows.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </div>
                                                                <div style={{ color: '#1890ff' }}>
                                                                    Oczekiwane: {record.expectedInflows.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </div>
                                                                <div style={{ fontWeight: '600', borderTop: '1px solid #f0f0f0', marginTop: 2, paddingTop: 2 }}>
                                                                    Suma: {record.totalInflow.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </div>
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        title: 'Wypływy (Koszty)',
                                                        key: 'outflows',
                                                        align: 'right' as const,
                                                        render: (_, record) => (
                                                            <div style={{ fontSize: '13px' }}>
                                                                <div style={{ color: '#faad14' }}>
                                                                    Rzeczywiste: {record.actualOutflows.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </div>
                                                                <div style={{ color: '#722ed1' }}>
                                                                    Oczekiwane: {record.expectedOutflows.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </div>
                                                                <div style={{ fontWeight: '600', borderTop: '1px solid #f0f0f0', marginTop: 2, paddingTop: 2 }}>
                                                                    Suma: {record.totalOutflow.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </div>
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        title: 'Tygodniowe Netto',
                                                        dataIndex: 'netFlow',
                                                        key: 'netFlow',
                                                        align: 'right' as const,
                                                        render: (val) => {
                                                            const color = val >= 0 ? '#52c41a' : '#ff4d4f';
                                                            const prefix = val >= 0 ? '+' : '';
                                                            return (
                                                                <Text style={{ color, fontWeight: '600', fontSize: '14px' }}>
                                                                    {prefix}{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                </Text>
                                                            );
                                                        }
                                                    },
                                                    {
                                                        title: 'Prognozowane Saldo',
                                                        key: 'endCash',
                                                        align: 'right' as const,
                                                        render: (_, record) => {
                                                            const isNegative = record.endCash < 0;
                                                            return (
                                                                <div>
                                                                    <Text style={{ 
                                                                        color: isNegative ? '#ff4d4f' : '#13c2c2', 
                                                                        fontWeight: '700',
                                                                        fontSize: '15px'
                                                                    }}>
                                                                        {record.endCash.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                                                    </Text>
                                                                    <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                                                        (od startu: {record.startCash.toLocaleString(undefined, { minimumFractionDigits: 2 })})
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    },
                                                    {
                                                        title: 'Akcja',
                                                        key: 'action',
                                                        align: 'center' as const,
                                                        render: (_, record) => (
                                                            <Button 
                                                                type={selectedForecastWeek === record.index ? 'primary' : 'default'} 
                                                                size="small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedForecastWeek(record.index);
                                                                }}
                                                            >
                                                                Szczegóły
                                                            </Button>
                                                        )
                                                    }
                                                ]}
                                            />
                                        </Card>
                                    </Col>

                                    <Col xs={24} xl={8}>
                                        <Card title="Wizualizacja trendu gotówkowego" style={{ borderRadius: token.borderRadiusLG, height: '100%' }}>
                                            <div style={{ width: '100%', height: 350 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={cashflowForecast.map(w => ({
                                                            name: `T${w.index}`,
                                                            'Wpływy': w.totalInflow,
                                                            'Wypływy': w.totalOutflow,
                                                            'Netto': w.netFlow,
                                                            'Saldo': w.endCash,
                                                        }))}
                                                        margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                                                        <XAxis dataKey="name" />
                                                        <YAxis tickFormatter={(val) => `${(val / 1000).toFixed(0)}k`} />
                                                        <ChartTooltip formatter={(val) => `${Number(val).toLocaleString()} PLN`} />
                                                        <Legend />
                                                        <Bar dataKey="Wpływy" fill="#52c41a" radius={[4, 4, 0, 0]} />
                                                        <Bar dataKey="Wypływy" fill="#faad14" radius={[4, 4, 0, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </Card>
                                    </Col>
                                </Row>

                                {selectedForecastWeek && (() => {
                                    const weekData = cashflowForecast.find(w => w.index === selectedForecastWeek);
                                    if (!weekData) return null;

                                    return (
                                        <Card 
                                            title={
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
                                                    <span>
                                                        <FileTextOutlined style={{ marginRight: 8, color: token.colorPrimary }} />
                                                        Pozycje operacyjne okresu: <span style={{ color: token.colorPrimary }}>{weekData.dateRangeStr} ({weekData.label})</span>
                                                    </span>
                                                    <div style={{ fontSize: '13px', fontWeight: 'normal' }}>
                                                        <Space split={<Divider type="vertical" />}>
                                                            <span>Suma wpływów: <Text type="success" strong>{weekData.totalInflow.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</Text></span>
                                                            <span>Suma wypływów: <Text type="warning" strong>{weekData.totalOutflow.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</Text></span>
                                                        </Space>
                                                    </div>
                                                </div>
                                            }
                                            style={{ borderRadius: token.borderRadiusLG }}
                                        >
                                            <Row gutter={[24, 24]}>
                                                <Col xs={24} lg={12}>
                                                    <Table
                                                        title={() => <Text strong style={{ color: '#52c41a' }}><RiseOutlined /> Wpływy (Przychody i należności)</Text>}
                                                        dataSource={weekData.inflowItems}
                                                        rowKey="id"
                                                        bordered
                                                        pagination={{ pageSize: 8 }}
                                                        onRow={(record) => ({
                                                            onClick: () => showDetails(record),
                                                            style: { cursor: 'pointer' }
                                                        })}
                                                        columns={[
                                                            {
                                                                title: 'Status',
                                                                dataIndex: '_forecastType',
                                                                key: 'status',
                                                                width: 120,
                                                                render: (val) => {
                                                                    if (val === 'actual') return <Tag color="success">Odebrane</Tag>;
                                                                    if (val === 'overdue') return <Tag color="error">Zaległe</Tag>;
                                                                    return <Tag color="processing">Planowane</Tag>;
                                                                }
                                                            },
                                                            {
                                                                title: 'Termin płatności',
                                                                key: 'date',
                                                                width: 120,
                                                                render: (_, record) => {
                                                                    const date = record._forecastType === 'actual' ? record.date : (record.dueDate || record.date);
                                                                    return dayjs(date).format('DD.MM.YYYY');
                                                                }
                                                            },
                                                            {
                                                                title: 'Kategoria',
                                                                dataIndex: 'category',
                                                                key: 'category',
                                                                width: 150,
                                                                render: (val) => getCategoryName(val),
                                                            },
                                                            {
                                                                title: 'Klient / Opis',
                                                                key: 'desc',
                                                                render: (_, record) => (
                                                                    <div>
                                                                        <div style={{ fontWeight: '500' }}>{record.description}</div>
                                                                        {record.contractorName && <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{record.contractorName}</div>}
                                                                    </div>
                                                                )
                                                            },
                                                            {
                                                                title: 'Kwota do spłaty',
                                                                key: 'amount',
                                                                align: 'right' as const,
                                                                width: 130,
                                                                render: (_, record) => {
                                                                    const amount = record._forecastType === 'actual' 
                                                                        ? (Number(record.amountPaid) || Number(record.amountGross))
                                                                        : (Number(record.amountGross) - (Number(record.amountPaid) || 0));
                                                                    return <Text strong>{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</Text>;
                                                                }
                                                            },
                                                        ]}
                                                    />
                                                </Col>

                                                <Col xs={24} lg={12}>
                                                    <Table
                                                        title={() => <Text strong style={{ color: '#faad14' }}><FallOutlined /> Wypływy (Koszty i zobowiązania)</Text>}
                                                        dataSource={weekData.outflowItems}
                                                        rowKey="id"
                                                        bordered
                                                        pagination={{ pageSize: 8 }}
                                                        onRow={(record) => ({
                                                            onClick: () => showDetails(record),
                                                            style: { cursor: 'pointer' }
                                                        })}
                                                        columns={[
                                                            {
                                                                title: 'Status',
                                                                dataIndex: '_forecastType',
                                                                key: 'status',
                                                                width: 120,
                                                                render: (val) => {
                                                                    if (val === 'actual') return <Tag color="success">Opłacone</Tag>;
                                                                    if (val === 'overdue') return <Tag color="error">Zaległe</Tag>;
                                                                    return <Tag color="processing">Planowane</Tag>;
                                                                }
                                                            },
                                                            {
                                                                title: 'Termin płatności',
                                                                key: 'date',
                                                                width: 120,
                                                                render: (_, record) => {
                                                                    const date = record._forecastType === 'actual' ? record.date : (record.dueDate || record.date);
                                                                    return dayjs(date).format('DD.MM.YYYY');
                                                                }
                                                            },
                                                            {
                                                                title: 'Kategoria',
                                                                dataIndex: 'category',
                                                                key: 'category',
                                                                width: 150,
                                                                render: (val) => getCategoryName(val),
                                                            },
                                                            {
                                                                title: 'Dostawca / Opis',
                                                                key: 'desc',
                                                                render: (_, record) => (
                                                                    <div>
                                                                        <div style={{ fontWeight: '500' }}>{record.description}</div>
                                                                        {record.contractorName && <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{record.contractorName}</div>}
                                                                    </div>
                                                                )
                                                            },
                                                            {
                                                                title: 'Kwota do spłaty',
                                                                key: 'amount',
                                                                align: 'right' as const,
                                                                width: 130,
                                                                render: (_, record) => {
                                                                    const amount = record._forecastType === 'actual' 
                                                                        ? (Number(record.amountPaid) || Number(record.amountGross))
                                                                        : (Number(record.amountGross) - (Number(record.amountPaid) || 0));
                                                                    return <Text strong>{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</Text>;
                                                                }
                                                            },
                                                        ]}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    );
                                })()}
                            </div>
                        ),
                    },
                ]}
            />

            {/* CENTRAL DETAIL VIEW MODAL */}
            <Modal
                title={
                    <Space>
                        <FileTextOutlined style={{ color: token.colorPrimary }} />
                        <span style={{ fontWeight: '600' }}>Szczegóły wpisu finansowego</span>
                    </Space>
                }
                open={isDetailModalOpen}
                onCancel={() => setIsDetailModalOpen(false)}
                width={850}
                footer={[
                    <Button key="close" onClick={() => setIsDetailModalOpen(false)}>Zamknij</Button>,
                    ...(selectedEntry?.isSynthetic ? [] : [
                        <Button key="edit" type="primary" ghost icon={<EditOutlined />} onClick={() => {
                            setIsDetailModalOpen(false);
                            startEdit(selectedEntry);
                        }}>Edytuj wpis</Button>
                    ])
                ]}
            >
                {selectedEntry && (
                    <div style={{ marginTop: 12 }}>
                        {/* Overdue alert inside modal */}
                        {selectedEntry.status !== 'COMPLETED' && getDelayDays(selectedEntry.dueDate) > 0 && (
                            <Alert
                                message={
                                    <span style={{ fontWeight: 'bold' }}>
                                        ⚠️ Zaległość płatnicza: Pozycja przeterminowana o {getDelayDays(selectedEntry.dueDate)} {getDelayDays(selectedEntry.dueDate) === 1 ? 'dzień' : 'dni'}!
                                    </span>
                                }
                                description={`Termin płatności minął: ${dayjs(selectedEntry.dueDate).format('DD.MM.YYYY')}. Pozostało do spłaty: ${(Number(selectedEntry.amountGross) - (Number(selectedEntry.amountPaid) || 0)).toFixed(2)} PLN.`}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <Row gutter={[24, 24]}>
                            {/* Left side: details */}
                            <Col xs={24} md={12}>
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <Card size="small" title="Informacje podstawowe">
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <tbody>
                                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Typ wpisu</td>
                                                    <td style={{ padding: '8px 0', textAlign: 'right' }}>
                                                        {selectedEntry.type === 'REVENUE' ? <Tag color="success">Przychód</Tag> : selectedEntry.type === 'EXPENSE' ? <Tag color="error">Koszt</Tag> : selectedEntry.type === 'OPENING_BALANCE' ? <Tag color="purple">Saldo otwarcia</Tag> : selectedEntry.type === 'BALANCE_CORRECTION' ? <Tag color="volcano">Korekta salda</Tag> : <Tag color="processing">Transfer</Tag>}
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Klasyfikacja</td>
                                                    <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500' }}>
                                                        {selectedEntry.classification === 'ACCRUAL' ? 'Wynikowe (memoriał)' : selectedEntry.classification === 'CASH' ? 'Gotówkowe (kasa)' : 'Transfer / Systemowe'}
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Data zdarzenia</td>
                                                    <td style={{ padding: '8px 0', textAlign: 'right' }}>
                                                        {dayjs(selectedEntry.date).format('DD.MM.YYYY')}
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Termin płatności</td>
                                                    <td style={{ padding: '8px 0', textAlign: 'right' }}>
                                                        {selectedEntry.dueDate ? dayjs(selectedEntry.dueDate).format('DD.MM.YYYY') : '-'}
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Kategoria</td>
                                                    <td style={{ padding: '8px 0', textAlign: 'right' }}>
                                                        <Tag color="cyan">{getCategoryName(selectedEntry.category)}</Tag>
                                                    </td>
                                                </tr>
                                                <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Kontrahent</td>
                                                    <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500' }}>
                                                        {selectedEntry.contractorName || <Text type="secondary">Brak</Text>}
                                                    </td>
                                                </tr>
                                                {selectedEntry.channelId && (
                                                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                        <td style={{ padding: '8px 0', color: '#8c8c8c' }}>Kanał sprzedaży</td>
                                                        <td style={{ padding: '8px 0', textAlign: 'right' }}>
                                                            {channels?.find(ch => ch.id === selectedEntry.channelId)?.name || 'Nieznany'}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>

                                        <div style={{ marginTop: 12 }}>
                                            <Text type="secondary">Opis:</Text>
                                            <div style={{ padding: '8px 12px', background: token.colorFillAlter, borderRadius: token.borderRadiusSM, marginTop: 4 }}>
                                                {selectedEntry.description}
                                            </div>
                                        </div>

                                        {selectedEntry.productIds && selectedEntry.productIds.length > 0 && (
                                            <div style={{ marginTop: 12 }}>
                                                <Text type="secondary">Powiązane produkty:</Text>
                                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                                                    {selectedEntry.productIds.map((pId: string) => {
                                                        const prod = products?.find(p => p.id === pId);
                                                        return (
                                                            <Tag key={pId} color="blue">
                                                                {prod ? `${prod.name} (${prod.sku})` : pId}
                                                            </Tag>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </Card>

                                    {entryFiles && entryFiles.length > 0 && (
                                        <Card size="small" title="Załączniki (Pliki / Dokumenty)">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                {entryFiles.map((file: any) => {
                                                    const downloadUrl = `${filesApi.getDownloadUrl(file.id)}?token=${tokenVal}`;
                                                    const previewUrl = `${filesApi.getPreviewUrl(file.id)}?token=${tokenVal}`;
                                                    const isPreviewable = isFilePreviewable(file.mimeType);
                                                    return (
                                                        <div 
                                                            key={file.id} 
                                                            style={{ 
                                                                display: 'flex', 
                                                                justifyContent: 'space-between', 
                                                                alignItems: 'center', 
                                                                padding: '6px 12px', 
                                                                background: token.colorFillAlter, 
                                                                borderRadius: token.borderRadiusSM,
                                                                border: `1px solid ${token.colorBorderSecondary}`
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                                                <PaperClipOutlined style={{ color: token.colorPrimary, flexShrink: 0 }} />
                                                                <Text ellipsis style={{ fontSize: 13 }} title={file.originalName}>
                                                                    {file.originalName}
                                                                </Text>
                                                            </div>
                                                            <Space size={4} style={{ flexShrink: 0 }}>
                                                                {isPreviewable && (
                                                                    <Button 
                                                                        type="link" 
                                                                        size="small" 
                                                                        icon={<EyeOutlined />} 
                                                                        href={previewUrl} 
                                                                        target="_blank"
                                                                    >
                                                                        Podgląd
                                                                    </Button>
                                                                )}
                                                                <Button 
                                                                    type="link" 
                                                                    size="small" 
                                                                    icon={<DownloadOutlined />} 
                                                                    href={downloadUrl} 
                                                                    download
                                                                >
                                                                    Pobierz
                                                                </Button>
                                                            </Space>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </Card>
                                    )}
                                </Space>
                            </Col>

                            {/* Right side: finance & payment history */}
                            <Col xs={24} md={12}>
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    {/* Financial Amounts summary */}
                                    <Card size="small" title="Podsumowanie finansowe">
                                        <Row gutter={8}>
                                            <Col span={8}>
                                                <Statistic title="Netto" value={selectedEntry.amountNet} precision={2} suffix=" PLN" valueStyle={{ fontSize: '15px' }} />
                                            </Col>
                                            <Col span={8}>
                                                <Statistic title="VAT" value={selectedEntry.amountVat} precision={2} suffix=" PLN" valueStyle={{ fontSize: '15px' }} />
                                            </Col>
                                            <Col span={8}>
                                                <Statistic title="Brutto" value={selectedEntry.amountGross} precision={2} suffix=" PLN" valueStyle={{ fontSize: '16px', fontWeight: 'bold' }} />
                                            </Col>
                                        </Row>

                                        <Divider style={{ margin: '12px 0' }} />

                                        <div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                <Text type="secondary">Rozliczenie (Opłacono):</Text>
                                                <Text style={{ fontWeight: '600' }}>
                                                    {(Number(selectedEntry.amountPaid) || 0).toFixed(2)} / {Number(selectedEntry.amountGross).toFixed(2)} PLN
                                                </Text>
                                            </div>
                                            <Progress
                                                percent={Number(((Number(selectedEntry.amountPaid) || 0) / Number(selectedEntry.amountGross) * 100).toFixed(1))}
                                                status={selectedEntry.status === 'COMPLETED' ? 'success' : 'active'}
                                            />
                                        </div>
                                    </Card>

                                    {/* Settlement / Register Payment Section */}
                                    {!selectedEntry.isSynthetic && selectedEntry.status !== 'COMPLETED' && (
                                        <Card size="small" title="Dodaj nową spłatę (Rozliczenie częściowe)" bordered={false} style={{ background: token.colorFillAlter }}>
                                            <Form form={paymentForm} onFinish={handleRegisterPayment} layout="vertical">
                                                <Row gutter={8}>
                                                    <Col span={10}>
                                                        <Form.Item name="amount" label="Kwota" rules={[{ required: true, message: 'Podaj kwotę' }]}>
                                                            <InputNumber style={{ width: '100%' }} precision={2} min={0.01} max={Number(selectedEntry.amountGross) - (Number(selectedEntry.amountPaid) || 0)} suffix="PLN" />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col span={14}>
                                                        <Form.Item name="date" label="Data" rules={[{ required: true, message: 'Podaj datę' }]}>
                                                            <DatePicker style={{ width: '100%' }} />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                                <Form.Item name="description" label="Notatka / Sposób płatności" style={{ marginBottom: 12 }}>
                                                    <Input placeholder="np. przelew bankowy, kasa 1" />
                                                </Form.Item>
                                                <Button type="primary" htmlType="submit" block icon={<CheckCircleOutlined />} loading={registerPaymentMutation.isPending}>
                                                    Zarejestruj spłatę
                                                </Button>
                                            </Form>
                                        </Card>
                                    )}

                                    {/* Payment History List */}
                                    <Card size="small" title="Historia spłat / wpłat">
                                        {selectedEntry.paymentHistory && selectedEntry.paymentHistory.length > 0 ? (
                                            <Table
                                                size="small"
                                                pagination={false}
                                                dataSource={selectedEntry.paymentHistory}
                                                rowKey={(row, idx) => idx.toString()}
                                                columns={[
                                                    { title: 'Data', dataIndex: 'date', key: 'date', render: (d) => dayjs(d).format('DD.MM.YYYY') },
                                                    { title: 'Kwota', dataIndex: 'amount', key: 'amount', align: 'right' as const, render: (val) => `${Number(val).toFixed(2)} PLN` },
                                                    { title: 'Opis', dataIndex: 'description', key: 'description' },
                                                ]}
                                            />
                                        ) : (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak historii spłat." style={{ padding: '8px 0' }} />
                                        )}
                                    </Card>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal>

            {/* MONTHLY DRILL-DOWN DETAILS MODAL */}
            <Modal
                title={
                    <Space>
                        <CalendarOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontWeight: '600' }}>Szczegóły finansowe: {selectedMonthDetails?.monthName}</span>
                    </Space>
                }
                open={isMonthModalOpen}
                onCancel={() => setIsMonthModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsMonthModalOpen(false)}>
                        Zamknij
                    </Button>
                ]}
                width={700}
                centered
            >
                {selectedMonthDetails && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                        <Alert 
                            message={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <span>Tryb raportowania: <strong>{reportMode === 'accrual' ? 'Wynikowo (Memoriał)' : 'Gotówkowo (Kasa)'}</strong></span>
                                    <span style={{ fontSize: '11px', color: '#8c8c8c' }}>Klucz: {selectedMonthDetails.monthKey}</span>
                                </div>
                            } 
                            type="info" 
                            showIcon 
                        />
                        
                        <Row gutter={16}>
                            <Col span={12}>
                                <Card size="small" title="Podsumowanie finansowe">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Przychody:</Text>
                                            <Text strong>{selectedMonthDetails.przychody.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</Text>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Koszty:</Text>
                                            <Text strong>{selectedMonthDetails.koszty.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</Text>
                                        </div>
                                        <Divider style={{ margin: '4px 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text strong>Wynik finansowy:</Text>
                                            <Text strong style={{ color: selectedMonthDetails.wynik >= 0 ? 'green' : 'red' }}>
                                                {selectedMonthDetails.wynik.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                            </Text>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Text type="secondary">Skumulowana gotówka:</Text>
                                            <Text strong style={{ color: selectedMonthDetails.cashPosition >= 0 ? '#1890ff' : 'red' }}>
                                                {selectedMonthDetails.cashPosition.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN
                                            </Text>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                            
                            <Col span={12}>
                                <Card size="small" title="Pozycje operacyjne">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">Płatności po terminie:</Text>
                                            <Tag color={selectedMonthDetails.overdueCount > 0 ? 'error' : 'success'}>
                                                {selectedMonthDetails.overdueCount}
                                            </Tag>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">Otwarte zobowiązania:</Text>
                                            <Tag color="warning">{selectedMonthDetails.openPayables}</Tag>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">Otwarte należności:</Text>
                                            <Tag color="processing">{selectedMonthDetails.openReceivables}</Tag>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>

                        <Card size="small" title="Podział przychodów ze sprzedaży według kanałów">
                            {Object.keys(selectedMonthDetails.salesByChannel).length === 0 ? (
                                <Empty description="Brak zarejestrowanych przychodów sprzedażowych w tym miesiącu" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {Object.entries(selectedMonthDetails.salesByChannel).map(([channel, amount]: [string, any]) => {
                                        const percentage = selectedMonthDetails.przychody > 0 
                                            ? (amount / selectedMonthDetails.przychody) * 100 
                                            : 0;
                                        return (
                                            <div key={channel} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Space>
                                                        <ShoppingOutlined style={{ color: '#1890ff' }} />
                                                        <Text strong>{channel}</Text>
                                                    </Space>
                                                    <Text strong>{amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN ({percentage.toFixed(1)}%)</Text>
                                                </div>
                                                <Progress percent={parseFloat(percentage.toFixed(1))} strokeColor="#1890ff" status="active" />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card>
                    </div>
                )}
            </Modal>

            {/* PLAN VS EXECUTION DRILL-DOWN MODAL */}
            <Modal
                title={
                    <Space>
                        <LineChartOutlined style={{ color: '#1890ff' }} />
                        <span style={{ fontWeight: '600' }}>
                            Analiza Plan vs Wykonanie: {selectedMonthForDrillDown?.monthName}
                        </span>
                    </Space>
                }
                open={isDrillDownModalOpen}
                onCancel={() => setIsDrillDownModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsDrillDownModalOpen(false)}>
                        Zamknij
                    </Button>
                ]}
                width={900}
                centered
            >
                {selectedMonthForDrillDown && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                            <Alert 
                                message={
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                        <span>Tryb kalkulacji kosztów: <strong>{reportMode === 'accrual' ? 'Wynikowo (Netto)' : 'Gotówkowo (Brutto)'}</strong></span>
                                        <span style={{ fontSize: '11px', color: '#8c8c8c' }}>Miesiąc: {selectedMonthForDrillDown.monthKey}</span>
                                    </div>
                                } 
                                type="info" 
                                showIcon 
                                style={{ flex: 1, margin: 0 }}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: '13px', color: '#595959' }}>Filtr kategorii:</span>
                                <Select
                                    allowClear
                                    placeholder="Wszystkie kategorie"
                                    style={{ width: 200 }}
                                    value={modalCategoryFilter}
                                    onChange={(val) => setModalCategoryFilter(val)}
                                >
                                    {expenseCategories.map((cat: string) => (
                                        <Select.Option key={cat} value={cat}>{cat}</Select.Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        {/* Top level KPIs */}
                        <Row gutter={16}>
                            <Col span={6}>
                                <Card size="small" style={{ background: token.colorFillAlter, borderRadius: 8, textAlign: 'center', border: `1px solid ${token.colorBorderSecondary}` }}>
                                    <Statistic 
                                        title={<span style={{ fontSize: '12px', color: token.colorTextDescription }}>Plan kosztów</span>}
                                        value={modalKPIs.plan} 
                                        precision={2} 
                                        suffix=" PLN" 
                                        valueStyle={{ color: '#1890ff', fontSize: '16px', fontWeight: 'bold' }} 
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ background: token.colorFillAlter, borderRadius: 8, textAlign: 'center', border: `1px solid ${token.colorBorderSecondary}` }}>
                                    <Statistic 
                                        title={<span style={{ fontSize: '12px', color: token.colorTextDescription }}>Realizacja (Wykonanie)</span>}
                                        value={modalKPIs.wykonanie} 
                                        precision={2} 
                                        suffix=" PLN" 
                                        valueStyle={{ color: token.colorText, fontSize: '16px', fontWeight: 'bold' }} 
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ 
                                    background: modalKPIs.variance > 0 ? token.colorErrorBg : (modalKPIs.variance < 0 ? token.colorSuccessBg : token.colorFillAlter), 
                                    borderRadius: 8, 
                                    border: `1px solid ${modalKPIs.variance > 0 ? token.colorErrorBorder : (modalKPIs.variance < 0 ? token.colorSuccessBorder : token.colorBorderSecondary)}`,
                                    textAlign: 'center' 
                                }}>
                                    <Statistic 
                                        title={<span style={{ fontSize: '12px', color: token.colorTextDescription }}>Odchylenie (Wariancja)</span>}
                                        value={modalKPIs.variance} 
                                        precision={2} 
                                        prefix={modalKPIs.variance > 0 ? '+' : ''}
                                        suffix=" PLN" 
                                        valueStyle={{ color: modalKPIs.variance > 0 ? '#ff4d4f' : (modalKPIs.variance < 0 ? '#52c41a' : token.colorTextDescription), fontSize: '16px', fontWeight: 'bold' }} 
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card size="small" style={{ 
                                    background: modalKPIs.variance > 0 ? token.colorErrorBg : (modalKPIs.variance < 0 ? token.colorSuccessBg : token.colorFillAlter), 
                                    borderRadius: 8, 
                                    border: `1px solid ${modalKPIs.variance > 0 ? token.colorErrorBorder : (modalKPIs.variance < 0 ? token.colorSuccessBorder : token.colorBorderSecondary)}`,
                                    textAlign: 'center' 
                                }}>
                                    <Statistic 
                                        title={<span style={{ fontSize: '12px', color: token.colorTextDescription }}>Procent odchylenia</span>}
                                        value={Math.abs(modalKPIs.variancePercent)} 
                                        precision={1} 
                                        prefix={modalKPIs.variance > 0 ? '+' : (modalKPIs.variance < 0 ? '-' : '')}
                                        suffix={modalKPIs.plan === 0 && modalKPIs.wykonanie > 0 ? '% (brak planu)' : '%'}
                                        valueStyle={{ color: modalKPIs.variance > 0 ? '#ff4d4f' : (modalKPIs.variance < 0 ? '#52c41a' : token.colorTextDescription), fontSize: '16px', fontWeight: 'bold' }} 
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Tabs defaultActiveKey="categories" style={{ marginTop: 8 }}>
                            <Tabs.TabPane tab="Odchylenia kategorii" key="categories">
                                <Table
                                    size="small"
                                    dataSource={categoryBreakdownData}
                                    rowKey="category"
                                    pagination={false}
                                    bordered
                                    columns={[
                                        {
                                            title: 'Kategoria',
                                            dataIndex: 'category',
                                            key: 'category',
                                            render: (cat: string) => <Tag color="cyan">{getCategoryName(cat)}</Tag>
                                        },
                                        {
                                            title: 'Plan kosztów',
                                            dataIndex: 'plan',
                                            key: 'plan',
                                            align: 'right' as const,
                                            render: (val: number) => `${val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN`
                                        },
                                        {
                                            title: 'Wykonanie',
                                            dataIndex: 'wykonanie',
                                            key: 'wykonanie',
                                            align: 'right' as const,
                                            render: (val: number) => <span style={{ fontWeight: '500' }}>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</span>
                                        },
                                        {
                                            title: 'Odchylenie (PLN)',
                                            dataIndex: 'variance',
                                            key: 'variance',
                                            align: 'right' as const,
                                            render: (val: number) => {
                                                const color = val > 0 ? '#ff4d4f' : (val < 0 ? '#52c41a' : '#595959');
                                                const sign = val > 0 ? '+' : '';
                                                return <span style={{ color, fontWeight: '500' }}>{sign}{val.toLocaleString(undefined, { minimumFractionDigits: 2 })} PLN</span>;
                                            }
                                        },
                                        {
                                            title: 'Odchylenie (%)',
                                            dataIndex: 'variancePercent',
                                            key: 'variancePercent',
                                            align: 'right' as const,
                                            render: (val: number, record: any) => {
                                                if (record.plan === 0 && record.wykonanie === 0) return <span style={{ color: '#8c8c8c' }}>-</span>;
                                                if (record.plan === 0 && record.wykonanie > 0) return <span style={{ color: '#ff4d4f', fontWeight: '500' }}>+100.0% (brak planu)</span>;
                                                const color = record.variance > 0 ? '#ff4d4f' : (record.variance < 0 ? '#52c41a' : '#8c8c8c');
                                                const sign = record.variance > 0 ? '+' : '';
                                                return <span style={{ color, fontWeight: '500' }}>{sign}{val.toFixed(1)}%</span>;
                                            }
                                        },
                                        {
                                            title: 'Status',
                                            dataIndex: 'status',
                                            key: 'status',
                                            align: 'center' as const,
                                            render: (status: string) => {
                                                if (status === 'exceeded') return <Tag color="error">Przekroczone</Tag>;
                                                if (status === 'below') return <Tag color="success">Poniżej planu</Tag>;
                                                if (status === 'normal') return <Tag color="processing">W normie</Tag>;
                                                return <Tag color="default">Brak planu</Tag>;
                                            }
                                        }
                                    ]}
                                />
                            </Tabs.TabPane>

                            <Tabs.TabPane tab={`Koszty zaplanowane (${filteredPlannedEntries.length})`} key="planned">
                                <Table
                                    size="small"
                                    dataSource={filteredPlannedEntries}
                                    rowKey="id"
                                    pagination={{ pageSize: 5 }}
                                    bordered
                                    columns={[
                                        {
                                            title: 'Data',
                                            dataIndex: 'date',
                                            key: 'date',
                                            render: (d) => dayjs(d).format('DD.MM.YYYY')
                                        },
                                        {
                                            title: 'Kategoria',
                                            dataIndex: 'category',
                                            key: 'category',
                                            render: (val) => <Tag color="cyan">{getCategoryName(val)}</Tag>
                                        },
                                        {
                                            title: 'Opis',
                                            dataIndex: 'description',
                                            key: 'description',
                                            ellipsis: true
                                        },
                                        {
                                            title: 'Dostawca',
                                            dataIndex: 'contractorName',
                                            key: 'contractorName',
                                            render: (name) => name || <Text type="secondary">-</Text>
                                        },
                                        {
                                            title: 'Netto',
                                            dataIndex: 'amountNet',
                                            key: 'amountNet',
                                            align: 'right' as const,
                                            render: (val) => `${Number(val).toFixed(2)} PLN`
                                        },
                                        {
                                            title: 'Brutto',
                                            dataIndex: 'amountGross',
                                            key: 'amountGross',
                                            align: 'right' as const,
                                            render: (val) => <span style={{ fontWeight: '500' }}>{Number(val).toFixed(2)} PLN</span>
                                        }
                                    ]}
                                />
                            </Tabs.TabPane>

                            <Tabs.TabPane tab={`Koszty rzeczywiste (${filteredActualEntries.length})`} key="actual">
                                <Table
                                    size="small"
                                    dataSource={filteredActualEntries}
                                    rowKey="id"
                                    pagination={{ pageSize: 5 }}
                                    bordered
                                    columns={[
                                        {
                                            title: 'Data',
                                            dataIndex: 'date',
                                            key: 'date',
                                            render: (d) => dayjs(d).format('DD.MM.YYYY')
                                        },
                                        {
                                            title: 'Kategoria',
                                            dataIndex: 'category',
                                            key: 'category',
                                            render: (val) => <Tag color="cyan">{getCategoryName(val)}</Tag>
                                        },
                                        {
                                            title: 'Opis',
                                            dataIndex: 'description',
                                            key: 'description',
                                            ellipsis: true
                                        },
                                        {
                                            title: 'Dostawca',
                                            dataIndex: 'contractorName',
                                            key: 'contractorName',
                                            render: (name) => name || <Text type="secondary">-</Text>
                                        },
                                        {
                                            title: 'Netto',
                                            dataIndex: 'amountNet',
                                            key: 'amountNet',
                                            align: 'right' as const,
                                            render: (val) => `${Number(val).toFixed(2)} PLN`
                                        },
                                        {
                                            title: 'Brutto',
                                            dataIndex: 'amountGross',
                                            key: 'amountGross',
                                            align: 'right' as const,
                                            render: (val) => <span style={{ fontWeight: '500' }}>{Number(val).toFixed(2)} PLN</span>
                                        },
                                        {
                                            title: 'Status',
                                            dataIndex: 'status',
                                            key: 'status',
                                            render: (status, record) => {
                                                if (status === 'COMPLETED') return <Badge status="success" text="Zrealizowane" />;
                                                if (status === 'OVERDUE' || (status === 'PENDING' && record.dueDate && new Date(record.dueDate) < new Date())) {
                                                    return <Badge status="error" text="Po terminie" />;
                                                }
                                                return <Badge status="warning" text="Oczekujące" />;
                                            }
                                        }
                                    ]}
                                />
                            </Tabs.TabPane>
                        </Tabs>
                    </div>
                )}
            </Modal>

            {/* CENTRAL CREATE & EDIT FORM MODAL (Drawer refactored to Modal) */}
            <Modal
                title={<span>{editingEntryId ? 'Edytuj wpis finansowy' : 'Nowy wpis finansowy'}</span>}
                width={650}
                onCancel={() => setIsFormOpen(false)}
                open={isFormOpen}
                onOk={handleFormSubmit}
                okText="Zapisz"
                cancelText="Anuluj"
                confirmLoading={createMutation.isPending || updateMutation.isPending}
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Alert
                        message={<span><strong>Tryb wynikowy / gotówkowy</strong> — to sposób interpretacji danych w raportach, nie ręczna klasyfikacja wpisu. Wpis wynikowy bazuje na dacie zdarzenia; gotówkowy — na dacie rzeczywistej płatności.</span>}
                        type="info"
                        showIcon
                        style={{ marginBottom: 12, fontSize: '12px' }}
                    />
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="type"
                                label="Typ"
                                rules={[{ required: true, message: 'Wybierz typ wpisu' }]}
                            >
                                <Select>
                                    <Select.Option value="REVENUE">Przychód</Select.Option>
                                    <Select.Option value="EXPENSE">Koszt</Select.Option>
                                    <Select.Option value="TRANSFER">Transfer</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{ required: true, message: 'Wybierz status' }]}
                            >
                                <Select>
                                    <Select.Option value="PLANNED">Planowane</Select.Option>
                                    <Select.Option value="PENDING">Oczekujące</Select.Option>
                                    <Select.Option value="COMPLETED">Zrealizowane</Select.Option>
                                    <Select.Option value="OVERDUE">Po terminie</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="category"
                                label="Kategoria"
                                rules={[{ required: true, message: 'Podaj kategorię' }]}
                            >
                                <Select showSearch mode="tags" maxCount={1} placeholder="Wybierz lub wpisz nową...">
                                    {formCategories.map((cat: any) => (
                                        <Select.Option key={cat.code} value={cat.code}>{cat.name}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="date"
                                label="Data zdarzenia"
                                rules={[{ required: true, message: 'Wybierz datę' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="dueDate" label="Termin płatności">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="contractorName" label="Kontrahent (np. dostawca, odbiorca)">
                                <AutoComplete
                                    options={usedContractors.map(name => ({ value: name }))}
                                    placeholder="Wpisz lub wybierz nazwę dostawcy, odbiorcy..."
                                    filterOption={(inputValue, option) =>
                                        option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                    }
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '12px 0' }} />
                    <Alert
                        message="Wpisz dokładnie 2 z 3 wartości (Netto, VAT lub Brutto), a brakująca kwota zostanie obliczona automatycznie."
                        type="info"
                        showIcon
                        style={{ marginBottom: 12, fontSize: '13px' }}
                    />

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Kwota Netto">
                                <InputNumber
                                    value={amountNet}
                                    onChange={v => handleAmountChange('net', v)}
                                    formatter={value => `${value} PLN`}
                                    parser={value => value!.replace(' PLN', '')}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Stawka VAT">
                                <Select
                                    value={vatRate}
                                    onChange={handleVatRateChange}
                                    style={{ width: '100%' }}
                                    options={[
                                        { label: '23%', value: 23 },
                                        { label: '8%', value: 8 },
                                        { label: '5%', value: 5 },
                                        { label: '0%', value: 0 },
                                        { label: 'Zwolniony (Manual)', value: null },
                                    ]}
                                />
                                {amountVat !== null && (
                                    <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                                        Kwota VAT: {amountVat.toFixed(2)} PLN
                                    </div>
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Kwota Brutto">
                                <InputNumber
                                    value={amountGross}
                                    onChange={v => handleAmountChange('gross', v)}
                                    formatter={value => `${value} PLN`}
                                    parser={value => value!.replace(' PLN', '')}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="amountPaid" label="Aktualnie opłacona kwota (Zaliczka / Spłata)">
                                <InputNumber
                                    formatter={value => `${value} PLN`}
                                    parser={value => value!.replace(' PLN', '')}
                                    style={{ width: '100%' }}
                                    min={0}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label="Opis"
                        rules={[{ required: true, message: 'Podaj krótki opis' }]}
                    >
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Divider>Załączniki (Faktury / Dokumenty)</Divider>
                    <Form.Item>
                        <Upload.Dragger
                            multiple
                            fileList={uploadedFiles}
                            onChange={(info) => {
                                setUploadedFiles(info.fileList);
                            }}
                            beforeUpload={() => false}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">Kliknij lub przeciągnij pliki do tego obszaru</p>
                            <p className="ant-upload-hint">Dodaj skany faktur, paragony lub inne dokumenty potwierdzające wpis.</p>
                        </Upload.Dragger>
                    </Form.Item>
                </Form>
            </Modal>

            {/* RECURRING COST TEMPLATES MODAL */}
            <Modal
                title={<span>{editingTemplate ? 'Edytuj szablon kosztu cyklicznego' : 'Nowy szablon kosztu cyklicznego'}</span>}
                width={650}
                onCancel={() => setIsTemplateModalOpen(false)}
                open={isTemplateModalOpen}
                onOk={handleTemplateFormSubmit}
                okText="Zapisz"
                cancelText="Anuluj"
                confirmLoading={createTemplateMutation.isPending || updateTemplateMutation.isPending}
            >
                <Form form={templateForm} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="name"
                        label="Nazwa szablonu (np. Księgowość, Abonament, Serwer)"
                        rules={[{ required: true, message: 'Podaj nazwę szablonu' }]}
                    >
                        <Input placeholder="np. Abonament za serwer VPS, Usługi księgowe..." />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="contractorName"
                                label="Dostawca / kontrahent"
                                rules={[{ required: true, message: 'Wpisz lub wybierz nazwę dostawcy / kontrahenta' }]}
                            >
                                <AutoComplete
                                    options={usedContractors.map(name => ({ value: name }))}
                                    placeholder="Wpisz nazwę dostawcy lub wybierz z listy..."
                                    filterOption={(inputValue, option) =>
                                        option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label="Kategoria"
                                rules={[{ required: true, message: 'Podaj kategorię' }]}
                            >
                                <Select showSearch mode="tags" maxCount={1} placeholder="Wybierz lub wpisz nową...">
                                    {expenseCategories.map((cat: any) => (
                                        <Select.Option key={cat.code} value={cat.code}>{cat.name}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="frequency"
                                label="Częstotliwość"
                                rules={[{ required: true, message: 'Wybierz częstotliwość' }]}
                            >
                                <Select>
                                    <Select.Option value="MONTHLY">Miesięcznie</Select.Option>
                                    <Select.Option value="QUARTERLY">Kwartalnie</Select.Option>
                                    <Select.Option value="YEARLY">Rocznie</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="billingDay"
                                label="Termin płatności (dzień miesiąca)"
                                rules={[{ required: true, message: 'Podaj dzień płatności' }]}
                            >
                                <InputNumber min={1} max={31} style={{ width: '100%' }} placeholder="np. 10" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="startDate"
                                label="Data startu cyklu"
                                rules={[{ required: true, message: 'Wybierz datę rozpoczęcia' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="endDate"
                                label="Data zakończenia cyklu (opcjonalnie)"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{ margin: '12px 0' }} />
                    <Alert
                        message="Wpisz dokładnie 2 z 3 wartości (Netto, VAT lub Brutto), a brakująca kwota zostanie obliczona automatycznie."
                        type="info"
                        showIcon
                        style={{ marginBottom: 12, fontSize: '13px' }}
                    />

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item label="Kwota Netto">
                                <InputNumber
                                    value={amountNet}
                                    onChange={v => handleAmountChange('net', v, templateForm)}
                                    formatter={value => `${value} PLN`}
                                    parser={value => value!.replace(' PLN', '')}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Stawka VAT">
                                <Select
                                    value={vatRate}
                                    onChange={v => handleVatRateChange(v, templateForm)}
                                    style={{ width: '100%' }}
                                    options={[
                                        { label: '23%', value: 23 },
                                        { label: '8%', value: 8 },
                                        { label: '5%', value: 5 },
                                        { label: '0%', value: 0 },
                                        { label: 'Zwolniony (Manual)', value: null },
                                    ]}
                                />
                                {amountVat !== null && (
                                    <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
                                        Kwota VAT: {amountVat.toFixed(2)} PLN
                                    </div>
                                )}
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Kwota Brutto">
                                <InputNumber
                                    value={amountGross}
                                    onChange={v => handleAmountChange('gross', v, templateForm)}
                                    formatter={value => `${value} PLN`}
                                    parser={value => value!.replace(' PLN', '')}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="isActive" valuePropName="checked" label="Status szablonu">
                                <Switch checkedChildren="Aktywny" unCheckedChildren="Nieaktywny" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="note" label="Notatka / Opis szablonu">
                        <Input.TextArea rows={3} placeholder="np. Stała opłata za pakiet premium..." />
                    </Form.Item>
                </Form>
            </Modal>

            {/* LIQUIDITY OPERATIONS MODAL (Saldo otwarcia / Korekta salda) */}
            <Modal
                title={
                    <Space>
                        <WalletOutlined style={{ color: token.colorPrimary }} />
                        <span>
                            {liquidityModalType === 'OPENING_BALANCE' ? 'Saldo otwarcia' : 'Korekta salda'}
                        </span>
                    </Space>
                }
                width={480}
                open={isLiquidityModalOpen}
                onCancel={() => { setIsLiquidityModalOpen(false); liquidityForm.resetFields(); }}
                onOk={handleLiquidityFormSubmit}
                okText="Zapisz"
                cancelText="Anuluj"
                confirmLoading={createLiquidityEntryMutation.isPending}
            >
                <Alert
                    message={
                        liquidityModalType === 'OPENING_BALANCE'
                            ? 'Saldo otwarcia określa punkt startowy stanu gotówki firmy od wskazanej daty. Wcześniejsze ruchy nie będą uwzględniane w tym punkcie startowym. Wpisz wartość odpowiadającą rzeczywistym środkom na rachunkach na wybraną datę.'
                            : 'Korekta salda służy do uzgodnienia systemowego salda z rzeczywistym stanem środków (np. po inwentaryzacji kasy, przeliczeniu sald). Wartość dodatnia zwiększa saldo, ujemna — zmniejsza.'
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16, fontSize: '12px' }}
                />
                <Form form={liquidityForm} layout="vertical">
                    <Form.Item
                        name="amount"
                        label={liquidityModalType === 'OPENING_BALANCE' ? 'Kwota salda otwarcia (PLN)' : 'Kwota korekty (PLN, może być ujemna)'}
                        rules={[{ required: true, message: 'Podaj kwotę' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            precision={2}
                            formatter={value => `${value} PLN`}
                            parser={value => value!.replace(' PLN', '')}
                            placeholder={liquidityModalType === 'OPENING_BALANCE' ? 'np. 50000' : 'np. -1500 lub 3000'}
                        />
                    </Form.Item>
                    <Form.Item
                        name="date"
                        label="Data operacji"
                        rules={[{ required: true, message: 'Wybierz datę' }]}
                    >
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="description" label="Opis (opcjonalnie)">
                        <Input.TextArea
                            rows={2}
                            placeholder={
                                liquidityModalType === 'OPENING_BALANCE'
                                    ? 'np. Stan kont bankowych na dzień otwarcia systemu'
                                    : 'np. Korekta po inwentaryzacji kasy, różnica kursowa'
                            }
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
